import { createContext, useContext, Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  BarChart3,
  Bot,
  Calendar,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDollarSign,
  CirclePlay,
  Clock,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Filter,
  FlaskConical,
  Gauge,
  Globe,
  HelpCircle,
  History,
  Info,
  Layers,
  LayoutGrid,
  List,
  ListChecks,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Package,
  Pause,
  Pencil,
  Play,
  PlugZap,
  Plus,
  PlusCircle,
  RefreshCw,
  Rocket,
  RotateCcw,
  Save,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sliders,
  Sparkles,
  Table,
  Target,
  Terminal,
  TrendingDown,
  TrendingUp,
  Trash2,
  Unlock,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { automaticTargetLabel, entityDisplayLabel, resolveActionEntityLabel, isBudgetAction, isPlacementAction, isStatePauseAction, parseNegativeTypeDisplay, parseTargetingTypeDisplay, extractPlacementPercentages, extractStateValues } from "./targeting.ts";
import { api, ApiError } from "./api.ts";
import { ArtifactPreviewDialog } from "./ArtifactPreviewDialog.tsx";
import { copyText } from "./clipboard.ts";
import { ErrorBoundary } from "./ErrorBoundary.tsx";
import { AiAnalysisView, ExecutionResultsView, OptimizationAdviceView, formatAcosText, withoutEntityIds } from "./RunAuditViews.tsx";
import { ExperimentCard } from "./ExperimentCard.tsx";
import { EventStreamConsole } from "./EventStreamConsole.tsx";
import { PerformanceTrendChart } from "./PerformanceTrendChart.tsx";
import { DailyAdPerformanceCard } from "./DailyAdPerformanceCard.tsx";
import { StrategySettingsPage } from "./StrategySettingsPage.tsx";
import { ScaleSpacePage } from "./ScaleSpacePage.tsx";
import { CustomSelect, getMarketplaceFlag, MarketplaceFlag, type CustomSelectOption } from "./CustomSelect.tsx";
import { CustomDatePicker } from "./CustomDatePicker.tsx";
import { CustomTimePicker } from "./CustomTimePicker.tsx";
import { DateRangeModal } from "./DateRangeModal.tsx";
import { useStaggerEntrance, useCountUp, pulseElement, gsap } from "./motion.ts";
import { ThemeProvider, useTheme, ThemeToggleBtn } from "./theme.tsx";
import type {
  AiAnalysisDetails,
  ActionEntity,
  Artifact,
  ArtifactDetail,
  DashboardPerformance,
  Experiment,
  ExperimentMetricPlan,
  MarketplaceSetting,
  ModelProvider,
  OptimizationRecommendations,
  Plan,
  ReasoningEffort,
  Run,
  RunExecutionDetails,
  Schedule,
  ScheduleTemplate,
  Scope,
  StrategyVersion,
  SystemInfo,
  TaskMode,
} from "./types.ts";

type View =
  | "overview"
  | "scopes"
  | "schedules"
  | "runs"
  | "approvals"
  | "experiments"
  | "scale"
  | "scale_dashboard"
  | "scale_optimizations"
  | "scale_decisions"
  | "scale_launch"
  | "system";

const AUTONOMOUS_TASK_TYPES = new Set([
  "AUTONOMOUS_LAUNCH",
  "DAILY_SENTINEL",
  "ADAPTIVE_EVALUATION",
  "MILESTONE_TRANSITION",
  "SCHEDULED_EVALUATION",
]);

export interface AppRoute {
  view: View;
  selectedScope: string | null;
  selectedRun: string | null;
}

const VIEW_PATHS: Record<View, string> = {
  overview: "/",
  scopes: "/scopes",
  schedules: "/scopes/schedules",
  runs: "/runs",
  approvals: "/approvals",
  experiments: "/experiments",
  scale: "/scale",
  scale_dashboard: "/scale/dashboard",
  scale_optimizations: "/scale/optimizations",
  scale_decisions: "/scale/decisions",
  scale_launch: "/scale/launch",
  system: "/system",
};

export interface NavigationContextType {
  navigate: (view: View) => void;
}

export const NavigationContext = createContext<NavigationContextType>({
  navigate: () => {},
});

export function routeFromPathname(pathname: string): AppRoute {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const segments = normalized.split("/").filter(Boolean);
  let id: string | null = null;
  if (segments[1]) {
    try {
      id = decodeURIComponent(segments[1]);
    } catch {
      return { view: "overview", selectedScope: null, selectedRun: null };
    }
  }

  if (normalized === "/") return { view: "overview", selectedScope: null, selectedRun: null };
  if (normalized === VIEW_PATHS.schedules) {
    return { view: "schedules", selectedScope: null, selectedRun: null };
  }
  if (segments[0] === "scopes" && segments.length <= 2) {
    return { view: "scopes", selectedScope: id, selectedRun: null };
  }
  if (segments[0] === "scale") {
    if (segments.length === 1) {
      return { view: "scale", selectedScope: null, selectedRun: null };
    }
    if (segments.length === 2) {
      if (segments[1] === "sop") {
        return { view: "scale_dashboard", selectedScope: null, selectedRun: null };
      }
      if (["dashboard", "optimizations", "decisions", "launch"].includes(segments[1])) {
        return { view: `scale_${segments[1]}` as View, selectedScope: null, selectedRun: null };
      }
      return { view: "scale", selectedScope: id, selectedRun: null };
    }
    if (segments.length === 3) {
      if (segments[1] === "sop") {
        try {
          const scopeId = decodeURIComponent(segments[2]);
          return { view: "scale_dashboard", selectedScope: scopeId, selectedRun: null };
        } catch {
          return { view: "scale_dashboard", selectedScope: null, selectedRun: null };
        }
      }
      if (["dashboard", "optimizations", "decisions", "launch"].includes(segments[1])) {
        try {
          const scopeId = decodeURIComponent(segments[2]);
          return { view: `scale_${segments[1]}` as View, selectedScope: scopeId, selectedRun: null };
        } catch {
          return { view: `scale_${segments[1]}` as View, selectedScope: null, selectedRun: null };
        }
      }
    }
    return { view: "scale", selectedScope: null, selectedRun: null };
  }
  if (segments[0] === "runs" && segments.length <= 2) {
    return { view: "runs", selectedScope: null, selectedRun: id };
  }
  if (segments.length === 1 && ["approvals", "experiments", "system"].includes(segments[0])) {
    return { view: segments[0] as View, selectedScope: null, selectedRun: null };
  }
  return { view: "overview", selectedScope: null, selectedRun: null };
}

export function pathnameForRoute(route: AppRoute): string {
  if (route.view === "scopes" && route.selectedScope) return `/scopes/${encodeURIComponent(route.selectedScope)}`;
  if (route.view === "scale" && route.selectedScope) return `/scale/${encodeURIComponent(route.selectedScope)}`;
  if (route.view.startsWith("scale_")) {
    const sub = route.view.replace("scale_", "");
    return route.selectedScope ? `/scale/${sub}/${encodeURIComponent(route.selectedScope)}` : `/scale/${sub}`;
  }
  if (route.view === "runs" && route.selectedRun) return `/runs/${encodeURIComponent(route.selectedRun)}`;
  return VIEW_PATHS[route.view];
}

function initialRoute(): AppRoute {
  return typeof window === "undefined"
    ? { view: "overview", selectedScope: null, selectedRun: null }
    : routeFromPathname(window.location.pathname);
}

export function App({ currentUser, onLogout }: { currentUser: { username: string; role: "ADMIN" }; onLogout: () => Promise<void> }) {
  const [initial] = useState(initialRoute);
  const [view, setView] = useState<View>(initial.view);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [marketplaceSettings, setMarketplaceSettings] = useState<MarketplaceSetting[]>([]);
  const [system, setSystem] = useState<SystemInfo | null>(null);
  const [selectedScope, setSelectedScope] = useState<string | null>(initial.selectedScope);
  const [selectedRun, setSelectedRun] = useState<string | null>(initial.selectedRun);
  const [notice, setNotice] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("ads_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [scaleFleetResetKey, setScaleFleetResetKey] = useState(0);
  const [homepageRefreshVersion, setHomepageRefreshVersion] = useState(0);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("ads_sidebar_collapsed", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const isRefreshingRef = useRef(false);
  const viewRef = useRef(view);
  const selectedScopeRef = useRef(selectedScope);
  const runsRef = useRef(runs);
  const schedulesRef = useRef(schedules);
  const previousViewRef = useRef(view);
  const previousSelectedScopeRef = useRef(selectedScope);
  viewRef.current = view;
  selectedScopeRef.current = selectedScope;
  runsRef.current = runs;
  schedulesRef.current = schedules;

  const refresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      setIsRefreshing(true);
      const currentView = viewRef.current;
      const isScaleView = currentView === "scale" || currentView.startsWith("scale_");
      const needsRuns = !isScaleView || currentView === "scale_optimizations" || currentView === "scale_decisions" || (isScaleView && Boolean(selectedScopeRef.current));
      const needsSchedules = currentView === "schedules" || currentView === "scopes" || (isScaleView && Boolean(selectedScopeRef.current));
      const [scopeRes, runRes, scheduleRes, systemRes, marketplaceRes] = await Promise.allSettled([
        api.get<Scope[]>("/api/v1/scopes"),
        needsRuns ? api.get<Run[]>("/api/v1/runs") : Promise.resolve<Run[]>(runsRef.current),
        needsSchedules ? api.get<Schedule[]>("/api/v1/schedules") : Promise.resolve<Schedule[]>(schedulesRef.current),
        api.get<SystemInfo>("/api/v1/system"),
        api.get<MarketplaceSetting[]>("/api/v1/marketplace-settings"),
      ]);
      if (scopeRes.status === "fulfilled") setScopes(scopeRes.value);
      if (runRes.status === "fulfilled") setRuns(runRes.value);
      if (scheduleRes.status === "fulfilled") setSchedules(scheduleRes.value);
      if (systemRes.status === "fulfilled") setSystem(systemRes.value);
      if (marketplaceRes.status === "fulfilled") setMarketplaceSettings(marketplaceRes.value);

      const rejected = [scopeRes, runRes, scheduleRes, systemRes, marketplaceRes].filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      if (rejected.length > 0) {
        showError(rejected[0].reason, setNotice);
      }
    } catch (error) {
      showError(error, setNotice);
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Background tabs do not need a 30-second full-workspace refresh. Poll at
    // a lower cadence and refresh immediately when the tab becomes visible.
    const timer = setInterval(() => {
      if (typeof document === "undefined" || !document.hidden) void refresh();
    }, 60_000);
    const onVisibilityChange = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh]);

  useEffect(() => {
    if (previousViewRef.current === view) return;
    previousViewRef.current = view;
    void refresh();
  }, [view, refresh]);

  useEffect(() => {
    if (previousSelectedScopeRef.current === selectedScope) return;
    previousSelectedScopeRef.current = selectedScope;
    if (view === "scale" || view.startsWith("scale_")) void refresh();
  }, [selectedScope, view, refresh]);

  const mainRef = useRef<HTMLElement>(null);

  const resetScroll = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, []);

  const applyRoute = useCallback((route: AppRoute, updateHistory = true) => {
    setView(route.view);
    setSelectedScope(route.selectedScope);
    setSelectedRun(route.selectedRun);
    setMenuOpen(false);
    resetScroll();
    if (updateHistory) {
      const pathname = pathnameForRoute(route);
      if (window.location.pathname !== pathname) window.history.pushState(null, "", pathname);
    }
  }, [resetScroll]);

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    if (!notice) return;
    const duration = notice.type === "success" ? 2000 : 4000;
    const timer = setTimeout(() => {
      setNotice(null);
    }, duration);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    resetScroll();
  }, [view, selectedScope, selectedRun, resetScroll]);

  useEffect(() => {
    const handlePopState = () => applyRoute(routeFromPathname(window.location.pathname), false);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyRoute]);

  useEffect(() => {
    const canonicalPathname = pathnameForRoute(initial);
    if (window.location.pathname !== canonicalPathname) {
      window.history.replaceState(null, "", canonicalPathname);
    }
  }, [initial]);

  const navigate = (next: View) => applyRoute({ view: next, selectedScope: null, selectedRun: null });
  const navigateToScope = (id: string) => applyRoute({ view: "scopes", selectedScope: id, selectedRun: null });
  const navigateToScale = (id?: string | null) => applyRoute({ view: "scale", selectedScope: id ?? null, selectedRun: null });
  const navigateToRun = (id: string) => applyRoute({ view: "runs", selectedScope: null, selectedRun: id });

  const workspaceRef = useRef<HTMLDivElement>(null);
  const isInitialPageRender = useRef(true);

  useEffect(() => {
    if (isInitialPageRender.current) {
      isInitialPageRender.current = false;
      return;
    }
    if (workspaceRef.current && typeof window !== "undefined") {
      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!prefersReduced) {
        gsap.killTweensOf(workspaceRef.current);
        gsap.fromTo(
          workspaceRef.current,
          { opacity: 0.25, y: 10 },
          {
            opacity: 1,
            y: 0,
            duration: 0.25,
            ease: "power2.out",
            clearProps: "transform,opacity",
          }
        );
      }
    }
  }, [view, selectedScope, selectedRun]);

  const pending = runs.filter(
    (run) => run.state === "APPROVAL_REQUIRED" && !AUTONOMOUS_TASK_TYPES.has(run.task_type || "")
  ).length;

  const isScaleSpace = view === "scale" || view.startsWith("scale_");

  return (
    <NavigationContext.Provider value={{ navigate }}>
      <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <aside className={`sidebar ${isScaleSpace ? "cockpit-mode" : ""} ${sidebarCollapsed ? "collapsed" : ""} ${menuOpen ? "open" : ""}`}>
        {isScaleSpace ? (
          <>
            <div className="brand scale-cockpit-brand">
              <span className="brand-mark scale-cockpit-mark" title="自动化放量驾驶舱">🚀</span>
              {!sidebarCollapsed && (
                <div className="brand-text">
                  <strong style={{ color: "var(--teal)" }}>放量驾驶舱</strong>
                </div>
              )}
            </div>

            <nav className="scale-sidebar-nav">
              <NavButton
                icon={<LayoutGrid />}
                label="战役大盘"
                href={VIEW_PATHS.scale_dashboard}
                active={view === "scale" || view === "scale_dashboard"}
                collapsed={sidebarCollapsed}
                onClick={() => {
                  setScaleFleetResetKey((k) => k + 1);
                  applyRoute({ view: "scale_dashboard", selectedScope: null, selectedRun: null });
                }}
              />
              <NavButton
                icon={<Zap />}
                label="优化记录"
                href={VIEW_PATHS.scale_optimizations}
                active={view === "scale_optimizations"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("scale_optimizations")}
              />
              <NavButton
                icon={<ShieldCheck />}
                label="审批中心"
                href={VIEW_PATHS.scale_decisions}
                active={view === "scale_decisions"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("scale_decisions")}
              />
              <NavButton
                icon={<PlusCircle />}
                label="发起新放量"
                href={VIEW_PATHS.scale_launch}
                active={view === "scale_launch"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("scale_launch")}
              />
            </nav>

            <div className="sidebar-bottom scale-sidebar-bottom-clean">
              <div className="scale-back-wrap">
                <button
                  type="button"
                  className={`scale-back-to-ops-ghost-btn ${sidebarCollapsed ? "collapsed" : ""}`}
                  onClick={() => navigate("overview")}
                  title="返回日常运营系统（资产/定时/流水线/审批/实验）"
                  aria-label="返回日常系统"
                >
                  <span className="sidebar-icon-slot">
                    <ArrowLeft size={16} />
                  </span>
                  {!sidebarCollapsed && <span className="nav-label">返回日常系统</span>}
                </button>
              </div>
              <div className={`sidebar-account scale-sidebar-account-clean ${sidebarCollapsed ? "collapsed" : ""}`}>
                <span className="sidebar-icon-slot">
                  <span className="sidebar-account-avatar" title={currentUser.username}>
                    {currentUser.username.slice(0, 1).toUpperCase()}
                  </span>
                </span>
                {!sidebarCollapsed && (
                  <span className="sidebar-account-copy">
                    <strong>{currentUser.username}</strong>
                    <small>管理员</small>
                  </span>
                )}
                {!sidebarCollapsed && (
                  <button type="button" onClick={() => void onLogout()} title="退出登录" aria-label="退出登录">
                    <LogOut size={15} />
                  </button>
                )}
              </div>
              <button
                type="button"
                className={`sidebar-collapse-btn scale-sidebar-collapse-clean ${sidebarCollapsed ? "collapsed" : ""}`}
                onClick={toggleSidebar}
                title={sidebarCollapsed ? "展开侧边栏" : "折叠侧边栏"}
                aria-label={sidebarCollapsed ? "展开侧边栏" : "折叠侧边栏"}
                aria-expanded={!sidebarCollapsed}
              >
                <span className="sidebar-icon-slot">
                  {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                </span>
                {!sidebarCollapsed && <span className="nav-label">折叠侧边栏</span>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="brand">
              <span className="brand-mark" title="Ads Operations">A</span>
              {!sidebarCollapsed && (
                <div className="brand-text">
                  <strong>Ads Operations</strong>
                </div>
              )}
            </div>
            <nav>
              <NavButton
                icon={<Gauge />}
                label="总览"
                href={VIEW_PATHS.overview}
                active={view === "overview"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("overview")}
              />
              <NavButton
                icon={<Target />}
                label="ASIN 运营"
                href={VIEW_PATHS.scopes}
                active={view === "scopes"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("scopes")}
              />

              <NavButton
                icon={<CalendarClock />}
                label="定时任务"
                href={VIEW_PATHS.schedules}
                active={view === "schedules"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("schedules")}
              />
              <NavButton
                icon={<Activity />}
                label="Runs 流水线"
                href={VIEW_PATHS.runs}
                active={view === "runs"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("runs")}
              />
              <NavButton
                icon={<ListChecks />}
                label="审批中心"
                href={VIEW_PATHS.approvals}
                count={pending}
                active={view === "approvals"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("approvals")}
              />
              <NavButton
                icon={<BarChart3 />}
                label="实验复盘"
                href={VIEW_PATHS.experiments}
                active={view === "experiments"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("experiments")}
              />
              <NavButton
                icon={<Settings />}
                label="系统与连接"
                href={VIEW_PATHS.system}
                active={view === "system"}
                collapsed={sidebarCollapsed}
                onClick={() => navigate("system")}
              />
            </nav>
            <div className="sidebar-bottom">
              <div className={`sidebar-account ${sidebarCollapsed ? "collapsed" : ""}`}>
                <span className="sidebar-account-avatar" title={currentUser.username}>{currentUser.username.slice(0, 1).toUpperCase()}</span>
                {!sidebarCollapsed && <span className="sidebar-account-copy"><strong>{currentUser.username}</strong><small>管理员</small></span>}
                <button type="button" onClick={() => void onLogout()} title="退出登录" aria-label="退出登录"><LogOut /></button>
              </div>
              <button
                type="button"
                className={`sidebar-collapse-btn ${sidebarCollapsed ? "collapsed" : ""}`}
                onClick={toggleSidebar}
                title={sidebarCollapsed ? "展开侧边栏" : "折叠侧边栏"}
                aria-label={sidebarCollapsed ? "展开侧边栏" : "折叠侧边栏"}
              >
                <span className="nav-icon-wrap">
                  {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </span>
                {!sidebarCollapsed && <span className="nav-label">折叠侧边栏</span>}
              </button>
            </div>
          </>
        )}
      </aside>

      <main ref={mainRef}>
        {notice && (
          <div className={`notice ${notice.type}`} role="status">
            {notice.type === "error" ? <AlertTriangle /> : <Check />}
            <span>{notice.text}</span>
            <button
              className="icon-button"
              onClick={() => setNotice(null)}
              title="关闭通知"
              aria-label="关闭通知"
            >
              <X />
            </button>
          </div>
        )}

        <div className="workspace" ref={workspaceRef}>
          {view === "overview" && (
            <Overview
              scopes={scopes}
              runs={runs}
              schedules={schedules}
              marketplaceSettings={marketplaceSettings}
              system={system}
              isRefreshing={isRefreshing}
              onRefresh={refresh}
              onHomepageRefresh={(marketplace) => {
                setHomepageRefreshVersion(Date.now());
                window.dispatchEvent(new CustomEvent("homepage-performance-refreshed", { detail: { marketplace } }));
              }}
              setNotice={setNotice}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
              onScope={(id) => {
                navigateToScope(id);
              }}
              onRun={(id) => {
                navigateToRun(id);
              }}
              onNavigate={(targetView) => navigate(targetView)}
            />
          )}
          {view === "scopes" && (
            <ScopesPage
              scopes={scopes}
              runs={runs}
              schedules={schedules}
              marketplaceSettings={marketplaceSettings}
              selected={selectedScope}
              onSelect={(id) => id ? navigateToScope(id) : navigate("scopes")}
              onChanged={refresh}
              setNotice={setNotice}
              system={system}
              isRefreshing={isRefreshing}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
              onRun={(id) => {
                navigateToRun(id);
              }}
              onNavigate={(targetView) => navigate(targetView)}
            />
          )}
          {view === "schedules" && (
            <GlobalSchedulesPage
              scopes={scopes}
              schedules={schedules}
              system={system}
              onChanged={refresh}
              setNotice={setNotice}
              isRefreshing={isRefreshing}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
              onScope={navigateToScope}
              onBack={() => navigate("scopes")}
            />
          )}
          {view === "runs" && (
            <RunsPage
              runs={runs}
              scopes={scopes}
              selected={selectedRun}
              onSelect={(id) => (id ? navigateToRun(id) : selectedScope ? applyRoute({ view: "scale", selectedScope, selectedRun: null }) : navigate("runs"))}
              system={system}
              isRefreshing={isRefreshing}
              onRefresh={refresh}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
              setNotice={setNotice}
            />
          )}
          {view === "approvals" && (
            <ApprovalsPage
              runs={runs}
              scopes={scopes}
              onNavigateToRun={navigateToRun}
              onChanged={refresh}
              setNotice={setNotice}
              system={system}
              isRefreshing={isRefreshing}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
            />
          )}
          {view === "experiments" && (
            <ExperimentsPage
              system={system}
              isRefreshing={isRefreshing}
              onRefresh={refresh}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
            />
          )}
          {isScaleSpace && (
            <ScaleSpacePage
              key={`scale-space-${scaleFleetResetKey}-${view}-${selectedScope || "fleet"}`}
              view={view}
              scopes={scopes}
              marketplaceSettings={marketplaceSettings}
              selectedScopeId={selectedScope}
              onSelectScope={(id) => (id ? applyRoute({ view, selectedScope: id, selectedRun: null }) : applyRoute({ view, selectedScope: null, selectedRun: null }))}
              onNavigateView={(targetView) => applyRoute({ view: targetView as View, selectedScope, selectedRun: null })}
              system={system}
              isRefreshing={isRefreshing}
              homepageRefreshVersion={homepageRefreshVersion}
              onRefresh={refresh}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
              setNotice={setNotice}
              onNavigate={(targetView) => navigate(targetView)}
              runs={runs}
              schedules={schedules}
              onRun={(runId) => applyRoute({ view: "runs", selectedScope, selectedRun: runId })}
              renderRunDetail={(runId, onBack) => (
                <RunDetail
                  runId={runId}
                  back={onBack}
                  system={system}
                  onToggleMenu={() => setMenuOpen(!menuOpen)}
                />
              )}
              renderApprovals={(targetRuns, onNavToRun) => (
                <ApprovalsPage
                  runs={targetRuns}
                  scopes={scopes}
                  onNavigateToRun={onNavToRun}
                  onChanged={refresh}
                  setNotice={setNotice}
                  system={system}
                  isRefreshing={isRefreshing}
                  onToggleMenu={() => setMenuOpen(!menuOpen)}
                />
              )}
              renderSchedules={(targetScope, targetSchedules) => (
                <SchedulesEditor
                  scope={targetScope}
                  schedules={targetSchedules}
                  autonomousOnly={true}
                  onChanged={refresh}
                  setNotice={setNotice}
                  isReadOnly={system?.write_kill_switch ?? false}
                />
              )}
            />
          )}
          {view === "system" && (
            <SystemPage
              system={system}
              marketplaceSettings={marketplaceSettings}
              onChanged={refresh}
              setNotice={setNotice}
              isRefreshing={isRefreshing}
              onToggleMenu={() => setMenuOpen(!menuOpen)}
            />
          )}
        </div>
      </main>
      </div>
    </NavigationContext.Provider>
  );
}

function Overview({
  scopes,
  runs,
  schedules,
  marketplaceSettings,
  system,
  isRefreshing = false,
  onRefresh,
  onHomepageRefresh,
  setNotice,
  onToggleMenu,
  onScope,
  onRun,
  onNavigate,
}: {
  scopes: Scope[];
  runs: Run[];
  schedules: Schedule[];
  marketplaceSettings: MarketplaceSetting[];
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onRefresh?: () => void | Promise<void>;
  onHomepageRefresh?: (marketplace: string) => void;
  setNotice: NoticeSetter;
  onToggleMenu?: () => void;
  onScope: (id: string) => void;
  onRun: (id: string) => void;
  onNavigate: (view: View) => void;
}) {
  const { navigate } = useContext(NavigationContext);
  const configured = marketplaceSettings.filter((setting) => setting.configured);
  const [marketplace, setMarketplace] = useState("UK");
  const [windowDays, setWindowDays] = useState<7 | 14 | 30>(7);
  const [customRange, setCustomRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [performance, setPerformance] = useState<DashboardPerformance | null>(null);
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [refreshingPerformance, setRefreshingPerformance] = useState(false);
  const [quickFilter, setQuickFilter] = useState<null | "APPROVAL_REQUIRED" | "FAILED">(null);

  const isReadOnly = system?.write_kill_switch ?? false;

  const overviewRef = useRef<HTMLDivElement>(null);
  const runsSectionRef = useRef<HTMLDivElement>(null);

  useStaggerEntrance(overviewRef, ".metric-card, .ops-card, .dashboard-grid > .panel", [marketplace, windowDays, customRange]);

  const failedRuns = useMemo(
    () => runs.filter((run) => ["FAILED", "BLOCKED", "EXECUTION_PARTIAL"].includes(run.state) && !AUTONOMOUS_TASK_TYPES.has(run.task_type || "")),
    [runs]
  );
  const pendingApprovals = useMemo(
    () => runs.filter((run) => run.state === "APPROVAL_REQUIRED" && !AUTONOMOUS_TASK_TYPES.has(run.task_type || "")),
    [runs]
  );
  const activeScopes = useMemo(
    () => scopes.filter((scope) => scope.status === "ACTIVE"),
    [scopes]
  );
  const activeSchedules = useMemo(
    () => schedules.filter((schedule) => schedule.enabled && !AUTONOMOUS_TASK_TYPES.has(schedule.task_type || "")),
    [schedules]
  );

  useEffect(() => {
    if (!configured.length || configured.some((setting) => setting.marketplace === marketplace)) return;
    setMarketplace(configured[0].marketplace);
  }, [configured.map((setting) => setting.marketplace).join("|"), marketplace]);

  useEffect(() => {
    let active = true;
    setLoadingPerformance(true);
    setPerformanceError(null);

    const queryUrl = customRange
      ? `/api/v1/dashboard/performance?marketplace=${marketplace}&start_date=${customRange.startDate}&end_date=${customRange.endDate}`
      : `/api/v1/dashboard/performance?marketplace=${marketplace}&window=${windowDays}`;

    void api
      .get<DashboardPerformance>(queryUrl)
      .then((result) => {
        if (active) setPerformance(result);
      })
      .catch((error) => {
        if (active) setPerformanceError(error instanceof Error ? error.message : "数据加载失败");
      })
      .finally(() => {
        if (active) setLoadingPerformance(false);
      });
    return () => {
      active = false;
    };
  }, [marketplace, windowDays, customRange]);

  const scrollToRuns = () => {
    setTimeout(() => {
      runsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleSelectPreset = (days: 7 | 14 | 30) => {
    setCustomRange(null);
    setWindowDays(days);
  };

  const handleApplyCustomRange = (range: { startDate: string; endDate: string }) => {
    setCustomRange(range);
  };

  const handleRefreshPerformance = async () => {
    setRefreshingPerformance(true);
    setPerformanceError(null);
    try {
      await api.post("/api/v1/dashboard/performance/refresh", { marketplace });
      const queryUrl = customRange
        ? `/api/v1/dashboard/performance?marketplace=${marketplace}&start_date=${customRange.startDate}&end_date=${customRange.endDate}`
        : `/api/v1/dashboard/performance?marketplace=${marketplace}&window=${windowDays}`;
      const latest = await api.get<DashboardPerformance>(queryUrl);
      setPerformance(latest);
      await onRefresh?.();
      onHomepageRefresh?.(marketplace);
      setNotice({ type: "success", text: `${marketplace} 站点最新数据已拉取` });
    } catch (error) {
      const message = error instanceof Error ? error.message : "最新数据拉取失败";
      setPerformanceError(message);
      showError(error, setNotice);
    } finally {
      setRefreshingPerformance(false);
    }
  };

  const totals = performance?.totals;
  const deltas = performance?.deltas;
  const totalSalesStatus = performance?.total_sales_status;
  const tacosNote = totalSalesNote(totals?.tacos, totalSalesStatus);
  const siteOptions = configured.length ? configured : marketplaceSettings;
  const adSalesLabel = performance?.ad_sales_vat_basis === "VAT_INCLUSIVE" ? "广告销售额（含税）" : "广告销售额（未税）";

  const displayedRuns = useMemo(() => {
    const nonAuto = runs.filter((r) => !AUTONOMOUS_TASK_TYPES.has(r.task_type || ""));
    if (quickFilter === "APPROVAL_REQUIRED") return pendingApprovals;
    if (quickFilter === "FAILED") return failedRuns;
    return nonAuto;
  }, [runs, quickFilter, pendingApprovals, failedRuns]);

  return (
    <div ref={overviewRef} className="overview-container">
      <div className="unified-page-header">
        <div className="header-title-group">
          {onToggleMenu && (
            <button
              type="button"
              className="icon-button mobile-menu overview-mobile-menu"
              onClick={onToggleMenu}
              title="切换导航菜单"
              aria-label="切换导航菜单"
            >
              <Menu />
            </button>
          )}
          <div className="title-with-badge">
            <h1>广告经营总览</h1>
            <span
              className={`title-safety-badge ${isReadOnly ? "safety-on" : "safety-off"}`}
              title={
                isReadOnly
                  ? "全局写入锁已开启：所有真实修改将被安全拦截 (只读保护)"
                  : "全局写入锁已关闭：执行器可写入 MAP"
              }
            >
              {isReadOnly ? <Lock size={12} /> : <Unlock size={12} />}
              <span>{isReadOnly ? "只读保护 (写入锁开启)" : "写入锁已解除"}</span>
            </span>
          </div>
        </div>

        <div className="dashboard-controls">
          <div className="marketplace-select" title="选择亚马逊目标站点">
            <span>站点</span>
            <CustomSelect
              value={marketplace}
              onChange={setMarketplace}
              options={siteOptions.map((setting) => ({
                value: setting.marketplace,
                label: setting.marketplace,
                sublabel: setting.currency,
                flag: getMarketplaceFlag(setting.marketplace),
              }))}
              title="选择亚马逊目标站点"
              aria-label="选择亚马逊目标站点"
            />
          </div>

          <div className="range-controls-group">
            <div className="range-segments" aria-label="快捷数据周期">
              {([7, 14, 30] as const).map((days) => (
                <button
                  key={days}
                  type="button"
                  className={!customRange && windowDays === days ? "active" : ""}
                  onClick={() => handleSelectPreset(days)}
                  title={performance ? `当前归因周期: ${performance.start_date} 至 ${performance.end_date}` : `最近 ${days} 天`}
                >
                  {days}天
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`custom-date-btn ${customRange ? "active" : ""}`}
              onClick={() => setIsDateModalOpen(true)}
              title={customRange ? `自定义归因周期: ${customRange.startDate} 至 ${customRange.endDate}` : "选择自定义日期范围"}
              aria-label="选择自定义日期范围"
            >
              <Calendar size={14} />
              <span>{customRange ? `${customRange.startDate.slice(5)} ~ ${customRange.endDate.slice(5)}` : "自定义"}</span>
            </button>

            {customRange && (
              <button
                type="button"
                className="clear-range-btn"
                onClick={() => setCustomRange(null)}
                title="恢复默认 7 天周期"
                aria-label="清除自定义日期"
              >
                <X size={13} />
              </button>
            )}

            <button
              type="button"
              className="header-refresh-btn"
              onClick={() => void handleRefreshPerformance()}
              title="拉取并刷新当前站点与周期数据"
              aria-label="拉取并刷新当前站点与周期数据"
              disabled={isRefreshing || refreshingPerformance}
            >
              <RefreshCw size={14} className={isRefreshing || refreshingPerformance ? "spin" : ""} />
            </button>

            <button
              type="button"
              className="header-cockpit-btn"
              onClick={() => (onNavigate ? onNavigate("scale_dashboard") : navigate("scale_dashboard"))}
              title="进入自动驾驶空间 (放量驾驶舱)"
              aria-label="进入自动驾驶空间 (放量驾驶舱)"
            >
              <Rocket size={15} />
            </button>

            <ThemeToggleBtn />
          </div>
        </div>
      </div>

      {/* KPI Metric Strip */}
      <section className="metric-strip performance-metrics" aria-label="核心业务指标概览">
        <Metric
          label="广告花费"
          value={performance ? money(totals?.ad_spend ?? 0, performance.currency) : "-"}
          note={`${performance?.data_days ?? 0}/${performance?.window_days ?? windowDays} 个完整自然日`}
          delta={deltas?.spend_delta_pct}
          deltaType="neutral"
          tooltip="所选周期内 Sponsored Products 广告实际扣费总额"
        />
        <Metric
          label={adSalesLabel}
          value={performance ? money(totals?.ad_sales ?? 0, performance.currency) : "-"}
          note="MAP 归因销售额 · VAT excl."
          delta={deltas?.sales_delta_pct}
          deltaType="good-up"
          tooltip="欧洲广告报表中的归因销售额，按未含 VAT 口径记录"
        />
        <Metric
          label="广告订单量"
          value={totals ? new Intl.NumberFormat("zh-CN").format(totals.ad_orders) : "-"}
          note="Sponsored Products 订单"
          delta={deltas?.orders_delta_pct}
          deltaType="good-up"
          tooltip="广告直接转化的订单总件数"
        />
        <Metric
          label="ACOS"
          value={ratioText(totals?.acos)}
          note={`广告花费 / ${adSalesLabel}`}
          delta={deltas?.acos_delta_pct}
          deltaType="good-down"
          tooltip={`广告投入产出比（广告花费 ÷ ${adSalesLabel}）。数值越低代表广告投放效率越高`}
        />
        <Metric
          label="TACOS"
          value={ratioText(totals?.tacos)}
          note={tacosNote}
          delta={deltas?.tacos_delta_pct}
          deltaType="good-down"
          tooltip={totalSalesStatus?.error ?? "综合广告占销比（广告花费 ÷ 店铺总销售额）。衡量广告对全局业务的撬动杠杆"}
        />
      </section>

      {/* Performance Trends Panel */}
      <section className="panel performance-panel">
        <div className="performance-header">
          <div>
            <h2>每日广告表现走势</h2>
            <span>
              {loadingPerformance
                ? "正在拉取最新报表..."
                : performanceError ?? `报表更新于 ${dateTime(performance?.updated_at)} · 归因区间 ${performance?.start_date ?? "-"} 至 ${performance?.end_date ?? "-"}`}
            </span>
          </div>
          <div className="refresh-cadence">
            <CalendarClock />
            <span>
              <strong>每日自动刷新 {performance?.schedule?.local_time ?? "15:00"}</strong>
              <small>上海时间 · 每站自动同步最近 30 天</small>
            </span>
          </div>
        </div>

        <PerformanceTrendChart
          performance={performance}
          currency={performance?.currency ?? "GBP"}
          loading={loadingPerformance}
        />
      </section>

      {/* Interactive Summary Operations Cards */}
      <div className="ops-summary-strip" role="region" aria-label="系统健康度与运维概览">
        <button
          type="button"
          className="ops-card scope-card"
          onClick={() => onNavigate("scopes")}
          title="点击前往 ASIN 运营管理"
        >
          <div className="ops-card-icon"><Target size={18} /></div>
          <div className="ops-card-content">
            <small>活跃 ASIN Scope</small>
            <strong>{activeScopes.length}</strong>
            <span className="ops-card-tag">共 {scopes.length} 个配置</span>
          </div>
          <ChevronRight className="ops-card-arrow" size={16} />
        </button>

        <button
          type="button"
          className={`ops-card approval-card ${pendingApprovals.length > 0 ? "highlight-warning" : ""} ${quickFilter === "APPROVAL_REQUIRED" ? "filter-active" : ""}`}
          onClick={() => {
            setQuickFilter(quickFilter === "APPROVAL_REQUIRED" ? null : "APPROVAL_REQUIRED");
            scrollToRuns();
          }}
          title={pendingApprovals.length > 0 ? "点击快速筛选待处理审批记录" : "暂无待审批任务"}
        >
          <div className="ops-card-icon"><ListChecks size={18} /></div>
          <div className="ops-card-content">
            <small>待处理审批</small>
            <strong>{pendingApprovals.length}</strong>
            <span className={`ops-card-tag ${pendingApprovals.length > 0 ? "tag-warning" : ""}`}>
              {pendingApprovals.length > 0 ? "急需处理 🔔" : "已全部就绪"}
            </span>
          </div>
          <ChevronRight className="ops-card-arrow" size={16} />
        </button>

        <button
          type="button"
          className={`ops-card danger-card ${failedRuns.length > 0 ? "highlight-danger" : ""} ${quickFilter === "FAILED" ? "filter-active" : ""}`}
          onClick={() => {
            setQuickFilter(quickFilter === "FAILED" ? null : "FAILED");
            scrollToRuns();
          }}
          title={failedRuns.length > 0 ? "点击快速筛选异常 Runs 记录" : "近期未发现异常"}
        >
          <div className="ops-card-icon"><AlertTriangle size={18} /></div>
          <div className="ops-card-content">
            <small>异常 Runs</small>
            <strong>{failedRuns.length}</strong>
            <span className={`ops-card-tag ${failedRuns.length > 0 ? "tag-danger" : ""}`}>
              {failedRuns.length > 0 ? "需排查告警" : "流水线正常"}
            </span>
          </div>
          <ChevronRight className="ops-card-arrow" size={16} />
        </button>

        <a
          href={VIEW_PATHS.schedules}
          className="ops-card schedule-card"
          onClick={(event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onNavigate("schedules");
          }}
          title="查看全部 ASIN 的定时任务"
          aria-label="前往 ASIN 运营定时任务管理"
        >
          <div className="ops-card-icon"><CalendarClock size={18} /></div>
          <div className="ops-card-content">
            <small>生效中定时任务</small>
            <strong>{activeSchedules.length}</strong>
            <span className="ops-card-tag">全站自动化巡检</span>
          </div>
          <ChevronRight className="ops-card-arrow" size={16} />
        </a>
      </div>

      {/* Quick Filter Notification Banner */}
      {quickFilter && (
        <div className="filter-notification-bar">
          <div className="filter-info">
            <Filter size={15} />
            <span>
              已按「<strong>{quickFilter === "APPROVAL_REQUIRED" ? "待处理审批" : "异常 Runs"}</strong>」过滤流水线记录（共 {displayedRuns.length} 条）
            </span>
          </div>
          <button type="button" className="clear-filter-btn" onClick={() => setQuickFilter(null)}>
            <X size={13} />
            <span>清除筛选</span>
          </button>
        </div>
      )}

      {/* Bottom Grid: Assets & Runs */}
      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-card-header">
            <div className="panel-title-group">
              <h3>ASIN 资产状态</h3>
              <span className="count-badge">{scopes.length}</span>
            </div>
            <button type="button" className="view-all-link" onClick={() => onNavigate("scopes")}>
              <span>查看全部 ASIN</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <DataTable
            empty="尚未创建 ASIN Scope"
            columns={["ASIN", "站点", "状态", "币种", "最近更新"]}
            rows={scopes.slice(0, 6).map((scope) => [
              <button key={scope.scope_id} className="asin-link-button" onClick={() => onScope(scope.scope_id)}>
                {scope.child_asin}
              </button>,
              scope.marketplace,
              <Status key="status" value={scope.status} />,
              scope.currency,
              dateTime(scope.updated_at),
            ])}
          />
        </section>

        <section className="panel" ref={runsSectionRef}>
          <div className="panel-card-header">
            <div className="panel-title-group">
              <h3>最近 Runs 动态</h3>
              <span className="count-badge">{displayedRuns.length}</span>
            </div>
            <button type="button" className="view-all-link" onClick={() => onNavigate("runs")}>
              <span>查看流水线</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <DataTable
            empty={quickFilter ? "当前筛选条件下暂无 Runs 记录" : "暂无 Run 记录"}
            emptyAction={
              quickFilter ? (
                <button
                  type="button"
                  className="clear-filter-btn"
                  onClick={() => setQuickFilter(null)}
                  style={{ marginTop: 10 }}
                >
                  <X size={13} />
                  <span>清除「{quickFilter === "APPROVAL_REQUIRED" ? "待处理审批" : "异常 Runs"}」筛选</span>
                </button>
              ) : undefined
            }
            columns={["Scope", "任务类型", "状态", "触发时间", "操作"]}
            rows={displayedRuns.slice(0, 6).map((run) => [
              <button key={run.run_id} className="asin-link-button" onClick={() => onRun(run.run_id)}>
                {run.child_asin}
              </button>,
              taskLabel(run.task_type),
              <Status key="status" value={run.state} />,
              dateTime(run.created_at),
              <div key="action" className="table-actions-cell">
                {run.state === "APPROVAL_REQUIRED" ? (
                  isReadOnly ? (
                    <button
                      type="button"
                      className="action-pill primary-pill disabled"
                      disabled
                      title="当前处于只读保护，解除锁定后可操作"
                      aria-label="当前处于只读保护，解除锁定后可操作"
                    >
                      <Lock size={12} />
                      <span>去审批</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="action-pill primary-pill"
                      onClick={() => {
                        onRun(run.run_id);
                        onNavigate("approvals");
                      }}
                      title="立即处理人工审批"
                    >
                      <Check size={12} />
                      <span>去审批</span>
                    </button>
                  )
                ) : ["FAILED", "BLOCKED", "EXECUTION_PARTIAL"].includes(run.state) ? (
                  <button
                    type="button"
                    className="action-pill danger-pill"
                    onClick={() => onRun(run.run_id)}
                    title="查看异常诊断日志"
                  >
                    <AlertTriangle size={12} />
                    <span>查看诊断</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="action-pill secondary-pill"
                    onClick={() => onRun(run.run_id)}
                    title="查看运行详情"
                  >
                    <span>详情</span>
                  </button>
                )}
              </div>,
            ])}
          />
        </section>
      </div>

      {/* Date Range Picker Modal */}
      <DateRangeModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        onApply={handleApplyCustomRange}
        initialStartDate={customRange?.startDate}
        initialEndDate={customRange?.endDate}
      />
    </div>
  );
}

type ScopeViewMode = "grouped" | "matrix" | "flat";

const STANDARD_EU_MARKETPLACES = ["UK", "DE", "FR", "IT", "ES"] as const;

const MARKETPLACE_LABELS: Record<string, string> = {
  UK: "英国",
  GB: "英国",
  DE: "德国",
  FR: "法国",
  IT: "意大利",
  ES: "西班牙",
  US: "美国",
  CA: "加拿大",
  JP: "日本",
};

function getAmazonProductUrl(asin: string, marketplace: string): string {
  const code = marketplace.toUpperCase();
  switch (code) {
    case "UK":
    case "GB":
      return `https://www.amazon.co.uk/dp/${asin}`;
    case "DE":
      return `https://www.amazon.de/dp/${asin}`;
    case "FR":
      return `https://www.amazon.fr/dp/${asin}`;
    case "IT":
      return `https://www.amazon.it/dp/${asin}`;
    case "ES":
      return `https://www.amazon.es/dp/${asin}`;
    case "US":
      return `https://www.amazon.com/dp/${asin}`;
    case "CA":
      return `https://www.amazon.ca/dp/${asin}`;
    case "JP":
      return `https://www.amazon.co.jp/dp/${asin}`;
    default:
      return `https://www.amazon.com/dp/${asin}`;
  }
}

function formatRelativeTime(isoString?: string | null): string {
  if (!isoString) return "-";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (isNaN(diffMs)) return "-";
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "刚刚";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} 分钟前`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} 小时前`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} 天前`;
  return date.toLocaleDateString();
}

interface AsinGroup {
  asin: string;
  sku: string;
  displayName: string;
  brand: string | null;
  scopes: Scope[];
  marketplaceMap: Map<string, Scope>;
  allActive: boolean;
  activeCount: number;
  totalMarketplaces: number;
  latestRun?: Run;
}

export function ScopesPage({
  scopes,
  runs,
  schedules,
  marketplaceSettings,
  selected,
  onSelect,
  onChanged,
  setNotice,
  system,
  isRefreshing,
  onToggleMenu,
  onRun,
  onNavigate,
}: {
  scopes: Scope[];
  runs: Run[];
  schedules: Schedule[];
  marketplaceSettings: MarketplaceSetting[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
  onRun?: (id: string) => void;
  onNavigate?: (view: View) => void;
}) {
  const [viewMode, setViewMode] = useState<ScopeViewMode>("grouped");
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>("ALL");
  const [selectedBrand, setSelectedBrand] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [selectedCoverage, setSelectedCoverage] = useState<string>("ALL");
  const [query, setQuery] = useState("");
  const [expandedAsins, setExpandedAsins] = useState<Set<string>>(new Set());
  const [copiedAsin, setCopiedAsin] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialValues, setCreateInitialValues] = useState<{
    marketplace?: string;
    child_asin?: string;
    sku?: string;
  } | null>(null);

  const [deleting, setDeleting] = useState<Scope | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [editingSkuTarget, setEditingSkuTarget] = useState<{ scope: Scope; siblingScopes: Scope[] } | null>(null);

  const isReadOnly = system?.write_kill_switch ?? true;

  const removeScope = async (scope: Scope) => {
    try {
      setDeleteBusy(true);
      const result = await api.delete<{ removed: { runs: number; strategies: number; schedules: number } }>(
        `/api/v1/scopes/${scope.scope_id}`
      );
      setDeleting(null);
      onSelect(null);
      setNotice({
        type: "success",
        text: `ASIN ${scope.child_asin} (${scope.marketplace}) 已删除，清理 ${result.removed.runs} 条 Run、${result.removed.strategies} 个策略版本和 ${result.removed.schedules} 个任务`,
      });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setDeleteBusy(false);
    }
  };

  // Associate latest run with each scope
  const latestRunByScopeId = useMemo(() => {
    const map = new Map<string, Run>();
    for (const run of runs) {
      const existing = map.get(run.scope_id);
      if (!existing || new Date(run.created_at).getTime() > new Date(existing.created_at).getTime()) {
        map.set(run.scope_id, run);
      }
    }
    return map;
  }, [runs]);

  // Aggregate scopes by child_asin
  const asinGroups = useMemo<AsinGroup[]>(() => {
    const map = new Map<string, Scope[]>();
    for (const s of scopes) {
      const list = map.get(s.child_asin) || [];
      list.push(s);
      map.set(s.child_asin, list);
    }

    const groups: AsinGroup[] = [];
    map.forEach((itemScopes, asin) => {
      const marketplaceMap = new Map<string, Scope>();
      let brand: string | null = null;
      let representativeSku = "";
      let activeCount = 0;
      let latestRun: Run | undefined = undefined;

      let displayName = "";
      for (const s of itemScopes) {
        marketplaceMap.set(s.marketplace.toUpperCase(), s);
        if (!representativeSku && s.sku) representativeSku = s.sku;
        if (!brand && s.brand) brand = s.brand;
        if (!displayName && s.display_name && s.display_name !== s.child_asin) {
          displayName = s.display_name;
        }
        if (s.status === "ACTIVE") activeCount++;

        const r = latestRunByScopeId.get(s.scope_id);
        if (r) {
          if (!latestRun || new Date(r.created_at).getTime() > new Date(latestRun.created_at).getTime()) {
            latestRun = r;
          }
        }
      }

      groups.push({
        asin,
        sku: representativeSku || itemScopes[0]?.sku || "",
        displayName: displayName || (itemScopes[0]?.display_name !== asin ? (itemScopes[0]?.display_name || "") : "") || "",
        brand,
        scopes: itemScopes,
        marketplaceMap,
        allActive: activeCount === itemScopes.length,
        activeCount,
        totalMarketplaces: itemScopes.length,
        latestRun,
      });
    });

    return groups;
  }, [scopes, latestRunByScopeId]);

  // Dynamic list of brands for dropdown
  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    for (const g of asinGroups) {
      if (g.brand) set.add(g.brand);
    }
    return Array.from(set).sort();
  }, [asinGroups]);

  // Marketplace pills list
  const marketplacePills = useMemo(() => {
    const counts: Record<string, number> = { ALL: scopes.length };
    for (const s of scopes) {
      const m = s.marketplace.toUpperCase();
      counts[m] = (counts[m] || 0) + 1;
    }
    const list: Array<{ code: string; label: string; count: number }> = [
      { code: "ALL", label: "全部站点", count: counts.ALL || 0 },
    ];
    for (const m of STANDARD_EU_MARKETPLACES) {
      list.push({
        code: m,
        label: `${MARKETPLACE_LABELS[m] || m} (${m})`,
        count: counts[m] || 0,
      });
    }
    for (const s of marketplaceSettings) {
      const m = s.marketplace.toUpperCase();
      if (!STANDARD_EU_MARKETPLACES.includes(m as any) && m !== "ALL") {
        if (!list.some((item) => item.code === m)) {
          list.push({
            code: m,
            label: `${MARKETPLACE_LABELS[m] || m} (${m})`,
            count: counts[m] || 0,
          });
        }
      }
    }
    return list;
  }, [scopes, marketplaceSettings]);

  // KPI Metrics
  const kpiMetrics = useMemo(() => {
    const totalAsins = asinGroups.length;
    const totalScopes = scopes.length;
    const activeScopes = scopes.filter((s) => s.status === "ACTIVE").length;
    const fullEu5Count = asinGroups.filter((g) =>
      STANDARD_EU_MARKETPLACES.every((m) => g.marketplaceMap.has(m))
    ).length;
    const activeRate = totalScopes > 0 ? Math.round((activeScopes / totalScopes) * 100) : 0;

    return {
      totalAsins,
      totalScopes,
      activeScopes,
      activeRate,
      fullEu5Count,
    };
  }, [asinGroups, scopes]);

  // Filtered ASIN groups
  const filteredGroups = useMemo(() => {
    return asinGroups.filter((group) => {
      if (selectedMarketplace !== "ALL") {
        if (!group.marketplaceMap.has(selectedMarketplace.toUpperCase())) {
          return false;
        }
      }
      if (selectedBrand !== "ALL") {
        if (group.brand !== selectedBrand) return false;
      }
      if (selectedStatus === "ACTIVE") {
        if (group.activeCount === 0) return false;
      } else if (selectedStatus === "INACTIVE") {
        if (group.allActive) return false;
      }
      if (selectedCoverage === "FULL") {
        const isFull = STANDARD_EU_MARKETPLACES.every((m) => group.marketplaceMap.has(m));
        if (!isFull) return false;
      } else if (selectedCoverage === "PARTIAL") {
        const isFull = STANDARD_EU_MARKETPLACES.every((m) => group.marketplaceMap.has(m));
        if (isFull) return false;
      }
      if (query.trim()) {
        const q = query.toLowerCase().trim();
        const matchAsin = group.asin.toLowerCase().includes(q);
        const matchSku = group.sku.toLowerCase().includes(q) || group.scopes.some((s) => s.sku.toLowerCase().includes(q));
        const matchBrand = (group.brand ?? "").toLowerCase().includes(q);
        const matchProfile = group.scopes.some((s) => s.amazon_profile_id.toLowerCase().includes(q));
        const matchMarketplace = group.scopes.some((s) => s.marketplace.toLowerCase().includes(q));
        if (!matchAsin && !matchSku && !matchBrand && !matchProfile && !matchMarketplace) {
          return false;
        }
      }
      return true;
    });
  }, [asinGroups, selectedMarketplace, selectedBrand, selectedStatus, selectedCoverage, query]);

  // Filtered Flat scopes
  const filteredFlatScopes = useMemo(() => {
    return scopes.filter((scope) => {
      if (selectedMarketplace !== "ALL" && scope.marketplace.toUpperCase() !== selectedMarketplace.toUpperCase()) {
        return false;
      }
      if (selectedBrand !== "ALL" && scope.brand !== selectedBrand) {
        return false;
      }
      if (selectedStatus !== "ALL" && scope.status !== selectedStatus) {
        return false;
      }
      if (query.trim()) {
        const q = query.toLowerCase().trim();
        const matchAsin = scope.child_asin.toLowerCase().includes(q);
        const matchSku = scope.sku.toLowerCase().includes(q);
        const matchMarketplace = scope.marketplace.toLowerCase().includes(q);
        const matchProfile = scope.amazon_profile_id.toLowerCase().includes(q);
        const matchBrand = (scope.brand ?? "").toLowerCase().includes(q);
        if (!matchAsin && !matchSku && !matchMarketplace && !matchProfile && !matchBrand) {
          return false;
        }
      }
      return true;
    });
  }, [scopes, selectedMarketplace, selectedBrand, selectedStatus, query]);

  const copyAsin = async (e: MouseEvent, asin: string) => {
    e.stopPropagation();
    const ok = await copyText(asin);
    if (ok) {
      setCopiedAsin(asin);
      setTimeout(() => {
        setCopiedAsin((cur) => (cur === asin ? null : cur));
      }, 1500);
    }
  };

  const toggleExpand = (asin: string) => {
    setExpandedAsins((prev) => {
      const next = new Set(prev);
      if (next.has(asin)) {
        next.delete(asin);
      } else {
        next.add(asin);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedAsins(new Set(filteredGroups.map((g) => g.asin)));
  };

  const collapseAll = () => {
    setExpandedAsins(new Set());
  };

  const openQuickCreate = (asin: string, sku: string, marketplace: string) => {
    if (isReadOnly) return;
    setCreateInitialValues({
      child_asin: asin,
      sku: sku,
      marketplace: marketplace,
    });
    setCreateOpen(true);
  };

  const current = scopes.find((scope) => scope.scope_id === selected);
  if (current)
    return (
      <>
        <ScopeDetail
          scope={current}
          siteSetting={marketplaceSettings.find((setting) => setting.marketplace === current.marketplace)}
          runs={runs.filter((run) => run.scope_id === current.scope_id)}
          schedules={schedules.filter((schedule) => schedule.scope_id === current.scope_id)}
          back={() => onSelect(null)}
          onDelete={() => setDeleting(current)}
          onChanged={onChanged}
          setNotice={setNotice}
          system={system}
          isRefreshing={isRefreshing}
          onToggleMenu={onToggleMenu}
          onRun={onRun}
          onNavigate={onNavigate}
        />
        {deleting && (
          <DeleteScopeDialog
            scope={deleting}
            runCount={runs.filter((run) => run.scope_id === deleting.scope_id).length}
            scheduleCount={schedules.filter((schedule) => schedule.scope_id === deleting.scope_id).length}
            busy={deleteBusy}
            close={() => setDeleting(null)}
            confirm={() => void removeScope(deleting)}
          />
        )}
      </>
    );

  return (
    <>
      <PageHeading
        title="ASIN 管理"
        isReadOnly={isReadOnly}
        action={
          <>
            <button
              type="button"
              className="secondary-button"
              onClick={() => onNavigate?.("schedules")}
              title="跨 ASIN 查看和管理全部定时任务"
            >
              <CalendarClock />
              定时任务管理
            </button>
            <button
              className="primary-button"
              onClick={() => {
                setCreateInitialValues(null);
                setCreateOpen(true);
              }}
              disabled={isReadOnly}
              title={isReadOnly ? "当前处于只读保护，解除写入锁后可新建 ASIN Scope" : "新建 ASIN Scope"}
            >
              <Plus />
              新建 ASIN Scope
            </button>
          </>
        }
        onRefresh={onChanged}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
      />

      {/* KPI Overview Summary Bar */}
      <section className="asin-kpi-bar">
        <div className="asin-kpi-card">
          <div className="kpi-icon-wrap kpi-icon-primary">
            <Package size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">管理商品 (Unique ASINs)</span>
            <div className="kpi-value-row">
              <strong className="kpi-value">{kpiMetrics.totalAsins}</strong>
              <small className="kpi-sub">款核心商品</small>
            </div>
          </div>
        </div>

        <div className="asin-kpi-card">
          <div className="kpi-icon-wrap kpi-icon-success">
            <Target size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">活跃 Scope 总计</span>
            <div className="kpi-value-row">
              <strong className="kpi-value">{kpiMetrics.activeScopes}</strong>
              <span className="kpi-divider">/</span>
              <span className="kpi-total">{kpiMetrics.totalScopes}</span>
              <span className="kpi-pill kpi-pill-success">{kpiMetrics.activeRate}% 活跃</span>
            </div>
          </div>
        </div>

        <div className="asin-kpi-card">
          <div className="kpi-icon-wrap kpi-icon-accent">
            <Globe size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">欧洲 5 国满覆盖商品</span>
            <div className="kpi-value-row">
              <strong className="kpi-value mono">{kpiMetrics.fullEu5Count}</strong>
              <span className="kpi-divider">/</span>
              <span className="kpi-total mono">{kpiMetrics.totalAsins}</span>
              <span className="kpi-pill kpi-pill-accent">
                {kpiMetrics.totalAsins > 0 ? `${Math.round((kpiMetrics.fullEu5Count / kpiMetrics.totalAsins) * 100)}% 满铺` : "0% 满铺"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Filter Pills */}
      <div className="marketplace-filter-pills">
        {marketplacePills.map((pill) => {
          const isActive = selectedMarketplace === pill.code;
          return (
            <button
              key={pill.code}
              type="button"
              className={`marketplace-pill ${isActive ? "is-active" : ""}`}
              onClick={() => setSelectedMarketplace(pill.code)}
            >
              {pill.code !== "ALL" && <MarketplaceFlag marketplace={pill.code} />}
              <span>{pill.label}</span>
              <span className="pill-count">{pill.count}</span>
            </button>
          );
        })}
      </div>

      {/* Main Table Panel */}
      <section className="panel table-panel">
        <div className="table-toolbar scope-toolbar-row">
          <div className="toolbar-left-group">
            <div className="search">
              <Search />
              <input
                placeholder="搜索 ASIN、SKU、Brand 或 Profile ID..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className="search-clear-btn" onClick={() => setQuery("")} title="清空搜索">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Brand Filter */}
            {brandOptions.length > 0 && (
              <CustomSelect
                value={selectedBrand}
                onChange={(val) => setSelectedBrand(val)}
                options={[
                  { value: "ALL", label: `全部品牌 (${brandOptions.length})` },
                  ...brandOptions.map((b) => ({ value: b, label: b })),
                ]}
                title="按品牌筛选"
                aria-label="按品牌筛选"
              />
            )}

            {/* Status Filter */}
            <CustomSelect
              value={selectedStatus}
              onChange={(val) => setSelectedStatus(val)}
              options={[
                { value: "ALL", label: "全部状态" },
                { value: "ACTIVE", label: "活跃", sublabel: "ACTIVE" },
                { value: "INACTIVE", label: "非活跃 / 异常", sublabel: "INACTIVE" },
              ]}
              title="按运行状态筛选"
              aria-label="按运行状态筛选"
            />

            {/* Coverage Filter (only in grouped / matrix view) */}
            {viewMode !== "flat" && (
              <CustomSelect
                value={selectedCoverage}
                onChange={(val) => setSelectedCoverage(val)}
                options={[
                  { value: "ALL", label: "全量覆盖度" },
                  { value: "FULL", label: "5 国已全覆盖", sublabel: "FULL" },
                  { value: "PARTIAL", label: "存在未开通站点", sublabel: "PARTIAL" },
                ]}
                title="按站点覆盖情况筛选"
                aria-label="按站点覆盖情况筛选"
              />
            )}
          </div>

          <div className="toolbar-right-group">
            {/* Expand / Collapse All in Grouped View */}
            {viewMode === "grouped" && (
              <div className="grouped-expand-controls">
                <button
                  type="button"
                  className="toolbar-text-btn"
                  onClick={expandAll}
                  disabled={filteredGroups.length === 0}
                >
                  全部展开
                </button>
                <span className="btn-sep">·</span>
                <button
                  type="button"
                  className="toolbar-text-btn"
                  onClick={collapseAll}
                  disabled={expandedAsins.size === 0}
                >
                  全部收起
                </button>
              </div>
            )}

            <span className="toolbar-vertical-divider" />

            {/* View Mode Switcher */}
            <div className="scope-view-toggle">
              <button
                type="button"
                className={`view-toggle-btn ${viewMode === "grouped" ? "active" : ""}`}
                onClick={() => setViewMode("grouped")}
                title="ASIN 聚合折叠视图 (推荐)"
              >
                <LayoutGrid size={14} />
                <span>聚合折叠</span>
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${viewMode === "matrix" ? "active" : ""}`}
                onClick={() => setViewMode("matrix")}
                title="5 国矩阵对比视图"
              >
                <Table size={14} />
                <span>5国矩阵</span>
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${viewMode === "flat" ? "active" : ""}`}
                onClick={() => setViewMode("flat")}
                title="平铺列表明细视图"
              >
                <List size={14} />
                <span>平铺明细</span>
              </button>
            </div>
          </div>
        </div>

        {/* View Mode 1: Grouped / Accordion View */}
        {viewMode === "grouped" && (
          <div className="table-scroll">
            <table className="grouped-asin-table">
              <thead>
                <tr>
                  <th style={{ width: "220px" }}>ASIN / 商品信息</th>
                  <th style={{ width: "160px" }}>SKU</th>
                  <th style={{ width: "130px" }}>品牌</th>
                  <th>欧洲 5 国覆盖状态</th>
                  <th style={{ width: "110px" }}>站点覆盖</th>
                  <th style={{ width: "160px" }}>最新运行</th>
                  <th style={{ width: "110px", textAlign: "right" }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.length ? (
                  filteredGroups.map((group) => {
                    const isExpanded = expandedAsins.has(group.asin);
                    const isFullCoverage = STANDARD_EU_MARKETPLACES.every((m) => group.marketplaceMap.has(m));
                    const primaryMarketplace = group.scopes[0]?.marketplace || "DE";

                    return (
                      <Fragment key={group.asin}>
                        <tr
                          className={`grouped-parent-row ${isExpanded ? "is-expanded" : ""}`}
                          onClick={() => toggleExpand(group.asin)}
                        >
                          {/* Col 1: ASIN + Thumbnail + Title + Copy + Link */}
                          <td>
                            <div className="asin-cell-rich">
                              <button
                                type="button"
                                className={`expand-caret-btn ${isExpanded ? "caret-open" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(group.asin);
                                }}
                                title={isExpanded ? "收起站点明细" : "展开站点明细"}
                              >
                                <ChevronRight size={15} />
                              </button>
                              <div className="asin-thumb-box" title={group.displayName || group.asin}>
                                <Package size={16} className="asin-thumb-icon" />
                              </div>
                              <div className="asin-main-info">
                                <div className="asin-line">
                                  <strong className="asin-code mono">{group.asin}</strong>
                                  <button
                                    type="button"
                                    className={`asin-copy-btn ${copiedAsin === group.asin ? "is-copied" : ""}`}
                                    onClick={(e) => copyAsin(e, group.asin)}
                                    title={copiedAsin === group.asin ? "已复制!" : "复制 ASIN"}
                                  >
                                    {copiedAsin === group.asin ? <Check size={12} /> : <Copy size={12} />}
                                  </button>
                                  <a
                                    className="asin-link-btn"
                                    href={getAmazonProductUrl(group.asin, primaryMarketplace)}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`在 Amazon (${primaryMarketplace}) 查看前台`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Col 2: SKU */}
                          <td>
                            <div className="sku-cell-wrapper">
                              <span className="sku-text-mono" title={`SKU: ${group.sku}`}>
                                {group.sku || "-"}
                              </span>
                              {group.scopes.length > 0 && (
                                <button
                                  type="button"
                                  className="sku-edit-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingSkuTarget({
                                      scope: group.scopes[0],
                                      siblingScopes: group.scopes.slice(1),
                                    });
                                  }}
                                  disabled={isReadOnly}
                                  title={isReadOnly ? "当前处于只读保护" : `修改 SKU (${group.sku})`}
                                  aria-label="修改 SKU"
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Col 3: Brand */}
                          <td>
                            <span className="brand-badge-pill">{group.brand || "待验证"}</span>
                          </td>

                          {/* Col 4: 5 Marketplaces Badges */}
                          <td>
                            <div className="coverage-badge-group">
                              {STANDARD_EU_MARKETPLACES.map((m) => {
                                const scope = group.marketplaceMap.get(m);
                                if (scope) {
                                  const isActive = scope.status === "ACTIVE";
                                  return (
                                    <button
                                      key={m}
                                      type="button"
                                      className={`flag-coverage-badge ${isActive ? "badge-active" : "badge-inactive"}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(scope.scope_id);
                                      }}
                                      title={`${m} 站点已开通 (${scope.status})，点击进入详情`}
                                    >
                                      <MarketplaceFlag marketplace={m} />
                                      <span className="badge-code">{m}</span>
                                      <span className={`status-dot ${isActive ? "dot-active" : "dot-inactive"}`} />
                                    </button>
                                  );
                                }
                                return (
                                  <button
                                    key={m}
                                    type="button"
                                    className="flag-coverage-badge badge-missing"
                                    disabled={isReadOnly}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openQuickCreate(group.asin, group.sku, m);
                                    }}
                                    title={isReadOnly ? "当前处于只读保护" : `为此 ASIN 一键开通 ${m} (${MARKETPLACE_LABELS[m] || m}) 站点`}
                                  >
                                    <MarketplaceFlag marketplace={m} className="flag-dimmed" />
                                    <span className="badge-code">{m}</span>
                                    <Plus size={10} className="plus-icon" />
                                  </button>
                                );
                              })}
                            </div>
                          </td>

                          {/* Col 5: Coverage ratio */}
                          <td>
                            <span className={`coverage-pill ${isFullCoverage ? "pill-full" : "pill-partial"}`}>
                              {group.scopes.length} / 5 站点
                            </span>
                          </td>

                          {/* Col 6: Latest Run */}
                          <td>
                            {group.latestRun ? (
                              <div className="latest-run-cell">
                                <RunStateDotBadge
                                  state={group.latestRun.state}
                                  failureCode={group.latestRun.error_code}
                                  failureReason={group.latestRun.error_message}
                                />
                                <small className="run-rel-time">{formatRelativeTime(group.latestRun.created_at)}</small>
                              </div>
                            ) : (
                              <span className="text-muted-small">未运行</span>
                            )}
                          </td>

                          {/* Col 7: Actions */}
                          <td style={{ textAlign: "right" }}>
                            <button
                              type="button"
                              className={`row-expand-indicator ${isExpanded ? "is-active" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(group.asin);
                              }}
                              title={isExpanded ? "收起站点明细" : "展开站点明细"}
                            >
                              <span>{isExpanded ? "收起" : "站点明细"}</span>
                              <ChevronDown size={13} className={`btn-caret ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Sub-table */}
                        {isExpanded && (
                          <tr className="grouped-child-row">
                            <td colSpan={7} className="grouped-subtable-cell">
                              <div className="grouped-subtable-container">
                                <table className="grouped-subtable">
                                  <thead>
                                    <tr>
                                      <th>站点</th>
                                      <th>Profile ID</th>
                                      <th>币种 / 时区</th>
                                      <th>最新运行</th>
                                      <th>定时任务</th>
                                      <th>状态</th>
                                      <th style={{ textAlign: "right" }}>操作</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.scopes.map((scope) => {
                                      const scopeRun = latestRunByScopeId.get(scope.scope_id);
                                      const scopeSchedules = schedules.filter((s) => s.scope_id === scope.scope_id);

                                      return (
                                        <tr key={scope.scope_id} className="subtable-row">
                                          <td>
                                            <div className="subtable-site-cell">
                                              <MarketplaceFlag marketplace={scope.marketplace} />
                                              <strong>{scope.marketplace}</strong>
                                              <span className="site-name-sub">({MARKETPLACE_LABELS[scope.marketplace] || scope.marketplace})</span>
                                            </div>
                                          </td>
                                          <td>
                                            <span className="profile-id-mono">{scope.amazon_profile_id}</span>
                                          </td>
                                          <td>
                                            <span className="currency-tz-text">
                                              {scope.currency} · {scope.timezone}
                                            </span>
                                          </td>
                                          <td>
                                            {scopeRun ? (
                                              <div className="latest-run-cell">
                                                <RunStateDotBadge
                                                  state={scopeRun.state}
                                                  failureCode={scopeRun.error_code}
                                                  failureReason={scopeRun.error_message}
                                                />
                                                <small className="run-rel-time">{formatRelativeTime(scopeRun.created_at)}</small>
                                              </div>
                                            ) : (
                                              <span className="text-muted-small">-</span>
                                            )}
                                          </td>
                                          <td>
                                            <span className="schedule-count-badge">
                                              {scopeSchedules.length > 0 ? `${scopeSchedules.length} 个任务` : "未配置"}
                                            </span>
                                          </td>
                                          <td>
                                            <Status value={scope.status} />
                                          </td>
                                          <td style={{ textAlign: "right" }}>
                                            <div className="subtable-actions-cell">
                                              <button
                                                type="button"
                                                className="icon-button"
                                                onClick={() => setEditingSkuTarget({ scope, siblingScopes: [] })}
                                                disabled={isReadOnly}
                                                title={isReadOnly ? "当前处于只读保护" : `修改 ${scope.marketplace} 站点的 SKU (${scope.sku})`}
                                                aria-label="修改 SKU"
                                              >
                                                <Pencil size={13} />
                                              </button>
                                              <button
                                                type="button"
                                                className="icon-button delete-action"
                                                onClick={() => setDeleting(scope)}
                                                disabled={isReadOnly || deleteBusy}
                                                title={isReadOnly ? "当前处于只读保护" : `删除 ${scope.child_asin} (${scope.marketplace})`}
                                                aria-label="删除站点"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                              <button
                                                type="button"
                                                className="primary-outline-btn-sm"
                                                onClick={() => onSelect(scope.scope_id)}
                                                title={`进入 ${scope.child_asin} (${scope.marketplace}) 详情`}
                                              >
                                                <span>进入详情</span>
                                                <ChevronRight size={13} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      {query ? "没有匹配的 ASIN" : "尚未创建 ASIN Scope"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* View Mode 2: 5-Country Matrix View */}
        {viewMode === "matrix" && (
          <div className="table-scroll">
            <table className="scope-matrix-table">
              <thead>
                <tr>
                  <th style={{ width: "260px" }}>商品 (ASIN / 品牌 / SKU)</th>
                  {STANDARD_EU_MARKETPLACES.map((m) => (
                    <th key={m} style={{ minWidth: "150px" }}>
                      <div className="matrix-th-content">
                        <MarketplaceFlag marketplace={m} />
                        <span>{m} ({MARKETPLACE_LABELS[m]})</span>
                      </div>
                    </th>
                  ))}
                  <th style={{ width: "100px", textAlign: "center" }}>覆盖率</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.length ? (
                  filteredGroups.map((group) => {
                    const isFullCoverage = STANDARD_EU_MARKETPLACES.every((m) => group.marketplaceMap.has(m));
                    const primaryMarketplace = group.scopes[0]?.marketplace || "DE";

                    return (
                      <tr key={group.asin} className="matrix-row">
                        {/* ASIN Column */}
                        <td>
                          <div className="matrix-asin-block">
                            <div className="asin-line">
                              <strong className="asin-code">{group.asin}</strong>
                              <button
                                type="button"
                                className={`asin-copy-btn ${copiedAsin === group.asin ? "is-copied" : ""}`}
                                onClick={(e) => copyAsin(e, group.asin)}
                                title={copiedAsin === group.asin ? "已复制!" : "复制 ASIN"}
                              >
                                {copiedAsin === group.asin ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                              <a
                                className="asin-link-btn"
                                href={getAmazonProductUrl(group.asin, primaryMarketplace)}
                                target="_blank"
                                rel="noreferrer"
                                title="在 Amazon 查看"
                              >
                                <ExternalLink size={12} />
                              </a>
                            </div>
                            <div className="matrix-sub-info">
                              <span className="brand-badge-pill">{group.brand || "待验证"}</span>
                              <span className="asin-sku-tag">{group.sku}</span>
                            </div>
                          </div>
                        </td>

                        {/* 5 Country Cells */}
                        {STANDARD_EU_MARKETPLACES.map((m) => {
                          const scope = group.marketplaceMap.get(m);
                          if (scope) {
                            const scopeRun = latestRunByScopeId.get(scope.scope_id);
                            return (
                              <td key={m}>
                                <div
                                  className="matrix-scope-card"
                                  onClick={() => onSelect(scope.scope_id)}
                                  title={`点击查看 ${group.asin} (${m}) Scope 详情`}
                                >
                                  <div className="matrix-card-top">
                                    <span className="matrix-site-badge">
                                      <MarketplaceFlag marketplace={m} />
                                      <strong>{m}</strong>
                                    </span>
                                    <Status value={scope.status} />
                                  </div>
                                  <div className="matrix-card-mid">
                                    <span className="matrix-sku-text" title={scope.sku}>{scope.sku}</span>
                                    <span className="matrix-curr-badge">{scope.currency}</span>
                                  </div>
                                  <div className="matrix-card-bot">
                                    {scopeRun ? (
                                      <div className="matrix-run-row">
                                        <RunStateDotBadge state={scopeRun.state} />
                                        <small className="matrix-time">{formatRelativeTime(scopeRun.created_at)}</small>
                                      </div>
                                    ) : (
                                      <small className="text-muted-small">未运行</small>
                                    )}
                                    <ChevronRight size={13} className="card-enter-icon" />
                                  </div>
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td key={m}>
                              <button
                                type="button"
                                className="matrix-empty-slot"
                                disabled={isReadOnly}
                                onClick={() => openQuickCreate(group.asin, group.sku, m)}
                                title={isReadOnly ? "只读保护" : `为此 ASIN 一键开通 ${m} (${MARKETPLACE_LABELS[m] || m}) 站点`}
                              >
                                <MarketplaceFlag marketplace={m} className="flag-dimmed" />
                                <span>+ 补齐 {m}</span>
                              </button>
                            </td>
                          );
                        })}

                        {/* Coverage Column */}
                        <td style={{ textAlign: "center" }}>
                          <span className={`coverage-pill ${isFullCoverage ? "pill-full" : "pill-partial"}`}>
                            {group.scopes.length}/5
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      {query ? "没有匹配的 ASIN" : "尚未创建 ASIN Scope"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* View Mode 3: Flat List View */}
        {viewMode === "flat" && (
          <DataTable
            empty={query ? "没有匹配的 ASIN" : "尚未创建 ASIN Scope"}
            columns={["ASIN / SKU", "站点", "Profile ID", "品牌", "币种 / 时区", "最新运行", "状态", ""]}
            rows={filteredFlatScopes.map((scope) => {
              const scopeRun = latestRunByScopeId.get(scope.scope_id);
              return [
                <div key="asin" className="flat-asin-cell">
                  <div className="asin-line">
                    <button
                      type="button"
                      className="entity-link-btn"
                      onClick={() => onSelect(scope.scope_id)}
                    >
                      <strong>{scope.child_asin}</strong>
                    </button>
                    <button
                      type="button"
                      className={`asin-copy-btn ${copiedAsin === scope.child_asin ? "is-copied" : ""}`}
                      onClick={(e) => copyAsin(e, scope.child_asin)}
                      title="复制 ASIN"
                    >
                      {copiedAsin === scope.child_asin ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                    <a
                      className="asin-link-btn"
                      href={getAmazonProductUrl(scope.child_asin, scope.marketplace)}
                      target="_blank"
                      rel="noreferrer"
                      title="在 Amazon 查看"
                    >
                      <ExternalLink size={12} />
                    </a>
                  </div>
                  <div className="sku-cell-wrapper">
                    <small className="asin-sku-tag">{scope.sku}</small>
                    <button
                      type="button"
                      className="sku-edit-btn"
                      onClick={() => {
                        const siblings = scopes.filter((s) => s.child_asin === scope.child_asin && s.scope_id !== scope.scope_id);
                        setEditingSkuTarget({ scope, siblingScopes: siblings });
                      }}
                      disabled={isReadOnly}
                      title={isReadOnly ? "当前处于只读保护" : `修改 SKU (${scope.sku})`}
                      aria-label="修改 SKU"
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                </div>,
                <div key="site" className="subtable-site-cell">
                  <MarketplaceFlag marketplace={scope.marketplace} />
                  <strong>{scope.marketplace}</strong>
                  <span className="site-name-sub">({MARKETPLACE_LABELS[scope.marketplace] || scope.marketplace})</span>
                </div>,
                <span key="profile" className="profile-id-mono">{scope.amazon_profile_id}</span>,
                <span key="brand" className="brand-badge-pill">{scope.brand ?? "待验证"}</span>,
                <span key="currency" className="currency-tz-text">{scope.currency} · {scope.timezone}</span>,
                <div key="run">
                  {scopeRun ? (
                    <div className="latest-run-cell">
                      <RunStateDotBadge state={scopeRun.state} />
                      <small className="run-rel-time">{formatRelativeTime(scopeRun.created_at)}</small>
                    </div>
                  ) : (
                    <span className="text-muted-small">-</span>
                  )}
                </div>,
                <Status key="status" value={scope.status} />,
                <div key="actions" className="table-actions-cell">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => {
                      const siblings = scopes.filter((s) => s.child_asin === scope.child_asin && s.scope_id !== scope.scope_id);
                      setEditingSkuTarget({ scope, siblingScopes: siblings });
                    }}
                    disabled={isReadOnly}
                    title={isReadOnly ? "当前处于只读保护" : `修改 SKU (${scope.sku})`}
                    aria-label={`修改 ${scope.child_asin} SKU`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    className="icon-button delete-action"
                    onClick={() => setDeleting(scope)}
                    disabled={isReadOnly || deleteBusy}
                    title={isReadOnly ? "当前处于只读保护，解除写入锁后可删除" : `删除 ${scope.child_asin}`}
                    aria-label={`删除 ${scope.child_asin}`}
                  >
                    <Trash2 />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => onSelect(scope.scope_id)}
                    title="进入详情"
                    aria-label={`进入 ${scope.child_asin} 详情`}
                  >
                    <ChevronRight />
                  </button>
                </div>,
              ];
            })}
          />
        )}
      </section>

      {/* Create Dialog */}
      {createOpen && (
        <CreateScopeDialog
          marketplaceSettings={marketplaceSettings}
          initialValues={createInitialValues}
          close={() => {
            setCreateOpen(false);
            setCreateInitialValues(null);
          }}
          onCreated={async (id) => {
            setCreateOpen(false);
            setCreateInitialValues(null);
            await onChanged();
            onSelect(id);
          }}
          setNotice={setNotice}
        />
      )}

      {/* Delete Dialog */}
      {deleting && (
        <DeleteScopeDialog
          scope={deleting}
          runCount={runs.filter((run) => run.scope_id === deleting.scope_id).length}
          scheduleCount={schedules.filter((schedule) => schedule.scope_id === deleting.scope_id).length}
          busy={deleteBusy}
          close={() => setDeleting(null)}
          confirm={() => void removeScope(deleting)}
        />
      )}

      {/* Edit SKU Dialog */}
      {editingSkuTarget && (
        <EditScopeSkuDialog
          scope={editingSkuTarget.scope}
          siblingScopes={editingSkuTarget.siblingScopes}
          close={() => setEditingSkuTarget(null)}
          onUpdated={async () => {
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
    </>
  );
}

function EditScopeSkuDialog({
  scope,
  siblingScopes = [],
  close,
  onUpdated,
  setNotice,
}: {
  scope: Scope;
  siblingScopes?: Scope[];
  close: () => void;
  onUpdated: () => Promise<void> | void;
  setNotice: NoticeSetter;
}) {
  const [sku, setSku] = useState(scope.sku);
  const [submitting, setSubmitting] = useState(false);
  const [syncSiblings, setSyncSiblings] = useState(siblingScopes.length > 0);

  const targets = useMemo(() => {
    return syncSiblings ? [scope, ...siblingScopes] : [scope];
  }, [scope, siblingScopes, syncSiblings]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = sku.trim();
    if (!trimmed) {
      setNotice({ type: "error", text: "SKU 不能为空" });
      return;
    }
    if (trimmed === scope.sku && (!syncSiblings || siblingScopes.every((s) => s.sku === trimmed))) {
      close();
      return;
    }
    try {
      setSubmitting(true);
      await Promise.all(
        targets.map((target) => api.patch<Scope>(`/api/v1/scopes/${target.scope_id}`, { sku: trimmed }))
      );
      const siteDesc = targets.length > 1 ? `${targets.length} 个站点` : `${scope.marketplace} 站点`;
      setNotice({
        type: "success",
        text: `ASIN ${scope.child_asin} (${siteDesc}) 的 SKU 已成功更新为 ${trimmed}`,
      });
      await onUpdated();
      close();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      title={`编辑 SKU · ${scope.child_asin}`}
      className="edit-sku-dialog"
      close={submitting ? () => undefined : close}
    >
      <form onSubmit={(e) => void submit(e)} className="edit-sku-form">
        {/* Context Information Card */}
        <div className="sku-context-card">
          <div className="sku-context-row">
            <div className="sku-context-col">
              <span className="sku-context-label">所属站点</span>
              <div className="sku-context-val">
                <MarketplaceFlag marketplace={scope.marketplace} />
                <strong>{scope.marketplace}</strong>
                <span className="sku-context-sub">({MARKETPLACE_LABELS[scope.marketplace] || scope.marketplace})</span>
              </div>
            </div>
            <div className="sku-context-divider" />
            <div className="sku-context-col">
              <span className="sku-context-label">Child ASIN</span>
              <div className="sku-context-val mono">
                <strong>{scope.child_asin}</strong>
              </div>
            </div>
            <div className="sku-context-divider" />
            <div className="sku-context-col">
              <span className="sku-context-label">当前 SKU</span>
              <div className="sku-context-val mono">
                <code className="sku-current-badge">{scope.sku}</code>
              </div>
            </div>
          </div>
        </div>

        {/* Form Main Field */}
        <div className="sku-field-block">
          <label className="sku-field-label" htmlFor="sku-edit-input">
            <span>新商品 SKU</span>
            <span className="sku-required-star">*</span>
          </label>
          <div className="sku-input-container">
            <input
              id="sku-edit-input"
              className="sku-modern-input"
              type="text"
              autoFocus
              required
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="请输入新的商品 SKU"
              spellCheck={false}
              autoComplete="off"
            />
            {sku && sku !== scope.sku && (
              <button
                type="button"
                className="sku-input-reset-btn"
                onClick={() => setSku(scope.sku)}
                title="恢复为当前 SKU"
              >
                <RotateCcw size={12} />
                <span>恢复</span>
              </button>
            )}
          </div>
        </div>

        {/* Sibling scopes checkbox */}
        {siblingScopes.length > 0 && (
          <label className="sku-sync-checkbox-label">
            <input
              type="checkbox"
              checked={syncSiblings}
              onChange={(e) => setSyncSiblings(e.target.checked)}
            />
            <div className="checkbox-text">
              <span>同时同步更新此 ASIN 的其他 <strong>{siblingScopes.length}</strong> 个站点</span>
              <span className="sibling-tags">
                ({siblingScopes.map((s) => s.marketplace).join(", ")})
              </span>
            </div>
          </label>
        )}

        {/* Notice Info Box */}
        <div className="sku-sync-notice">
          <Info size={15} className="notice-icon" />
          <div className="notice-text">
            修改后将自动同步更新关联<strong>策略版本</strong>的绑定信息并重新计算策略 Hash，同时更新<strong>自动驾驶目标</strong>上下文，确保各项优化任务与模型执行一致。
          </div>
        </div>

        {/* Actions */}
        <div className="dialog-actions">
          <button type="button" className="secondary-button" onClick={close} disabled={submitting}>
            取消
          </button>
          <button
            className="primary-button"
            type="submit"
            disabled={submitting || !sku.trim() || (sku.trim() === scope.sku && !syncSiblings)}
          >
            {submitting ? <RefreshCw className="spin" size={14} /> : <Save size={14} />}
            {submitting ? "正在保存..." : "保存修改"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ScopeDetail({
  scope,
  siteSetting,
  runs,
  schedules,
  back,
  onDelete,
  onChanged,
  setNotice,
  system,
  isRefreshing,
  onToggleMenu,
  onRun,
  onNavigate,
}: {
  scope: Scope;
  siteSetting?: MarketplaceSetting;
  runs: Run[];
  schedules: Schedule[];
  back: () => void;
  onDelete: () => void;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
  onRun?: (id: string) => void;
  onNavigate?: (view: View) => void;
}) {
  const [tab, setTab] = useState("overview");
  const [runningNow, setRunningNow] = useState(false);
  const [verifyingIdentity, setVerifyingIdentity] = useState(false);
  const [editingSku, setEditingSku] = useState(false);
  const [latestContext, setLatestContext] = useState<any | null>(null);
  const [latestReport, setLatestReport] = useState<any | null>(null);
  const [latestGoal, setLatestGoal] = useState<any | null>(null);

  const isReadOnly = system?.write_kill_switch ?? true;
  const currencySymbol = scope.currency === "GBP" ? "£" : scope.currency === "USD" ? "$" : "€";

  useEffect(() => {
    let active = true;
    const loadRunData = async () => {
      const latestRun = runs[0];
      let loadedContext: any = null;
      let loadedReport: any = null;
      let loadedGoal: any = null;

      if (latestRun) {
        try {
          const artifacts = await api.get<Artifact[]>(`/api/v1/runs/${latestRun.run_id}/artifacts`);
          if (!active) return;
          const contextArt = artifacts.find(
            (a) => a.kind === "HOLISTIC_CONTEXT" || a.kind === "INPUT_CONTEXT" || a.kind === "pull_package" || a.stage === "pull" || (a.relative_path && (a.relative_path.toLowerCase().includes("context") || a.relative_path.toLowerCase().includes("pull")))
          );
          const reportArt = artifacts.find(
            (a) => a.kind === "EVALUATION_REPORT" || a.kind === "ai_final_recommendation" || a.kind === "ai_analysis_report" || (a.relative_path && (a.relative_path.toLowerCase().includes("evaluation") || a.relative_path.toLowerCase().includes("report") || a.relative_path.toLowerCase().includes("recommendation")))
          );
          const goalArt = artifacts.find(
            (a) => a.kind === "GOAL_SPEC" || a.kind === "optimization_plan" || (a.relative_path && (a.relative_path.toLowerCase().includes("goal") || a.relative_path.toLowerCase().includes("plan")))
          );

          const [contextDetail, reportDetail, goalDetail] = await Promise.all([
            contextArt ? api.get<ArtifactDetail>(`/api/v1/artifacts/${contextArt.artifact_id}`) : Promise.resolve(null),
            reportArt ? api.get<ArtifactDetail>(`/api/v1/artifacts/${reportArt.artifact_id}`) : Promise.resolve(null),
            goalArt ? api.get<ArtifactDetail>(`/api/v1/artifacts/${goalArt.artifact_id}`) : Promise.resolve(null),
          ]);
          if (!active) return;
          if (contextDetail?.data) loadedContext = contextDetail.data;
          if (reportDetail?.data) loadedReport = reportDetail.data;
          if (goalDetail?.data) loadedGoal = goalDetail.data;
        } catch {
          // Fallback gracefully
        }
      }

      // Always query active goal endpoint to get live, up-to-date holistic metrics if needed
      if (!loadedContext || !(loadedContext.business_performance?.daily_metrics?.length > 0) || !loadedGoal) {
        try {
          const goalData = await api.get<any>(`/api/v1/scopes/${scope.scope_id}/goals/latest`);
          if (active && goalData) {
            if (goalData.context) loadedContext = goalData.context;
            if (!loadedReport && goalData.last_report) loadedReport = goalData.last_report;
            if (!loadedGoal && goalData.goal_spec) loadedGoal = goalData.goal_spec;
          }
        } catch {
          // ignore
        }
      }

      if (active) {
        setLatestContext(loadedContext);
        setLatestReport(loadedReport);
        setLatestGoal(loadedGoal);
      }
    };
    void loadRunData();
    return () => {
      active = false;
    };
  }, [runs[0]?.run_id, scope.scope_id]);

  const runNow = async (targetTaskType?: string) => {
    try {
      setRunningNow(true);
      const targetSchedule = targetTaskType
        ? schedules.find((s) => s.task_type === targetTaskType)
        : (schedules.find((s) => s.task_type === "WEEKLY_OPTIMIZATION") ?? schedules.find((s) => s.task_type === "MONTHLY_OPTIMIZATION") ?? schedules[0]);
      if (!targetSchedule) throw new ApiError("SCHEDULE_REQUIRED", "请先在'定时任务'标签页中创建优化任务");
      await api.post(`/api/v1/schedules/${targetSchedule.schedule_id}/run-now`);
      setNotice({ type: "success", text: `已手动触发 ${taskLabel(targetSchedule.task_type)}，Run 进入排队状态` });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setRunningNow(false);
    }
  };

  const verifyIdentity = async () => {
    try {
      setVerifyingIdentity(true);
      await api.post(`/api/v1/scopes/${scope.scope_id}/verify`);
      setNotice({ type: "success", text: `ASIN ${scope.child_asin} 身份校验通过` });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setVerifyingIdentity(false);
    }
  };

  return (
    <>
      <PageHeading
        title={
          <div className="detail-title-group">
            <button className="back-link-btn" onClick={back} title="返回 ASIN 列表">
              <ChevronRight className="rotate-180" size={16} /> 返回列表
            </button>
            <h1>{scope.child_asin}</h1>
          </div>
        }
        badge={<Status value={scope.status} />}
        isReadOnly={isReadOnly}
        action={
          <>
            <button
              className="danger-button"
              onClick={onDelete}
              disabled={isReadOnly || runningNow}
              title={isReadOnly ? "当前处于只读保护，解除写入锁后可删除" : "删除此 ASIN Scope"}
            >
              <Trash2 />
              删除 ASIN
            </button>
            <button
              className="primary-button"
              onClick={() => void runNow()}
              disabled={isReadOnly || scope.status !== "ACTIVE" || runningNow}
              title={
                isReadOnly
                  ? "当前处于只读保护，解除写入锁后可运行"
                  : scope.status !== "ACTIVE"
                  ? "当前 ASIN 非活跃状态"
                  : "立即运行优化"
              }
            >
              {runningNow ? <RefreshCw className="spin" /> : <Play />}
              立即运行优化
            </button>
          </>
        }
        onRefresh={onChanged}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
      />

      <div className="tabs">
        <Tab id="overview" current={tab} set={setTab}>
          资产概览
        </Tab>
        <Tab id="economics" current={tab} set={setTab}>
          经济模型
        </Tab>
        <Tab id="strategy" current={tab} set={setTab}>
          优化策略
        </Tab>
        <Tab id="schedules" current={tab} set={setTab}>
          定时任务
        </Tab>
        <Tab id="runs" current={tab} set={setTab}>
          Runs 历史
        </Tab>
      </div>

      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="two-column">
            <section>
              <SectionTitle
                title="Scope 站点身份"
                action={
                  <div className="section-title-actions">
                    <button
                      type="button"
                      className="section-action"
                      onClick={() => setEditingSku(true)}
                      disabled={isReadOnly}
                      title={isReadOnly ? "当前处于只读保护，解除锁定后可修改" : "修改此 Scope 对应的 SKU"}
                    >
                      <Pencil size={12} />
                      <span>修改 SKU</span>
                    </button>
                    <button
                      className="section-action"
                      onClick={() => void verifyIdentity()}
                      disabled={isReadOnly || verifyingIdentity}
                      title={isReadOnly ? "当前处于只读保护，解除锁定后可校验" : "重新调用 MAP 与 Sorftime 校验身份"}
                    >
                      {verifyingIdentity ? <RefreshCw className="spin" /> : <ShieldCheck />}
                      {verifyingIdentity ? "正在校验" : scope.status === "ACTIVE" ? "重新校验" : "校验身份"}
                    </button>
                  </div>
                }
              />
              <DefinitionList
                rows={[
                  ["Marketplace", scope.marketplace],
                  [
                    "SKU",
                    <div key="sku" className="scope-sku-value-row" style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                      <code className="mono-badge">{scope.sku}</code>
                      <button
                        type="button"
                        className="section-action mini-edit-btn"
                        onClick={() => setEditingSku(true)}
                        disabled={isReadOnly}
                        title={isReadOnly ? "当前处于只读保护，解除锁定后可修改" : "修改 SKU"}
                        aria-label="修改 SKU"
                        style={{ padding: "2px 8px", fontSize: "12px", height: "auto" }}
                      >
                        <Pencil size={11} />
                        <span>编辑</span>
                      </button>
                    </div>,
                  ],
                  ["Integration ID", <code key="integration" className="mono-badge">{siteSetting?.integration_id ?? scope.integration_id}</code>],
                  ["account_id", <code key="acc" className="mono-badge">{scope.account_id}</code>],
                  ["amazon_profile_id", <code key="prof" className="mono-badge">{scope.amazon_profile_id}</code>],
                  ["品牌", scope.brand ?? "待验证"],
                  ["币种 / 时区", `${scope.currency} / ${scope.timezone}`],
                  ["更新时间", dateTime(scope.updated_at)],
                ]}
              />
            </section>
            <section>
              <SectionTitle title="运行与策略概况" />
              <DefinitionList
                rows={[
                  ["最近 Run", runs[0] ? dateTime(runs[0].created_at) : "暂无"],
                  [
                    "当前 Run 状态",
                    runs[0] ? (
                      <div key="status" className="prominent-run-status">
                        <Status value={runs[0].state} />
                        <button
                          type="button"
                          className="run-link-button mono"
                          onClick={() => onRun?.(runs[0].run_id)}
                          title={`查看 Run ${runs[0].run_id} 详情`}
                        >
                          {shortId(runs[0].run_id).toLowerCase() === runs[0].state.toLowerCase()
                            ? "查看详情"
                            : shortId(runs[0].run_id)}
                        </button>
                      </div>
                    ) : "-",
                  ],
                  [
                    "定时任务",
                    <span key="sched" className="schedule-count-highlight">
                      <strong className={schedules.filter((s) => s.enabled).length > 0 ? "has-active" : "zero-active"}>
                        {schedules.filter((s) => s.enabled).length}
                      </strong> 条已启用
                      {schedules.filter((s) => !s.enabled).length > 0 && (
                        <small className="paused-count-sub">
                          （{schedules.filter((s) => !s.enabled).length} 条已暂停）
                        </small>
                      )}
                    </span>,
                  ],
                  ["质检状态", runs[0]?.quality_status ? <Status key="q" value={runs[0].quality_status} /> : "暂无"],
                ]}
              />
            </section>
          </div>

          {/* Daily Ad Spend, Sales & ACOS Trends Card */}
          <DailyAdPerformanceCard
            scopeId={scope.scope_id}
            context={latestContext}
            report={latestReport}
            goal={latestGoal}
            currencySymbol={currencySymbol}
            marketplace={scope.marketplace}
            asin={scope.child_asin}
          />
        </div>
      )}

      {tab === "economics" && (
        <EconomicsEditor
          scope={scope}
          onChanged={onChanged}
          setNotice={setNotice}
          isReadOnly={isReadOnly}
        />
      )}
      {tab === "strategy" && (
        <StrategySettingsPage
          scopes={[scope]}
          selected={scope.scope_id}
          onSelect={() => {}}
          onChanged={onChanged}
          setNotice={setNotice}
          system={system}
          isRefreshing={isRefreshing}
          embedded
        />
      )}
      {tab === "schedules" && (
        <SchedulesEditor
          scope={scope}
          schedules={schedules}
          onChanged={onChanged}
          setNotice={setNotice}
          isReadOnly={isReadOnly}
        />
      )}
      {tab === "runs" && (
        <section className="panel table-panel">
          <DataTable
            empty="暂无 Run 记录"
            columns={["Run ID", "任务类型", "触发方式", "策略 Hash", "质量状态", "运行状态", "创建时间", "操作"]}
            rows={runs
              .filter((r) => !AUTONOMOUS_TASK_TYPES.has(r.task_type || ""))
              .map((run) => [
              <button
                key="id"
                type="button"
                className="run-link-button mono"
                onClick={() => onRun?.(run.run_id)}
                title={`查看 Run ${run.run_id} 详情`}
              >
                {shortId(run.run_id)}
              </button>,
              taskLabel(run.task_type),
              run.trigger_source,
              <span key="hash" className="mono">
                {shortHash(run.strategy_hash)}
              </span>,
              run.quality_status ? <Status key="q" value={run.quality_status} /> : "-",
              <Status key="status" value={run.state} />,
              dateTime(run.created_at),
              <div key="action" className="table-actions-cell">
                {run.state === "APPROVAL_REQUIRED" ? (
                  isReadOnly ? (
                    <button
                      type="button"
                      className="action-pill primary-pill disabled"
                      disabled
                      title="当前处于只读保护，解除锁定后可操作"
                      aria-label="当前处于只读保护，解除锁定后可操作"
                    >
                      <Lock size={12} />
                      <span>去审批</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="action-pill primary-pill"
                      onClick={() => {
                        onRun?.(run.run_id);
                        onNavigate?.("approvals");
                      }}
                      title="立即处理人工审批"
                    >
                      <Check size={12} />
                      <span>去审批</span>
                    </button>
                  )
                ) : ["FAILED", "BLOCKED", "EXECUTION_PARTIAL"].includes(run.state) ? (
                  <button
                    type="button"
                    className="action-pill danger-pill"
                    onClick={() => onRun?.(run.run_id)}
                    title="查看异常诊断日志"
                  >
                    <AlertTriangle size={12} />
                    <span>查看诊断</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="action-pill secondary-pill"
                    onClick={() => onRun?.(run.run_id)}
                    title="查看运行详情"
                  >
                    <span>查看详情</span>
                  </button>
                )}
              </div>,
            ])}
          />
          <div className="table-pagination-bar">
            <span className="pagination-count-label">
              共 <strong>{runs.length}</strong> 条历史 Runs 记录
            </span>
          </div>
        </section>
      )}

      {editingSku && (
        <EditScopeSkuDialog
          scope={scope}
          close={() => setEditingSku(false)}
          onUpdated={async () => {
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
    </>
  );
}

function EconomicsEditor({
  scope,
  onChanged,
  setNotice,
  isReadOnly = false,
}: {
  scope: Scope;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  isReadOnly?: boolean;
}) {
  const [versions, setVersions] = useState<StrategyVersion[]>([]);
  const [active, setActive] = useState<StrategyVersion | null>(null);
  const [strategy, setStrategy] = useState<any>(null);
  const [validation, setValidation] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api.get<StrategyVersion[]>(`/api/v1/scopes/${scope.scope_id}/strategies`);
    setVersions(data);
    const candidate =
      data.find((v) => v.status === "DRAFT") ??
      data.find((v) => v.status === "VALIDATED") ??
      data[0];
    if (candidate) {
      const detail = await api.get<StrategyVersion>(`/api/v1/strategy-versions/${candidate.version_id}`);
      setActive(detail);
      setStrategy(simplifyUiStrategy(JSON.parse(detail.canonical_json!)));
      setValidation(detail.validation_result_json ? JSON.parse(detail.validation_result_json) : null);
    }
  }, [scope.scope_id]);

  useEffect(() => {
    void load();
  }, [load]);

  const createDraft = async () => {
    try {
      setBusy(true);
      await api.post(`/api/v1/scopes/${scope.scope_id}/strategies/drafts`, {
        change_note: versions.length ? "经济模型修订" : "初始经济模型",
        strategy: versions.length ? strategy : undefined,
      });
      setNotice({ type: "success", text: "已创建经济模型新草稿版本" });
      await load();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!active || !strategy) return;
    try {
      setBusy(true);
      await api.put(`/api/v1/strategy-versions/${active.version_id}`, {
        strategy,
        change_note: active.change_note,
      });
      setNotice({ type: "success", text: "草稿已保存" });
      await load();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!active) return;
    try {
      setBusy(true);
      await save();
      const result = await api.post<any>(`/api/v1/strategy-versions/${active.version_id}/validate`);
      setValidation(result);
      setNotice({
        type: result.valid ? "success" : "error",
        text: result.valid ? "策略校验通过，随时可以发布" : `校验发现 ${result.errors.length} 项错误`,
      });
      await load();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!active) return;
    try {
      setBusy(true);
      await api.post(`/api/v1/strategy-versions/${active.version_id}/publish`);
      setNotice({ type: "success", text: `策略版本 v${active.version} 已正式发布并生效` });
      await load();
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(false);
    }
  };

  if (!strategy || !active)
    return (
      <section className="empty-state">
        <CircleDollarSign />
        <h2>尚未创建经济模型</h2>
        <p>配置售价、FBA 操作费、佣金与产品成本后，系统将自动推演 Break-even CPA 与 ACOS 护栏。</p>
        <button
          className="primary-button"
          onClick={() => void createDraft()}
          disabled={busy || isReadOnly}
          title={isReadOnly ? "当前处于只读保护，解除写入锁后可创建" : "创建经济模型草稿"}
        >
          <Plus />
          创建经济模型草稿
        </button>
      </section>
    );

  const set = (path: string, value: any) => {
    const clone = structuredClone(strategy);
    const parts = path.split(".");
    let cursor = clone;
    parts.slice(0, -1).forEach((part) => (cursor = cursor[part]));
    cursor[parts.at(-1)!] = value;
    setStrategy(clone);
    setValidation(null);
  };

  const isDraft = active.status === "DRAFT";
  const isValidated = active.status === "VALIDATED";
  const canEdit = !isReadOnly && isDraft;
  const derived = derivePreview(strategy);
  const productStages = [
    { value: "LAUNCH", label: "LAUNCH", detail: "新品启动 · 优先积累有效流量与转化信号", icon: <Sparkles /> },
    { value: "PROFIT", label: "PROFIT", detail: "利润优先 · 以 ACOS、CPA 与盈亏护栏为核心", icon: <CircleDollarSign /> },
    { value: "SCALE", label: "SCALE", detail: "规模增长 · 在效率护栏内扩大流量和订单", icon: <TrendingUp /> },
  ] as const;

  return (
    <div className="economics-layout">
      <div className="editor-main">
        <div className="version-bar">
          <div>
            <strong>版本 v{active.version}</strong>
            <Status value={active.status} />
            <span className="mono">{shortHash(active.strategy_hash)}</span>
          </div>
          <div>
            <button
              className={!isDraft && !isReadOnly ? "primary-button" : "secondary-button"}
              onClick={() => void createDraft()}
              disabled={busy || isReadOnly || isDraft}
              title={
                isReadOnly
                  ? "当前处于只读保护，解除写入锁后可创建版本"
                  : isDraft
                  ? "当前已有草稿版本正在编辑"
                  : "基于当前模型创建新草稿版本"
              }
            >
              <Plus size={14} />
              新建版本
            </button>
            <button
              className="secondary-button"
              onClick={() => void validate()}
              disabled={busy || !canEdit}
              title={
                isReadOnly
                  ? "当前处于只读保护，解除写入锁后可校验"
                  : !isDraft
                  ? "非草稿状态不可校验"
                  : "校验草稿合法性与护栏"
              }
            >
              <Check size={14} />
              校验草稿
            </button>
            <button
              className={isValidated && !isReadOnly ? "primary-button" : "secondary-button"}
              onClick={() => void publish()}
              disabled={busy || isReadOnly || !isValidated}
              title={
                isReadOnly
                  ? "当前处于只读保护，解除写入锁后可发布"
                  : !isValidated
                  ? "需先通过草稿校验（状态为 VALIDATED）后方可发布策略"
                  : "正式发布此策略版本并生效"
              }
            >
              <ShieldCheck size={14} />
              发布策略
            </button>
          </div>
        </div>

        {!isDraft && (
          <div className="economics-version-tip">
            <Info size={14} />
            <span>
              {active.status === "PUBLISHED"
                ? "当前展示为已发布的线上生效版本（只读）。如需调整参数，请点击右上角「新建版本」生成新草稿。"
                : "当前版本已通过校验，可直接点击右上角「发布策略」上线生效。"}
            </span>
          </div>
        )}

        <FormSection title="ASIN 所处阶段">
          <div className="task-mode-segments product-stage-segments" role="radiogroup" aria-label="ASIN 所处阶段">
            {productStages.map((stage) => (
              <label key={stage.value} className={`${strategy.product.stage === stage.value ? "selected" : ""} ${!canEdit ? "disabled" : ""}`.trim()}>
                <input
                  type="radio"
                  name="product-stage"
                  value={stage.value}
                  checked={strategy.product.stage === stage.value}
                  disabled={!canEdit}
                  onChange={() => set("product.stage", stage.value)}
                />
                <span>
                  {stage.icon}
                  <strong>{stage.label}</strong>
                  <small>{stage.detail}</small>
                </span>
              </label>
            ))}
          </div>
        </FormSection>

        <FormSection title="单件利润与成本结构">
          <div className="profit-formula">
            <strong>单件利润 = 有效售价 − FBA 操作费 − 销售佣金 − VAT 税金 − 产品成本</strong>
            <span>有效售价 = 售价 ×（1 − 退款率）</span>
          </div>
          <div className="form-grid">
            <MoneyField
              label="含税售价"
              path="economics.gross_order_revenue_vat_inclusive"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
            <PercentField
              label="预估退款率"
              path="economics.refund_rate"
              strategy={strategy}
              set={set}
              disabled={!canEdit}
            />
            <MoneyField
              label="FBA 操作费"
              path="economics.fulfillment_cost"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
            <MoneyField
              label="Amazon 佣金"
              path="economics.amazon_fees"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
            <PercentField
              label="VAT 税率"
              path="tax.vat_rate"
              strategy={strategy}
              set={set}
              disabled={!canEdit}
            />
            <MoneyField
              label="产品到仓成本"
              path="economics.landed_product_cost"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
          </div>
        </FormSection>

        <FormSection title="广告优化目标与护栏">
          <div className="profit-formula">
            <strong>ACOS 销售额口径：未税（VAT excl.）</strong>
            <span>与欧洲广告报表原始销售额口径一致</span>
          </div>
          <div className="form-grid four">
            <PercentField
              label="Target ACOS"
              path="economics.target_acos"
              strategy={strategy}
              set={set}
              disabled={!canEdit}
            />
            <PercentField
              label="Hard ACOS (上限)"
              path="economics.hard_acos"
              strategy={strategy}
              set={set}
              disabled={!canEdit}
            />
            <MoneyField
              label="Target CPA"
              path="economics.target_cpa"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
            <MoneyField
              label="Hard CPA (上限)"
              path="economics.hard_cpa"
              strategy={strategy}
              set={set}
              currency={scope.currency}
              disabled={!canEdit}
            />
          </div>
        </FormSection>

        <div className="form-actions">
          <button
            className="secondary-button"
            onClick={() => void save()}
            disabled={busy || !canEdit}
            title={
              isReadOnly
                ? "当前处于只读保护，解除写入锁后可保存"
                : !isDraft
                ? "非草稿版本无法保存"
                : "保存当前草稿修订"
            }
          >
            <Save size={14} />
            保存草稿
          </button>
        </div>
      </div>

      <aside className="economics-aside">
        <h3>预计单件利润拆解</h3>
        <BridgeRow label="售价（含 VAT）" value={money(derived.price, scope.currency)} />
        <BridgeRow
          label={`退款损失 (${percent(derived.refundRate)})`}
          value={money(derived.refundAmount, scope.currency)}
          negative
        />
        <BridgeRow label="有效售价" value={money(derived.effectivePrice, scope.currency)} strong />
        <BridgeRow label="FBA 操作费" value={money(derived.fbaFee, scope.currency)} negative />
        <BridgeRow label="Amazon 佣金" value={money(derived.commission, scope.currency)} negative />
        <BridgeRow label="VAT 税金" value={money(derived.vat, scope.currency)} negative />
        <BridgeRow label="产品成本" value={money(derived.productCost, scope.currency)} negative />
        <BridgeRow label="单件净利润" value={money(derived.profit, scope.currency)} strong />
        <div className="break-even">
          <span>
            Break-even CPA
            <strong>{money(derived.profit, scope.currency)}</strong>
          </span>
          <span>
            Break-even ACOS
            <strong>{derived.acos == null ? "-" : percent(derived.acos)}</strong>
          </span>
        </div>
        {validation && <ValidationPanel validation={validation} />}
      </aside>
    </div>
  );
}

export type ScheduleTab = "ALL" | "ENABLED" | "TODAY" | "APPROVAL" | "ALERT" | "PAUSED";
export type ScheduleSortField = "next_run_at" | "child_asin" | "sku" | "consecutive_failures" | "task_type";
export type ScheduleSortOrder = "asc" | "desc";
export type ScheduleViewMode = "TABLE" | "GROUPED" | "HEATMAP";

export function isScheduleToday(schedule: Schedule): boolean {
  if (!schedule.next_run_at || !schedule.enabled) return false;
  const nextDate = new Date(schedule.next_run_at);
  if (isNaN(nextDate.getTime())) return false;
  const now = new Date();
  return (
    nextDate.getFullYear() === now.getFullYear() &&
    nextDate.getMonth() === now.getMonth() &&
    nextDate.getDate() === now.getDate()
  );
}

export function GlobalSchedulesPage({
  scopes,
  schedules,
  system,
  onChanged,
  setNotice,
  isRefreshing,
  onToggleMenu,
  onScope,
  onBack,
}: {
  scopes: Scope[];
  schedules: Schedule[];
  system: SystemInfo | null;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
  onScope: (id: string) => void;
  onBack: () => void;
}) {
  const [tab, setTab] = useState<ScheduleTab>("ALL");
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>("ALL");
  const [selectedTaskType, setSelectedTaskType] = useState<string>("ALL");
  const [selectedTaskMode, setSelectedTaskMode] = useState<string>("ALL");
  const [query, setQuery] = useState("");
  const [sortField, setSortField] = useState<ScheduleSortField>("next_run_at");
  const [sortOrder, setSortOrder] = useState<ScheduleSortOrder>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewMode, setViewMode] = useState<ScheduleViewMode>("TABLE");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [batchMode, setBatchMode] = useState<TaskMode>("APPROVAL_REQUIRED");
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  const [editing, setEditing] = useState<Schedule | null>(null);
  const [deleting, setDeleting] = useState<Schedule | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCompletedReviews, setShowCompletedReviews] = useState(false);
  const isReadOnly = system?.write_kill_switch ?? true;
  const scopeById = useMemo(() => new Map(scopes.map((scope) => [scope.scope_id, scope])), [scopes]);

  const effectiveSchedules = useMemo(
    () => schedules.filter((s) => !AUTONOMOUS_TASK_TYPES.has(s.task_type || "")),
    [schedules]
  );

  const completedReviewCount = useMemo(
    () => effectiveSchedules.filter(isExecutedReviewSchedule).length,
    [effectiveSchedules]
  );

  const loadTemplates = useCallback(async () => {
    try {
      const data = await api.get<ScheduleTemplate[]>("/api/v1/schedule-templates");
      setTemplates(data);
      return data;
    } catch (error) {
      showError(error, setNotice);
      return [];
    }
  }, [setNotice]);

  useEffect(() => { void loadTemplates(); }, [loadTemplates]);
  useEffect(() => {
    const liveIds = new Set(effectiveSchedules.map((schedule) => schedule.schedule_id));
    setSelectedIds((current) => new Set([...current].filter((scheduleId) => liveIds.has(scheduleId))));
  }, [effectiveSchedules]);

  // KPI Metrics Calculation
  const kpiMetrics = useMemo(() => {
    const total = effectiveSchedules.length;
    const enabled = effectiveSchedules.filter((s) => s.enabled).length;
    const paused = total - enabled;
    const today = effectiveSchedules.filter((s) => isScheduleToday(s)).length;
    const approval = effectiveSchedules.filter((s) => s.task_mode === "APPROVAL_REQUIRED").length;
    const autoExec = effectiveSchedules.filter((s) => s.task_mode === "AUTO_EXECUTE").length;
    const alerts = effectiveSchedules.filter((s) => (s.consecutive_failures ?? 0) > 0).length;
    return { total, enabled, paused, today, approval, autoExec, alerts };
  }, [effectiveSchedules]);

  // Marketplace pills calculation
  const marketplacePills = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of effectiveSchedules) {
      const sc = scopeById.get(s.scope_id);
      const mp = sc?.marketplace ?? "OTHER";
      counts.set(mp, (counts.get(mp) ?? 0) + 1);
    }
    const list: Array<{ code: string; label: string; count: number }> = [
      { code: "ALL", label: "全部站点", count: effectiveSchedules.length },
    ];
    const order = ["UK", "DE", "FR", "IT", "ES", "US", "CA", "JP"];
    for (const mp of order) {
      if (counts.has(mp)) {
        list.push({ code: mp, label: mp, count: counts.get(mp)! });
      }
    }
    for (const [mp, count] of counts.entries()) {
      if (!order.includes(mp) && mp !== "ALL") {
        list.push({ code: mp, label: mp, count });
      }
    }
    return list;
  }, [effectiveSchedules, scopeById]);

  // Quick tabs definition
  const quickTabs: Array<{ id: ScheduleTab; label: string; count: number }> = [
    { id: "ALL", label: "全部", count: kpiMetrics.total },
    { id: "ENABLED", label: "已启用", count: kpiMetrics.enabled },
    { id: "TODAY", label: "今日待运行", count: kpiMetrics.today },
    { id: "APPROVAL", label: "需要审批", count: kpiMetrics.approval },
    { id: "PAUSED", label: "已暂停", count: kpiMetrics.paused },
    { id: "ALERT", label: "异常告警", count: kpiMetrics.alerts },
  ];

  // Filter and Sort
  const filteredAndSortedSchedules = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = effectiveSchedules.filter((schedule) => {
      // Hide executed experiment review schedules by default
      if (!showCompletedReviews && isExecutedReviewSchedule(schedule)) return false;

      // Tab filter
      if (tab === "ENABLED" && !schedule.enabled) return false;
      if (tab === "PAUSED" && schedule.enabled) return false;
      if (tab === "TODAY" && !isScheduleToday(schedule)) return false;
      if (tab === "APPROVAL" && schedule.task_mode !== "APPROVAL_REQUIRED") return false;
      if (tab === "ALERT" && (schedule.consecutive_failures ?? 0) <= 0) return false;

      const scope = scopeById.get(schedule.scope_id);

      // Marketplace filter
      if (selectedMarketplace !== "ALL" && scope?.marketplace !== selectedMarketplace) {
        return false;
      }

      // Task Type filter
      if (selectedTaskType !== "ALL" && schedule.task_type !== selectedTaskType) {
        return false;
      }

      // Task Mode filter
      if (selectedTaskMode !== "ALL" && schedule.task_mode !== selectedTaskMode) {
        return false;
      }

      // Query filter
      if (normalizedQuery) {
        const matches = [
          scope?.child_asin,
          scope?.sku,
          scope?.marketplace,
          schedule.display_name,
          schedule.task_type,
          schedule.local_time,
          schedule.timezone,
        ].some((val) => String(val ?? "").toLowerCase().includes(normalizedQuery));
        if (!matches) return false;
      }

      return true;
    });

    // Sort
    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortField === "next_run_at") {
        const timeA = a.next_run_at ? new Date(a.next_run_at).getTime() : Infinity;
        const timeB = b.next_run_at ? new Date(b.next_run_at).getTime() : Infinity;
        comparison = timeA - timeB;
      } else if (sortField === "child_asin") {
        const asinA = scopeById.get(a.scope_id)?.child_asin ?? "";
        const asinB = scopeById.get(b.scope_id)?.child_asin ?? "";
        comparison = asinA.localeCompare(asinB);
      } else if (sortField === "sku") {
        const skuA = scopeById.get(a.scope_id)?.sku ?? "";
        const skuB = scopeById.get(b.scope_id)?.sku ?? "";
        comparison = skuA.localeCompare(skuB);
      } else if (sortField === "consecutive_failures") {
        const failA = a.consecutive_failures ?? 0;
        const failB = b.consecutive_failures ?? 0;
        comparison = failA - failB;
      } else if (sortField === "task_type") {
        comparison = a.task_type.localeCompare(b.task_type);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [
    effectiveSchedules,
    tab,
    selectedMarketplace,
    selectedTaskType,
    selectedTaskMode,
    query,
    scopeById,
    sortField,
    sortOrder,
    showCompletedReviews,
  ]);

  // Paginated slice
  const paginatedSchedules = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSortedSchedules.slice(start, start + pageSize);
  }, [filteredAndSortedSchedules, page, pageSize]);

  const groupedSchedules = useMemo(() => {
    const groups = new Map<string, Schedule[]>();
    for (const schedule of filteredAndSortedSchedules) {
      const current = groups.get(schedule.scope_id) ?? [];
      current.push(schedule);
      groups.set(schedule.scope_id, current);
    }
    return [...groups.entries()];
  }, [filteredAndSortedSchedules]);

  const toggleSelected = (scheduleId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(scheduleId)) next.delete(scheduleId);
      else next.add(scheduleId);
      return next;
    });
  };

  const selectSchedules = (scheduleIds: string[], selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const scheduleId of scheduleIds) selected ? next.add(scheduleId) : next.delete(scheduleId);
      return next;
    });
  };

  // Check if active filters exist
  const hasActiveFilters = Boolean(
    query.trim() ||
    tab !== "ALL" ||
    selectedMarketplace !== "ALL" ||
    selectedTaskType !== "ALL" ||
    selectedTaskMode !== "ALL"
  );

  const resetFilters = () => {
    setTab("ALL");
    setSelectedMarketplace("ALL");
    setSelectedTaskType("ALL");
    setSelectedTaskMode("ALL");
    setQuery("");
    setPage(1);
  };

  const handleSort = (field: ScheduleSortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "consecutive_failures" ? "desc" : "asc");
    }
    setPage(1);
  };

  const renderSortHeader = (label: string, field: ScheduleSortField) => {
    const isCurrent = sortField === field;
    return (
      <button
        type="button"
        className={`table-sort-header ${isCurrent ? "is-sorted" : ""}`}
        onClick={() => handleSort(field)}
        title={`按 ${label} ${isCurrent && sortOrder === "asc" ? "降序" : "升序"} 排序`}
      >
        <span>{label}</span>
        {isCurrent ? (
          sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
        ) : (
          <ArrowUpDown size={12} className="sort-icon-idle" />
        )}
      </button>
    );
  };

  const toggle = async (schedule: Schedule) => {
    try {
      setBusy(`toggle:${schedule.schedule_id}`);
      await api.post(`/api/v1/schedules/${schedule.schedule_id}/${schedule.enabled ? "disable" : "enable"}`);
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const runNow = async (schedule: Schedule) => {
    try {
      setBusy(`run:${schedule.schedule_id}`);
      await api.post(`/api/v1/schedules/${schedule.schedule_id}/run-now`);
      setNotice({ type: "success", text: `${schedule.display_name || taskModeLabel(schedule.task_mode)} 已手动触发并进入队列` });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (schedule: Schedule) => {
    try {
      setBusy(`delete:${schedule.schedule_id}`);
      const result = await api.delete<{ preserved_runs: number }>(`/api/v1/schedules/${schedule.schedule_id}`);
      setDeleting(null);
      setNotice({
        type: "success",
        text: result.preserved_runs
          ? `任务已安全删除，${result.preserved_runs} 条历史 Runs 与审计已保留`
          : "任务已删除",
      });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const runBatch = async (action: "ENABLE" | "DISABLE" | "SET_MODE" | "SHIFT_TIME" | "DELETE", extra: Record<string, unknown> = {}) => {
    const scheduleIds = [...selectedIds];
    if (!scheduleIds.length) return;
    if (action === "DELETE" && !window.confirm(`确认删除已选的 ${scheduleIds.length} 个定时任务？历史 Runs 与审计记录会保留。`)) return;
    try {
      setBusy(`batch:${action}`);
      const result = await api.post<{ updated_count: number; preserved_runs: number }>("/api/v1/schedules/batch", { schedule_ids: scheduleIds, action, ...extra });
      const labels: Record<typeof action, string> = { ENABLE: "启用", DISABLE: "暂停", SET_MODE: "修改模式", SHIFT_TIME: "调整时间", DELETE: "删除" };
      setNotice({ type: "success", text: `已批量${labels[action]} ${result.updated_count} 个任务${result.preserved_runs ? `，保留 ${result.preserved_runs} 条历史 Runs` : ""}` });
      setSelectedIds(new Set());
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const selectedScopeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const schedule of schedules) if (selectedIds.has(schedule.schedule_id)) ids.add(schedule.scope_id);
    return [...ids];
  }, [schedules, selectedIds]);

  return (
    <>
      <PageHeading
        title="ASIN 运营 · 定时任务管理"
        isReadOnly={isReadOnly}
        action={
          <button type="button" className="secondary-button" onClick={onBack}>
            <Target />
            返回 ASIN 列表
          </button>
        }
        onRefresh={onChanged}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
      />

      {/* KPI Overview Summary Bar */}
      <section className="asin-kpi-bar schedule-kpi-bar">
        <div
          className={`asin-kpi-card schedule-kpi-card ${tab === "ALL" ? "is-active" : ""}`}
          onClick={() => {
            setTab("ALL");
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="查看全部定时任务"
        >
          <div className="kpi-icon-wrap kpi-icon-primary">
            <CalendarClock size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">全部定时任务</span>
            <div className="kpi-value-row">
              <strong className="kpi-value">{kpiMetrics.total}</strong>
              <span className="kpi-divider">/</span>
              <span className="kpi-sub">
                <strong>{kpiMetrics.enabled}</strong> 启用 · <strong>{kpiMetrics.paused}</strong> 暂停
              </span>
            </div>
          </div>
        </div>

        <div
          className={`asin-kpi-card schedule-kpi-card ${tab === "TODAY" ? "is-active" : ""}`}
          onClick={() => {
            setTab("TODAY");
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="查看今日待运行任务"
        >
          <div className="kpi-icon-wrap kpi-icon-success">
            <Clock3 size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">今日待执行任务</span>
            <div className="kpi-value-row">
              <strong className="kpi-value">{kpiMetrics.today}</strong>
              <span className="kpi-pill kpi-pill-success">
                {kpiMetrics.total > 0 ? `${Math.round((kpiMetrics.today / kpiMetrics.total) * 100)}% 今日触发` : "0% 今日触发"}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`asin-kpi-card schedule-kpi-card ${tab === "APPROVAL" ? "is-active" : ""}`}
          onClick={() => {
            setTab("APPROVAL");
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="查看需人工审批的任务"
        >
          <div className="kpi-icon-wrap kpi-icon-accent">
            <ListChecks size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">需要审批 (安全防线)</span>
            <div className="kpi-value-row">
              <strong className="kpi-value mono">{kpiMetrics.approval}</strong>
              <span className="kpi-divider">/</span>
              <span className="kpi-total mono">{kpiMetrics.total}</span>
              <span className="kpi-pill kpi-pill-accent">
                {kpiMetrics.total > 0 ? `${Math.round((kpiMetrics.approval / kpiMetrics.total) * 100)}% 需审核` : "0% 需审核"}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`asin-kpi-card schedule-kpi-card ${kpiMetrics.alerts > 0 ? "kpi-card-alert" : ""} ${tab === "ALERT" ? "is-active" : ""}`}
          onClick={() => {
            setTab("ALERT");
            setPage(1);
          }}
          role="button"
          tabIndex={0}
          title="查看异常连续失败任务"
        >
          <div className={`kpi-icon-wrap ${kpiMetrics.alerts > 0 ? "kpi-icon-danger" : "kpi-icon-muted"}`}>
            <AlertTriangle size={20} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">异常失败告警</span>
            <div className="kpi-value-row">
              <strong className={`kpi-value ${kpiMetrics.alerts > 0 ? "kpi-value-alert" : ""}`}>
                {kpiMetrics.alerts}
              </strong>
              <span className={`kpi-pill ${kpiMetrics.alerts > 0 ? "kpi-pill-danger" : "kpi-pill-gray"}`}>
                {kpiMetrics.alerts > 0 ? "需排查" : "运行正常"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Filter Pills */}
      {marketplacePills.length > 1 && (
        <div className="marketplace-filter-pills schedule-marketplace-pills">
          {marketplacePills.map((pill) => {
            const isActive = selectedMarketplace === pill.code;
            return (
              <button
                key={pill.code}
                type="button"
                className={`marketplace-pill ${isActive ? "is-active" : ""}`}
                onClick={() => {
                  setSelectedMarketplace(pill.code);
                  setPage(1);
                }}
              >
                {pill.code !== "ALL" && <MarketplaceFlag marketplace={pill.code} />}
                <span>{pill.label}</span>
                <span className="pill-count">{pill.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main Table Panel */}
      <section className="panel table-panel schedule-table-panel global-schedule-panel">
        <div className="table-toolbar global-schedule-toolbar">
          <div className="global-schedule-toolbar-header">
            <div>
              <SectionTitle title={`全部定时任务（${filteredAndSortedSchedules.length}/${effectiveSchedules.length}）`} />
              <p className="muted-copy">跨 ASIN 批量管理、按产品聚合，并检查 7×24 小时调度密度。</p>
            </div>

            <div className="schedule-view-actions">
              <div className="schedule-view-switch" role="group" aria-label="定时任务视图">
                {([
                  { id: "TABLE", label: "列表", icon: <Table size={14} /> },
                  { id: "GROUPED", label: "按 ASIN", icon: <LayoutGrid size={14} /> },
                  { id: "HEATMAP", label: "调度热力图", icon: <CalendarDays size={14} /> },
                ] as Array<{ id: ScheduleViewMode; label: string; icon: ReactNode }>).map((item) => (
                  <button key={item.id} type="button" className={viewMode === item.id ? "is-active" : ""} onClick={() => setViewMode(item.id)}>
                    {item.icon}<span>{item.label}</span>
                  </button>
                ))}
              </div>
              <button type="button" className="secondary-button schedule-template-button" disabled={isReadOnly || busy !== null} onClick={() => setShowTemplateDialog(true)}>
                <Copy size={14} />
                模板批量下发
                {templates.length > 0 && <span className="button-count">{templates.length}</span>}
              </button>
            </div>

            {/* Quick Tabs */}
            <div className="schedule-quick-tabs">
              {quickTabs.map((t) => {
                const isActive = tab === t.id;
                const isAlertTab = t.id === "ALERT" && t.count > 0;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`schedule-quick-tab ${isActive ? "is-active" : ""} ${isAlertTab ? "is-alert" : ""}`}
                    onClick={() => {
                      setTab(t.id);
                      setPage(1);
                    }}
                  >
                    <span>{t.label}</span>
                    <span className="quick-tab-badge">{t.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="schedule-filter-group">
            <div className="search">
              <Search size={15} />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="搜索 ASIN、SKU、站点或任务..."
              />
              {query && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => {
                    setQuery("");
                    setPage(1);
                  }}
                  title="清空搜索"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <CustomSelect
              value={selectedTaskType}
              onChange={(val) => {
                setSelectedTaskType(val);
                setPage(1);
              }}
              options={[
                { value: "ALL", label: "全部任务类型" },
                { value: "WEEKLY_OPTIMIZATION", label: "每周优化" },
                { value: "MONTHLY_OPTIMIZATION", label: "月度优化" },
                { value: "EXPERIMENT_REVIEW", label: "实验复盘" },
              ]}
              title="按任务类型筛选"
              aria-label="按任务类型筛选"
            />

            <CustomSelect
              value={selectedTaskMode}
              onChange={(val) => {
                setSelectedTaskMode(val);
                setPage(1);
              }}
              options={[
                { value: "ALL", label: "全部运行模式" },
                { value: "APPROVAL_REQUIRED", label: "需要审批", sublabel: "APPROVAL_REQUIRED" },
                { value: "AUTO_EXECUTE", label: "自动执行", sublabel: "AUTO_EXECUTE" },
                { value: "ANALYZE_ONLY", label: "仅分析", sublabel: "ANALYZE_ONLY" },
              ]}
              title="按运行模式筛选"
              aria-label="按运行模式筛选"
            />

            {completedReviewCount > 0 && (
              <label className="schedule-filter-checkbox" style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "var(--muted)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  checked={showCompletedReviews}
                  onChange={(e) => {
                    setShowCompletedReviews(e.target.checked);
                    setPage(1);
                  }}
                />
                <span>显示已完成复盘 ({completedReviewCount})</span>
              </label>
            )}

            {hasActiveFilters && (
              <button
                type="button"
                className="toolbar-text-btn reset-filters-btn"
                onClick={resetFilters}
                title="重置所有筛选条件"
              >
                <RotateCcw size={12} />
                重置筛选
              </button>
            )}

            <span className="filter-result-count">
              展示 <strong>{filteredAndSortedSchedules.length}</strong> / {effectiveSchedules.length}
            </span>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="schedule-batch-toolbar" role="toolbar" aria-label="批量操作">
            <div className="batch-selection-summary">
              <CheckCircle2 size={16} />
              <strong>已选 {selectedIds.size} 个任务</strong>
              {selectedIds.size < filteredAndSortedSchedules.length && (
                <button type="button" onClick={() => selectSchedules(filteredAndSortedSchedules.map((schedule) => schedule.schedule_id), true)}>
                  选择全部筛选结果（{filteredAndSortedSchedules.length}）
                </button>
              )}
              <button type="button" onClick={() => setSelectedIds(new Set())}>清空</button>
            </div>
            <div className="batch-action-buttons">
              <button type="button" className="secondary-button" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("ENABLE")}><Play size={14} />批量启用</button>
              <button type="button" className="secondary-button" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("DISABLE")}><Pause size={14} />批量暂停</button>
              <div className="batch-mode-control">
                <CustomSelect
                  value={batchMode}
                  onChange={(value) => setBatchMode(value as TaskMode)}
                  options={[
                    { value: "APPROVAL_REQUIRED", label: "需要审批" },
                    { value: "AUTO_EXECUTE", label: "自动执行" },
                    { value: "ANALYZE_ONLY", label: "仅分析" },
                  ]}
                  title="批量运行模式"
                  aria-label="批量运行模式"
                />
                <button type="button" className="secondary-button" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("SET_MODE", { task_mode: batchMode })}>应用模式</button>
              </div>
              <button type="button" className="secondary-button" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("SHIFT_TIME", { shift_minutes: -15 })}>−15 分钟</button>
              <button type="button" className="secondary-button" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("SHIFT_TIME", { shift_minutes: 15 })}>+15 分钟</button>
              <button type="button" className="secondary-button danger-outline" disabled={isReadOnly || busy !== null} onClick={() => void runBatch("DELETE")}><Trash2 size={14} />批量删除</button>
            </div>
          </div>
        )}

        {viewMode === "TABLE" && <DataTable
          empty={effectiveSchedules.length ? "没有符合筛选条件的定时任务" : "尚未创建定时任务"}
          emptyAction={
            hasActiveFilters ? (
              <button type="button" className="secondary-button empty-action-btn" onClick={resetFilters}>
                <RotateCcw size={14} />
                清空所有筛选条件
              </button>
            ) : undefined
          }
          rowClassName={(index) => {
            const item = paginatedSchedules[index];
            if (!item) return undefined;
            if (!item.enabled) return "row-paused";
            if ((item.consecutive_failures ?? 0) > 0) return "row-has-failure";
            return undefined;
          }}
          columns={[
            <input
              key="select"
              type="checkbox"
              aria-label="选择本页任务"
              checked={paginatedSchedules.length > 0 && paginatedSchedules.every((schedule) => selectedIds.has(schedule.schedule_id))}
              onChange={(event) => selectSchedules(paginatedSchedules.map((schedule) => schedule.schedule_id), event.target.checked)}
            />,
            renderSortHeader("ASIN", "child_asin"),
            renderSortHeader("SKU", "sku"),
            "站点",
            renderSortHeader("任务类型", "task_type"),
            "模式",
            "频率与时间",
            renderSortHeader("下次运行", "next_run_at"),
            renderSortHeader("状态", "consecutive_failures"),
            "操作",
          ]}
          rows={paginatedSchedules.map((schedule) => {
            const scope = scopeById.get(schedule.scope_id);
            const hasFailure = (schedule.consecutive_failures ?? 0) > 0;
            const isToday = isScheduleToday(schedule);

            return [
              <input
                key="select"
                type="checkbox"
                aria-label={`选择 ${scope?.child_asin ?? schedule.schedule_id} ${taskLabel(schedule.task_type)}`}
                checked={selectedIds.has(schedule.schedule_id)}
                onChange={() => toggleSelected(schedule.schedule_id)}
              />,
              <div key="asin" className="schedule-asin-cell">
                <button
                  type="button"
                  className="run-link-button schedule-scope-link"
                  onClick={() => scope && onScope(scope.scope_id)}
                  disabled={!scope}
                  title={scope ? `进入 ${scope.child_asin} 的 ASIN 运营详情` : "关联 ASIN 已不存在"}
                >
                  <strong>{scope?.child_asin ?? "未知 ASIN"}</strong>
                </button>
                {hasFailure && (
                  <span
                    className="schedule-failure-badge"
                    title={`该任务已连续失败 ${schedule.consecutive_failures} 次，建议进入 ASIN 检查`}
                  >
                    <AlertTriangle size={11} />
                    <span>连续失败 {schedule.consecutive_failures} 次</span>
                  </span>
                )}
              </div>,
              <span key="sku" className="schedule-sku-text mono" title={scope?.sku ?? schedule.scope_id}>
                {scope?.sku ?? schedule.scope_id}
              </span>,
              scope ? <MarketplaceFlag key="marketplace" marketplace={scope.marketplace} /> : "-",
              taskLabel(schedule.task_type),
              <TaskModeBadge key="mode" mode={schedule.task_mode} />,
              <span key="frequency" className="schedule-freq-time">
                {scheduleFrequencyLabel(schedule)} · {schedule.local_time}
              </span>,
              <div key="next_run" className="schedule-next-run-cell">
                {!schedule.enabled ? (
                  <span className="muted-text schedule-paused-tag">已暂停</span>
                ) : isToday ? (
                  <span className="schedule-today-tag" title="将在今日触发">
                    <span className="tag-pulse" />
                    今日 {schedule.local_time}
                  </span>
                ) : (
                  <span className="schedule-next-date">{dateTime(schedule.next_run_at)}</span>
                )}
              </div>,
              <div key="state" className="schedule-state-cell">
                {!schedule.enabled ? (
                  <Status value="PAUSED" />
                ) : hasFailure ? (
                  <span className="status status-failed" title={`连续失败 ${schedule.consecutive_failures} 次`}>
                    <AlertCircle size={12} />
                    异常待查
                  </span>
                ) : (
                  <Status value="ENABLED" />
                )}
              </div>,
              <div key="actions" className="schedule-actions">
                <button
                  className="icon-button"
                  title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "手动触发一次"}
                  aria-label={`手动触发 ${scope?.child_asin ?? "ASIN"} ${taskLabel(schedule.task_type)}`}
                  disabled={isReadOnly || busy !== null}
                  onClick={() => void runNow(schedule)}
                >
                  <CirclePlay />
                </button>
                <button
                  className="icon-button"
                  title={
                    isReadOnly
                      ? "当前处于只读保护，解除写入锁后可操作"
                      : schedule.enabled
                      ? "暂停任务"
                      : "启用任务"
                  }
                  aria-label={`${schedule.enabled ? "暂停" : "启用"} ${scope?.child_asin ?? "ASIN"} ${taskLabel(schedule.task_type)}`}
                  disabled={isReadOnly || busy !== null}
                  onClick={() => void toggle(schedule)}
                >
                  {schedule.enabled ? <Pause /> : <Play />}
                </button>
                <button
                  className="icon-button"
                  title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "修改任务"}
                  aria-label={`修改 ${scope?.child_asin ?? "ASIN"} ${taskLabel(schedule.task_type)}`}
                  disabled={isReadOnly || busy !== null || !scope}
                  onClick={() => setEditing(schedule)}
                >
                  <Pencil />
                </button>
                <button
                  className="icon-button delete-action"
                  title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "删除任务"}
                  aria-label={`删除 ${scope?.child_asin ?? "ASIN"} ${taskLabel(schedule.task_type)}`}
                  disabled={isReadOnly || busy !== null}
                  onClick={() => setDeleting(schedule)}
                >
                  <Trash2 />
                </button>
              </div>,
            ];
          })}
        />}

        {viewMode === "GROUPED" && (
          <ScheduleGroupedView
            groups={groupedSchedules}
            scopeById={scopeById}
            selectedIds={selectedIds}
            isReadOnly={isReadOnly}
            busy={busy}
            onSelect={selectSchedules}
            onToggleSelected={toggleSelected}
            onScope={onScope}
            onRun={runNow}
            onToggle={toggle}
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        )}

        {viewMode === "HEATMAP" && (
          <ScheduleHeatmap
            schedules={filteredAndSortedSchedules}
            scopeById={scopeById}
            selectedIds={selectedIds}
            onSelect={selectSchedules}
          />
        )}

        {viewMode === "TABLE" && filteredAndSortedSchedules.length > 0 && (
          <Pagination
            current={page}
            total={filteredAndSortedSchedules.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </section>

      {editing && scopeById.get(editing.scope_id) && (
        <ScheduleDialog
          scope={scopeById.get(editing.scope_id)!}
          schedule={editing}
          close={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
      {deleting && (
        <DeleteScheduleDialog
          schedule={deleting}
          busy={busy === `delete:${deleting.schedule_id}`}
          close={() => setDeleting(null)}
          confirm={() => void remove(deleting)}
        />
      )}
      {showTemplateDialog && (
        <ScheduleTemplateDialog
          scopes={scopes}
          templates={templates}
          initialScopeIds={selectedScopeIds}
          close={() => setShowTemplateDialog(false)}
          reloadTemplates={loadTemplates}
          onApplied={async (message) => {
            setNotice({ type: "success", text: message });
            setShowTemplateDialog(false);
            setSelectedIds(new Set());
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
    </>
  );
}

function scheduleFrequencyLabel(schedule: Schedule | { frequency: string; day_of_week?: number | null; day_of_month?: number | null; task_type?: string }): string {
  if (schedule.frequency === "EVERY_3_HOURS") return "每3小时";
  if (schedule.frequency === "MONTHLY") return `每月 (${schedule.day_of_month ?? 5}日)`;
  if (schedule.frequency === "WEEKLY") return `每周 (${dayOfWeekLabel(schedule.day_of_week)})`;
  if (schedule.frequency === "DAILY") return "每日";
  if (schedule.task_type === "EXPERIMENT_REVIEW") return "单次";
  return "手动";
}

function ScheduleGroupedView({
  groups,
  scopeById,
  selectedIds,
  isReadOnly,
  busy,
  onSelect,
  onToggleSelected,
  onScope,
  onRun,
  onToggle,
  onEdit,
  onDelete,
}: {
  groups: Array<[string, Schedule[]]>;
  scopeById: Map<string, Scope>;
  selectedIds: Set<string>;
  isReadOnly: boolean;
  busy: string | null;
  onSelect: (scheduleIds: string[], selected: boolean) => void;
  onToggleSelected: (scheduleId: string) => void;
  onScope: (scopeId: string) => void;
  onRun: (schedule: Schedule) => Promise<void>;
  onToggle: (schedule: Schedule) => Promise<void>;
  onEdit: (schedule: Schedule) => void;
  onDelete: (schedule: Schedule) => void;
}) {
  if (!groups.length) return <div className="empty-state schedule-view-empty">没有符合筛选条件的定时任务</div>;
  return (
    <div className="schedule-grouped-view">
      {groups.map(([scopeId, items]) => {
        const scope = scopeById.get(scopeId);
        const itemIds = items.map((schedule) => schedule.schedule_id);
        const allSelected = itemIds.every((scheduleId) => selectedIds.has(scheduleId));
        const failures = items.filter((schedule) => (schedule.consecutive_failures ?? 0) > 0).length;
        const enabled = items.filter((schedule) => schedule.enabled).length;
        return (
          <details className="schedule-scope-group" key={scopeId} open>
            <summary>
              <input
                type="checkbox"
                aria-label={`选择 ${scope?.child_asin ?? scopeId} 的全部任务`}
                checked={allSelected}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => onSelect(itemIds, event.target.checked)}
              />
              <span className="group-chevron"><ChevronRight size={15} /></span>
              <button type="button" className="run-link-button" onClick={(event) => { event.preventDefault(); onScope(scopeId); }}>
                <strong>{scope?.child_asin ?? "未知 ASIN"}</strong>
              </button>
              <span className="mono muted-text">{scope?.sku ?? scopeId}</span>
              {scope && <MarketplaceFlag marketplace={scope.marketplace} />}
              <span className="group-task-count">{items.length} 个任务 · {enabled} 个启用</span>
              {failures > 0 && <span className="schedule-failure-badge"><AlertTriangle size={11} />{failures} 个异常</span>}
            </summary>
            <div className="schedule-group-items">
              {items.map((schedule) => (
                <div className={`schedule-group-item ${schedule.enabled ? "" : "is-paused"}`} key={schedule.schedule_id}>
                  <input
                    type="checkbox"
                    aria-label={`选择 ${taskLabel(schedule.task_type)}`}
                    checked={selectedIds.has(schedule.schedule_id)}
                    onChange={() => onToggleSelected(schedule.schedule_id)}
                  />
                  <div className="group-item-primary"><strong>{taskLabel(schedule.task_type)}</strong><TaskModeBadge mode={schedule.task_mode} /></div>
                  <span>{scheduleFrequencyLabel(schedule)} · {schedule.local_time}</span>
                  <span className="mono muted-text">{schedule.enabled ? dateTime(schedule.next_run_at) : "已暂停"}</span>
                  <div>{schedule.enabled ? <Status value={(schedule.consecutive_failures ?? 0) > 0 ? "FAILED" : "ENABLED"} /> : <Status value="PAUSED" />}</div>
                  <div className="schedule-actions">
                    <button className="icon-button" title="手动触发一次" disabled={isReadOnly || busy !== null} onClick={() => void onRun(schedule)}><CirclePlay /></button>
                    <button className="icon-button" title={schedule.enabled ? "暂停任务" : "启用任务"} disabled={isReadOnly || busy !== null} onClick={() => void onToggle(schedule)}>{schedule.enabled ? <Pause /> : <Play />}</button>
                    <button className="icon-button" title="修改任务" disabled={isReadOnly || busy !== null || !scope} onClick={() => onEdit(schedule)}><Pencil /></button>
                    <button className="icon-button delete-action" title="删除任务" disabled={isReadOnly || busy !== null} onClick={() => onDelete(schedule)}><Trash2 /></button>
                  </div>
                </div>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

const HEATMAP_WEEKDAYS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

export function buildScheduleHeatmap(schedules: Schedule[]): Schedule[][][] {
  const buckets: Schedule[][][] = Array.from({ length: 24 }, () => Array.from({ length: 7 }, () => [] as Schedule[]));
  const weekdayIndex: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  for (const schedule of schedules) {
    if (!schedule.enabled) continue;
    const hour = Number(schedule.local_time.slice(0, 2));
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
    if (schedule.frequency === "DAILY") {
      for (let day = 0; day < 7; day += 1) buckets[hour][day].push(schedule);
    } else if (schedule.frequency === "WEEKLY" && schedule.day_of_week && schedule.day_of_week >= 1 && schedule.day_of_week <= 7) {
      buckets[hour][schedule.day_of_week - 1].push(schedule);
    } else if (schedule.next_run_at) {
      const day = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Shanghai", weekday: "short" }).format(new Date(schedule.next_run_at));
      const index = weekdayIndex[day];
      if (index != null) buckets[hour][index].push(schedule);
    }
  }
  return buckets;
}

function ScheduleHeatmap({
  schedules,
  scopeById,
  selectedIds,
  onSelect,
}: {
  schedules: Schedule[];
  scopeById: Map<string, Scope>;
  selectedIds: Set<string>;
  onSelect: (scheduleIds: string[], selected: boolean) => void;
}) {
  const buckets = useMemo(() => buildScheduleHeatmap(schedules), [schedules]);
  const max = Math.max(1, ...buckets.flatMap((row) => row.map((cell) => cell.length)));
  const enabledCount = schedules.filter((schedule) => schedule.enabled).length;
  return (
    <div className="schedule-heatmap-view">
      <div className="heatmap-summary">
        <div><strong>7 × 24 调度密度</strong><span>{enabledCount} 个启用任务；每日任务会计入每天对应时段</span></div>
        <div className="heatmap-legend"><span>低</span><i className="level-1" /><i className="level-2" /><i className="level-3" /><i className="level-4" /><span>高</span></div>
      </div>
      <div className="schedule-heatmap-scroll">
        <div className="schedule-heatmap-grid">
          <div className="heatmap-corner">上海时间</div>
          {HEATMAP_WEEKDAYS.map((day) => <div className="heatmap-day" key={day}>{day}</div>)}
          {buckets.map((row, hour) => (
            <Fragment key={hour}>
              <div className="heatmap-hour">{String(hour).padStart(2, "0")}:00</div>
              {row.map((items, day) => {
                const scheduleIds = items.map((schedule) => schedule.schedule_id);
                const allSelected = scheduleIds.length > 0 && scheduleIds.every((scheduleId) => selectedIds.has(scheduleId));
                const title = items.length
                  ? `${HEATMAP_WEEKDAYS[day]} ${String(hour).padStart(2, "0")}:00 · ${items.length} 个任务\n${items.slice(0, 8).map((schedule) => `${scopeById.get(schedule.scope_id)?.child_asin ?? schedule.scope_id} · ${taskLabel(schedule.task_type)} · ${schedule.local_time}`).join("\n")}`
                  : `${HEATMAP_WEEKDAYS[day]} ${String(hour).padStart(2, "0")}:00 · 无任务`;
                return (
                  <button
                    type="button"
                    key={`${hour}-${day}`}
                    className={`heatmap-cell ${items.length ? "has-tasks" : ""} ${allSelected ? "is-selected" : ""}`}
                    style={items.length ? { backgroundColor: `rgba(16, 185, 129, ${0.22 + (items.length / max) * 0.65})` } : undefined}
                    title={title}
                    disabled={!items.length}
                    onClick={() => onSelect(scheduleIds, !allSelected)}
                  >
                    {items.length > 0 && <strong>{items.length}</strong>}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
      <p className="heatmap-note"><Info size={13} />同一时段任务较多时，调度器会在原计划之后加入 0–300 秒稳定抖动，并按站点限制每轮入队数量。</p>
    </div>
  );
}

function ScheduleTemplateDialog({
  scopes,
  templates,
  initialScopeIds,
  close,
  reloadTemplates,
  onApplied,
  setNotice,
}: {
  scopes: Scope[];
  templates: ScheduleTemplate[];
  initialScopeIds: string[];
  close: () => void;
  reloadTemplates: () => Promise<ScheduleTemplate[]>;
  onApplied: (message: string) => Promise<void>;
  setNotice: NoticeSetter;
}) {
  const activeScopes = useMemo(() => scopes.filter((scope) => scope.status === "ACTIVE"), [scopes]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(templates[0]?.template_id ?? "");
  const [selectedScopeIds, setSelectedScopeIds] = useState<Set<string>>(() => new Set(initialScopeIds));
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", task_type: "WEEKLY_OPTIMIZATION", task_mode: "APPROVAL_REQUIRED", frequency: "WEEKLY", day_of_week: 1, day_of_month: 5, local_time: "09:00" });

  useEffect(() => {
    if (!selectedTemplateId && templates[0]) setSelectedTemplateId(templates[0].template_id);
  }, [selectedTemplateId, templates]);

  const createTemplate = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      const created = await api.post<ScheduleTemplate>("/api/v1/schedule-templates", form);
      await reloadTemplates();
      setSelectedTemplateId(created.template_id);
      setForm({ ...form, name: "" });
      setNotice({ type: "success", text: `模板“${created.name}”已创建` });
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template: ScheduleTemplate) => {
    if (!window.confirm(`确认删除模板“${template.name}”？已下发的任务不会受影响。`)) return;
    try {
      setSaving(true);
      await api.delete(`/api/v1/schedule-templates/${template.template_id}`);
      const next = await reloadTemplates();
      setSelectedTemplateId(next[0]?.template_id ?? "");
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async () => {
    if (!selectedTemplateId || !selectedScopeIds.size) return;
    try {
      setSaving(true);
      const result = await api.post<{ created_count: number; skipped_count: number }>(`/api/v1/schedule-templates/${selectedTemplateId}/apply`, { scope_ids: [...selectedScopeIds] });
      await onApplied(`模板已下发：新建 ${result.created_count} 个任务${result.skipped_count ? `，跳过 ${result.skipped_count} 个重复任务` : ""}`);
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title="任务模板与批量下发" close={saving ? () => undefined : close}>
      <div className="schedule-template-dialog-body">
        <form className="template-create-form" onSubmit={createTemplate}>
          <div className="template-section-heading"><div><strong>新建任务模板</strong><span>保存一次，后续可跨 ASIN 重复使用</span></div></div>
          <div className="template-form-grid">
            <Field label="模板名称"><input value={form.name} maxLength={80} required placeholder="例如：周一标准优化" onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
            <Field label="任务类型">
              <CustomSelect
                value={form.task_type}
                onChange={(value) => {
                  let nextTime = form.local_time;
                  if (value === "MONTHLY_OPTIMIZATION" && (form.local_time === "09:00" || !form.local_time)) {
                    nextTime = "15:00";
                  } else if (value === "WEEKLY_OPTIMIZATION" && form.local_time === "15:00") {
                    nextTime = "09:00";
                  }
                  setForm({
                    ...form,
                    task_type: value,
                    task_mode: value === "EXPERIMENT_REVIEW" ? "APPROVAL_REQUIRED" : form.task_mode,
                    frequency: value === "MONTHLY_OPTIMIZATION" ? "MONTHLY" : form.frequency,
                    local_time: nextTime,
                  });
                }}
                options={[
                  { value: "WEEKLY_OPTIMIZATION", label: "每周优化" },
                  { value: "MONTHLY_OPTIMIZATION", label: "月度优化" },
                  { value: "EXPERIMENT_REVIEW", label: "实验复盘" },
                  { value: "DAILY_SENTINEL", label: "每日安全巡检轨" },
                  { value: "ADAPTIVE_EVALUATION", label: "多维自适应调优轨" },
                  { value: "MILESTONE_TRANSITION", label: "阶段展期与跃迁终审轨" },
                ]}
              />
            </Field>
            <Field label="运行模式"><CustomSelect value={form.task_mode} onChange={(value) => setForm({ ...form, task_mode: value })} options={form.task_type === "EXPERIMENT_REVIEW" ? [{ value: "APPROVAL_REQUIRED", label: "自动复盘 · 回滚需审批" }] : [{ value: "APPROVAL_REQUIRED", label: "需要审批" }, { value: "AUTO_EXECUTE", label: "自动执行" }, { value: "ANALYZE_ONLY", label: "仅分析" }]} /></Field>
            <Field label="执行周期">
              <CustomSelect
                value={form.frequency}
                onChange={(value) => {
                  let nextTime = form.local_time;
                  if (value === "MONTHLY" && form.local_time === "09:00") {
                    nextTime = "15:00";
                  } else if (value === "WEEKLY" && form.local_time === "15:00") {
                    nextTime = "09:00";
                  }
                  setForm({ ...form, frequency: value, local_time: nextTime });
                }}
                options={[{ value: "WEEKLY", label: "每周" }, { value: "MONTHLY", label: "每月" }, { value: "DAILY", label: "每日" }, { value: "MANUAL", label: "仅手动" }]}
              />
            </Field>
            {form.frequency === "WEEKLY" && <Field label="星期"><CustomSelect value={form.day_of_week} onChange={(value) => setForm({ ...form, day_of_week: Number(value) })} options={HEATMAP_WEEKDAYS.map((label, index) => ({ value: index + 1, label }))} /></Field>}
            {form.frequency === "MONTHLY" && <Field label="月份日期"><CustomSelect value={form.day_of_month ?? 5} onChange={(value) => setForm({ ...form, day_of_month: Number(value) })} options={Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: `${i + 1}日` }))} /></Field>}
            <Field label="执行时间 · 上海">
              <CustomTimePicker
                value={form.local_time}
                onChange={(val) => setForm({ ...form, local_time: val })}
              />
            </Field>
          </div>
          <div className="template-create-actions"><button type="submit" className="secondary-button" disabled={saving || !form.name.trim()}><Plus size={14} />保存模板</button></div>
        </form>

        <section className="template-apply-section">
          <div className="template-section-heading"><div><strong>选择模板与目标 ASIN</strong><span>完全相同的任务会自动跳过，避免重复创建</span></div><span>{selectedScopeIds.size} / {activeScopes.length} 已选</span></div>
          {templates.length ? (
            <div className="template-picker-row">
              <CustomSelect
                value={selectedTemplateId || templates[0].template_id}
                onChange={setSelectedTemplateId}
                options={templates.map((template) => ({ value: template.template_id, label: template.name, sublabel: `${taskLabel(template.task_type)} · ${scheduleFrequencyLabel(template)} ${template.local_time}` }))}
                aria-label="选择任务模板"
              />
              {templates.map((template) => template.template_id === selectedTemplateId ? <button type="button" key={template.template_id} className="icon-button delete-action" title="删除当前模板" disabled={saving} onClick={() => void deleteTemplate(template)}><Trash2 size={15} /></button> : null)}
            </div>
          ) : <div className="template-empty"><Copy size={18} /><span>还没有模板，请先在上方创建</span></div>}
          <div className="scope-select-actions"><button type="button" onClick={() => setSelectedScopeIds(new Set(activeScopes.map((scope) => scope.scope_id)))}>全选全部 ASIN</button><button type="button" onClick={() => setSelectedScopeIds(new Set())}>清空</button></div>
          <div className="template-scope-grid">
            {activeScopes.map((scope) => (
              <label key={scope.scope_id} className={selectedScopeIds.has(scope.scope_id) ? "is-selected" : ""}>
                <input type="checkbox" checked={selectedScopeIds.has(scope.scope_id)} onChange={(event) => {
                  setSelectedScopeIds((current) => {
                    const next = new Set(current);
                    event.target.checked ? next.add(scope.scope_id) : next.delete(scope.scope_id);
                    return next;
                  });
                }} />
                <MarketplaceFlag marketplace={scope.marketplace} />
                <span><strong>{scope.child_asin}</strong><small>{scope.sku}</small></span>
              </label>
            ))}
          </div>
          <div className="dialog-actions template-dialog-actions"><button type="button" className="secondary-button" disabled={saving} onClick={close}>取消</button><button type="button" className="primary-button" disabled={saving || !selectedTemplateId || !selectedScopeIds.size} onClick={() => void applyTemplate()}>{saving ? "处理中..." : `下发到 ${selectedScopeIds.size} 个 ASIN`}</button></div>
        </section>
      </div>
    </Dialog>
  );
}

export function isExecutedReviewSchedule(schedule: Schedule): boolean {
  return schedule.task_type === "EXPERIMENT_REVIEW" && (!schedule.enabled || !schedule.next_run_at);
}

export function SchedulesEditor({
  scope,
  schedules,
  onChanged,
  setNotice,
  isReadOnly = false,
  includeAutonomous = false,
  autonomousOnly = false,
}: {
  scope: Scope;
  schedules: Schedule[];
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  isReadOnly?: boolean;
  includeAutonomous?: boolean;
  autonomousOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [deleting, setDeleting] = useState<Schedule | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCompletedReviews, setShowCompletedReviews] = useState(false);

  const displayedSchedules = useMemo(() => {
    if (autonomousOnly) {
      return schedules.filter((s) => AUTONOMOUS_TASK_TYPES.has(s.task_type || ""));
    }
    return includeAutonomous
      ? schedules
      : schedules.filter((s) => !AUTONOMOUS_TASK_TYPES.has(s.task_type || ""));
  }, [schedules, includeAutonomous, autonomousOnly]);

  const completedReviewCount = useMemo(
    () => displayedSchedules.filter(isExecutedReviewSchedule).length,
    [displayedSchedules]
  );

  const visibleSchedules = useMemo(() => {
    if (showCompletedReviews) return displayedSchedules;
    return displayedSchedules.filter((s) => !isExecutedReviewSchedule(s));
  }, [displayedSchedules, showCompletedReviews]);

  const toggle = async (schedule: Schedule) => {
    try {
      setBusy(`toggle:${schedule.schedule_id}`);
      await api.post(`/api/v1/schedules/${schedule.schedule_id}/${schedule.enabled ? "disable" : "enable"}`);
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const runNow = async (schedule: Schedule) => {
    try {
      setBusy(`run:${schedule.schedule_id}`);
      await api.post(`/api/v1/schedules/${schedule.schedule_id}/run-now`);
      setNotice({ type: "success", text: `${taskModeLabel(schedule.task_mode)}任务已手动触发并进入队列` });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (schedule: Schedule) => {
    try {
      setBusy(`delete:${schedule.schedule_id}`);
      const result = await api.delete<{ preserved_runs: number }>(`/api/v1/schedules/${schedule.schedule_id}`);
      setDeleting(null);
      setNotice({
        type: "success",
        text: result.preserved_runs
          ? `任务已安全删除，${result.preserved_runs} 条历史 Runs 与审计已保留`
          : "任务已删除",
      });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="panel table-panel schedule-table-panel">
      <div className="table-toolbar">
        <SectionTitle title="定时执行任务" />
        <div className="table-toolbar-actions" style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          {completedReviewCount > 0 && (
            <label className="schedule-filter-checkbox" style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "var(--muted)", cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={showCompletedReviews}
                onChange={(e) => setShowCompletedReviews(e.target.checked)}
              />
              <span>显示已完成实验复盘 ({completedReviewCount})</span>
            </label>
          )}
          <button
            className="primary-button"
            onClick={() => setOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? "当前处于只读保护，解除写入锁后可新建任务" : "新建定时任务"}
          >
            <Plus />
            新建定时任务
          </button>
        </div>
      </div>
      <DataTable
        empty={displayedSchedules.length > 0 && visibleSchedules.length === 0 ? "所有单次实验复盘任务已执行完毕并默认隐藏" : "尚未为当前 ASIN 创建定时任务"}
        rowClassName={(index) => (visibleSchedules[index]?.enabled ? undefined : "row-paused")}
        columns={["任务类型", "运行模式", "频率", "执行时间", "时区", "下次运行", "状态", "操作"]}
        rows={visibleSchedules.map((schedule) => [
          taskLabel(schedule.task_type),
          <TaskModeBadge key="mode" mode={schedule.task_mode} />,
          scheduleFrequencyLabel(schedule),
          schedule.local_time,
          schedule.timezone === "Asia/Shanghai" ? "上海时间" : schedule.timezone,
          dateTime(schedule.next_run_at),
          schedule.enabled ? <Status key="s" value="ENABLED" /> : <Status key="s" value="PAUSED" />,
          <div key="actions" className="schedule-actions">
            <button
              className="icon-button"
              title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "手动触发一次"}
              aria-label={`手动触发 ${taskLabel(schedule.task_type)}`}
              disabled={isReadOnly || busy !== null}
              onClick={() => void runNow(schedule)}
            >
              <CirclePlay />
            </button>
            <button
              className="icon-button"
              title={
                isReadOnly
                  ? "当前处于只读保护，解除写入锁后可操作"
                  : schedule.enabled
                  ? "暂停任务"
                  : "启用任务"
              }
              aria-label={`${schedule.enabled ? "暂停" : "启用"} ${taskLabel(schedule.task_type)}`}
              disabled={isReadOnly || busy !== null}
              onClick={() => void toggle(schedule)}
            >
              {schedule.enabled ? <Pause /> : <Play />}
            </button>
            <button
              className="icon-button"
              title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "修改任务"}
              aria-label={`修改 ${taskLabel(schedule.task_type)}`}
              disabled={isReadOnly || busy !== null}
              onClick={() => setEditing(schedule)}
            >
              <Pencil />
            </button>
            <button
              className="icon-button delete-action"
              title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "删除任务"}
              aria-label={`删除 ${taskLabel(schedule.task_type)}`}
              disabled={isReadOnly || busy !== null}
              onClick={() => setDeleting(schedule)}
            >
              <Trash2 />
            </button>
          </div>,
        ])}
      />
      {open && (
        <ScheduleDialog
          scope={scope}
          close={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
      {editing && (
        <ScheduleDialog
          scope={scope}
          schedule={editing}
          close={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
          setNotice={setNotice}
        />
      )}
      {deleting && (
        <DeleteScheduleDialog
          schedule={deleting}
          busy={busy === `delete:${deleting.schedule_id}`}
          close={() => setDeleting(null)}
          confirm={() => void remove(deleting)}
        />
      )}
    </section>
  );
}

function DeleteScheduleDialog({
  schedule,
  busy,
  close,
  confirm,
}: {
  schedule: Schedule;
  busy: boolean;
  close: () => void;
  confirm: () => void;
}) {
  return (
    <Dialog title="确认删除任务" close={busy ? () => undefined : close}>
      <div className="delete-confirmation">
        <div className="delete-warning">
          <AlertTriangle />
          <div>
            <strong>确认删除此定时任务？</strong>
            <p>删除后将停止后续周期触发；历史 Runs、AI 建议与执行审计记录仍将完整保留。</p>
          </div>
        </div>
        <dl>
          <div>
            <dt>任务类型</dt>
            <dd>{taskLabel(schedule.task_type)}</dd>
          </div>
          <div>
            <dt>运行模式</dt>
            <dd>{taskModeLabel(schedule.task_mode)}</dd>
          </div>
          <div>
            <dt>执行周期</dt>
            <dd>{schedule.frequency === "MANUAL" ? (schedule.task_type === "EXPERIMENT_REVIEW" ? `单次 · ${dateTime(schedule.next_run_at)}` : "手动") : `${scheduleFrequencyLabel(schedule)} · ${schedule.local_time}`}</dd>
          </div>
        </dl>
        <div className="dialog-actions">
          <button className="secondary-button" onClick={close} disabled={busy}>
            取消
          </button>
          <button className="danger-button" onClick={confirm} disabled={busy}>
            <Trash2 />
            {busy ? "正在删除..." : "确认删除"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function DeleteScopeDialog({
  scope,
  runCount,
  scheduleCount,
  busy,
  close,
  confirm,
}: {
  scope: Scope;
  runCount: number;
  scheduleCount: number;
  busy: boolean;
  close: () => void;
  confirm: () => void;
}) {
  return (
    <Dialog title="确认删除 ASIN" close={busy ? () => undefined : close}>
      <div className="delete-confirmation">
        <div className="delete-warning">
          <AlertTriangle />
          <div>
            <strong>永久删除 {scope.child_asin}？</strong>
            <p>此操作不可恢复，将删除该 Scope 的经济模型、定时任务、Runs、AI 结果、执行记录、实验与本地凭证文件；系统审计事件仍会保留。</p>
          </div>
        </div>
        <dl>
          <div>
            <dt>ASIN / SKU</dt>
            <dd>{scope.child_asin} / {scope.sku}</dd>
          </div>
          <div>
            <dt>站点</dt>
            <dd>{scope.marketplace}</dd>
          </div>
          <div>
            <dt>关联数据</dt>
            <dd>{runCount} 条 Run · {scheduleCount} 个任务</dd>
          </div>
        </dl>
        <div className="dialog-actions">
          <button className="secondary-button" onClick={close} disabled={busy}>
            取消
          </button>
          <button className="danger-button" onClick={confirm} disabled={busy}>
            <Trash2 />
            {busy ? "正在删除..." : "确认永久删除"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export function Pagination({
  current,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
}: {
  current: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return (
    <div className="pagination-bar">
      <div className="pagination-info">
        <span>
          显示第 <strong>{start}</strong> - <strong>{end}</strong> 条，共 <strong>{total}</strong> 条记录
        </span>
        {onPageSizeChange && (
          <div className="pagination-size-select">
            <span>每页</span>
            <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} 条
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="pagination-controls">
        <button
          type="button"
          className="icon-button pagination-btn"
          disabled={current <= 1}
          onClick={() => onPageChange(current - 1)}
          title="上一页"
          aria-label="上一页"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="pagination-current">
          第 <strong>{current}</strong> / {totalPages} 页
        </span>
        <button
          type="button"
          className="icon-button pagination-btn"
          disabled={current >= totalPages}
          onClick={() => onPageChange(current + 1)}
          title="下一页"
          aria-label="下一页"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

export function PruneConfirmDialog({
  target,
  busy,
  close,
  confirm,
}: {
  target: "FAILED_AND_BLOCKED" | "OLD_NO_CHANGE";
  busy: boolean;
  close: () => void;
  confirm: () => void;
}) {
  const isFailed = target === "FAILED_AND_BLOCKED";
  return (
    <Dialog title={isFailed ? "清理历史失败与阻断 Runs" : "清理 30 天前无变动 Runs"} close={busy ? () => undefined : close}>
      <div className="delete-confirmation">
        <div className="delete-warning">
          <AlertTriangle />
          <div>
            <strong>确认执行数据清理？</strong>
            <p>
              {isFailed
                ? "将清理所有状态为 FAILED、BLOCKED、CANCELLED、REJECTED 的调试与异常 Run 记录及其关联快照与 Artifacts。"
                : "将清理 30 天前状态为 NO_CHANGE、ANALYZED 的无广告变动分析记录及其关联快照。"}
            </p>
            <p style={{ marginTop: "6px", color: "var(--teal-deep, #0f766e)", fontWeight: 500 }}>
              🛡️ 核心安全保证：所有已执行写操作的审计记录（state=EXECUTED 及审批批次）将永久完整保留。
            </p>
          </div>
        </div>
        <div className="dialog-actions">
          <button type="button" className="secondary-button" onClick={close} disabled={busy}>
            取消
          </button>
          <button type="button" className="danger-button" onClick={confirm} disabled={busy}>
            {busy ? <RefreshCw className="spin" size={14} /> : <Trash2 size={14} />}
            {busy ? "正在清理..." : "确认清理"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export type RunStateTab = "all" | "approval" | "running" | "completed" | "issues";

export function RunsView({
  runs,
  scopes = [],
  onSelect,
  system,
  onRefresh,
  isRefreshing,
  onToggleMenu,
  setNotice,
}: {
  runs: Run[];
  scopes?: Scope[];
  onSelect: (id: string) => void;
  system: SystemInfo | null;
  onRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
  setNotice?: NoticeSetter;
}) {
  const [tab, setTab] = useState<RunStateTab>("all");
  const [query, setQuery] = useState("");
  const [selectedMarketplace, setSelectedMarketplace] = useState("all");
  const [selectedAsin, setSelectedAsin] = useState("all");
  const [selectedTaskType, setSelectedTaskType] = useState("all");
  const [selectedTrigger, setSelectedTrigger] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [confirmPrune, setConfirmPrune] = useState<"FAILED_AND_BLOCKED" | "OLD_NO_CHANGE" | null>(null);
  const [pruningBusy, setPruningBusy] = useState(false);

  const isReadOnly = system?.write_kill_switch ?? true;
  const runsRef = useRef<HTMLDivElement>(null);
  useStaggerEntrance(runsRef, ".runs-metric-card, .table-panel", [tab, page]);

  const handlePrune = async (target: "FAILED_AND_BLOCKED" | "OLD_NO_CHANGE") => {
    try {
      setPruningBusy(true);
      const res = await api.post<{ pruned_count: number; removed: { runs: number; artifacts: number } }>(
        "/api/v1/runs/prune",
        {
          target,
          retention_days: target === "OLD_NO_CHANGE" ? 30 : undefined,
        }
      );
      setConfirmPrune(null);
      setNotice?.({
        type: "success",
        text: res.pruned_count > 0
          ? `已成功清理 ${res.pruned_count} 条 Runs 记录及 ${res.removed.artifacts} 个关联 Artifacts`
          : "暂无可清理的匹配记录",
      });
      if (onRefresh) await onRefresh();
    } catch (err) {
      if (setNotice) showError(err, setNotice);
    } finally {
      setPruningBusy(false);
    }
  };

  const effectiveRuns = useMemo(
    () => runs.filter((r) => !AUTONOMOUS_TASK_TYPES.has(r.task_type || "")),
    [runs]
  );

  const metrics = useMemo(() => {
    const today = new Date().toDateString();
    let todayCount = 0;
    let pendingApprovalCount = 0;
    let executedCount = 0;
    let issuesCount = 0;
    let runningCount = 0;
    let recentAllCount = 0;
    const now = Date.now();
    const fourteenDaysMs = 14 * 86400000;

    for (const r of effectiveRuns) {
      if (now - new Date(r.created_at).getTime() <= fourteenDaysMs) recentAllCount += 1;
      if (new Date(r.created_at).toDateString() === today) todayCount += 1;
      if (r.state === "APPROVAL_REQUIRED") pendingApprovalCount += 1;
      if (r.state === "EXECUTED") executedCount += 1;
      if (["FAILED", "BLOCKED", "CANCELLED", "REJECTED"].includes(r.state)) issuesCount += 1;
      if (["QUEUED", "CLAIMED", "PULLING", "PULL_PARTIAL", "PULL_COMPLETE", "ANALYZING", "OPTIMIZING", "PLAN_READY"].includes(r.state)) runningCount += 1;
    }

    return { todayCount, pendingApprovalCount, executedCount, issuesCount, runningCount, recentAllCount };
  }, [effectiveRuns]);

  const skuMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of scopes ?? []) {
      if (s.scope_id && s.sku) map.set(s.scope_id, s.sku);
      if (s.child_asin && s.sku) map.set(s.child_asin, s.sku);
    }
    return map;
  }, [scopes]);

  const marketplaceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of scopes ?? []) {
      if (s.marketplace) set.add(s.marketplace.toUpperCase());
    }
    for (const r of effectiveRuns) {
      if (r.marketplace) set.add(r.marketplace.toUpperCase());
    }
    return Array.from(set).sort();
  }, [scopes, effectiveRuns]);

  const asinOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of scopes ?? []) {
      if (s.child_asin) set.add(s.child_asin);
    }
    for (const r of effectiveRuns) {
      if (r.child_asin) set.add(r.child_asin);
    }
    return Array.from(set).sort();
  }, [scopes, effectiveRuns]);

  const filteredRuns = useMemo(() => {
    const now = Date.now();
    const fourteenDaysMs = 14 * 86400000;
    return effectiveRuns.filter((run) => {
      if (tab === "all" && now - new Date(run.created_at).getTime() > fourteenDaysMs) return false;
      if (tab === "approval" && run.state !== "APPROVAL_REQUIRED") return false;
      if (tab === "running" && !["QUEUED", "CLAIMED", "PULLING", "PULL_PARTIAL", "PULL_COMPLETE", "ANALYZING", "OPTIMIZING", "PLAN_READY"].includes(run.state)) return false;
      if (tab === "completed" && !["EXECUTED", "ANALYZED", "NO_CHANGE"].includes(run.state)) return false;
      if (tab === "issues" && !["FAILED", "BLOCKED", "CANCELLED", "REJECTED"].includes(run.state)) return false;

      if (selectedMarketplace !== "all") {
        const m = (run.marketplace || "").toUpperCase();
        if (m !== selectedMarketplace.toUpperCase()) return false;
      }
      if (selectedAsin !== "all" && run.child_asin !== selectedAsin) return false;
      if (selectedTaskType !== "all" && run.task_type !== selectedTaskType) return false;
      if (selectedTrigger !== "all" && run.trigger_source !== selectedTrigger) return false;

      if (dateFilter === "today") {
        if (new Date(run.created_at).toDateString() !== new Date().toDateString()) return false;
      } else if (dateFilter === "7d") {
        if (Date.now() - new Date(run.created_at).getTime() > 7 * 86400000) return false;
      } else if (dateFilter === "30d") {
        if (Date.now() - new Date(run.created_at).getTime() > 30 * 86400000) return false;
      }

      if (query.trim()) {
        const q = query.toLowerCase().trim();
        const sku = (run.sku || skuMap.get(run.scope_id) || skuMap.get(run.child_asin) || "").toLowerCase();
        const match =
          run.run_id.toLowerCase().includes(q) ||
          run.child_asin.toLowerCase().includes(q) ||
          sku.includes(q) ||
          (run.marketplace && run.marketplace.toLowerCase().includes(q)) ||
          (run.display_name && run.display_name.toLowerCase().includes(q)) ||
          run.task_type.toLowerCase().includes(q) ||
          run.state.toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [effectiveRuns, tab, selectedMarketplace, selectedAsin, selectedTaskType, selectedTrigger, dateFilter, query, skuMap]);

  const totalFiltered = filteredRuns.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRuns.slice(start, start + pageSize);
  }, [filteredRuns, currentPage, pageSize]);

  const hasActiveFilters =
    query ||
    selectedMarketplace !== "all" ||
    selectedAsin !== "all" ||
    selectedTaskType !== "all" ||
    selectedTrigger !== "all" ||
    dateFilter !== "all" ||
    tab !== "all";

  const resetFilters = () => {
    setTab("all");
    setQuery("");
    setSelectedMarketplace("all");
    setSelectedAsin("all");
    setSelectedTaskType("all");
    setSelectedTrigger("all");
    setDateFilter("all");
    setPage(1);
  };

  const handleRetry = async (run: Run, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setRetrying(run.run_id);
      await api.post(`/api/v1/runs/${run.run_id}/retry-step`);
      setNotice?.({ type: "success", text: `Run ${shortId(run.run_id)} 已发起重试并进入执行队列` });
      if (onRefresh) await onRefresh();
    } catch (err) {
      if (setNotice) showError(err, setNotice);
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div ref={runsRef} className="runs-view-container">
      <PageHeading
        title="Runs 流水线"
        isReadOnly={isReadOnly}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
        action={
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setConfirmPrune("FAILED_AND_BLOCKED")}
              disabled={isReadOnly || pruningBusy || metrics.issuesCount === 0}
              title={
                isReadOnly
                  ? "只读保护状态"
                  : metrics.issuesCount === 0
                  ? "暂无异常或调试 Runs 可清理"
                  : `一键清理当前 ${metrics.issuesCount} 条异常/调试 Runs`
              }
            >
              <Trash2 size={14} />
              <span>清理异常记录 {metrics.issuesCount > 0 ? `(${metrics.issuesCount})` : ""}</span>
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setConfirmPrune("OLD_NO_CHANGE")}
              disabled={isReadOnly || pruningBusy}
              title={isReadOnly ? "只读保护状态" : "清理 30 天前无变动的分析与拉取记录 (NO_CHANGE / ANALYZED)"}
            >
              <History size={14} />
              <span>清理 30 天前无变动 Runs</span>
            </button>
          </div>
        }
      />

      <div className="runs-metrics-grid">
        <div
          className={`runs-metric-card ${tab === "all" ? "active" : ""}`}
          onClick={() => { setTab("all"); setPage(1); }}
          title="点击查看全部运行记录"
        >
          <div className="runs-metric-header">
            <span>今日运行总数</span>
            <Activity size={16} />
          </div>
          <div className="runs-metric-value">{metrics.todayCount}</div>
          <div className="runs-metric-sub">历史累计 {runs.length} 次运行</div>
        </div>

        <div
          className={`runs-metric-card warning ${tab === "approval" ? "active" : ""}`}
          onClick={() => { setTab("approval"); setPage(1); }}
          title="点击查看待人工审批任务"
        >
          <div className="runs-metric-header">
            <span>待人工审批</span>
            <ShieldAlert size={16} />
          </div>
          <div className="runs-metric-value">{metrics.pendingApprovalCount}</div>
          <div className="runs-metric-sub">需运营或审批人确认执行</div>
        </div>

        <div
          className={`runs-metric-card success ${tab === "completed" ? "active" : ""}`}
          onClick={() => { setTab("completed"); setPage(1); }}
          title="点击查看已执行完成任务"
        >
          <div className="runs-metric-header">
            <span>已成功执行</span>
            <CheckCircle2 size={16} />
          </div>
          <div className="runs-metric-value">{metrics.executedCount}</div>
          <div className="runs-metric-sub">实际下发广告调整与写回</div>
        </div>

        <div
          className={`runs-metric-card danger ${tab === "issues" ? "active" : ""}`}
          onClick={() => { setTab("issues"); setPage(1); }}
          title="点击查看异常阻断任务"
        >
          <div className="runs-metric-header">
            <span>异常 / 阻断告警</span>
            <AlertTriangle size={16} />
          </div>
          <div className="runs-metric-value">{metrics.issuesCount}</div>
          <div className="runs-metric-sub">包含失败、阻断与已拒绝记录</div>
        </div>
      </div>

      <section className="panel table-panel">
        {tab === "issues" && metrics.issuesCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: "var(--red-soft)",
              borderBottom: "1px solid var(--red-border)",
              color: "#fb7185",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AlertTriangle size={15} />
              <span>当前共有 <strong style={{ color: "var(--ink-heading)" }}>{metrics.issuesCount}</strong> 条异常/阻断/调试记录，可一键安全清理以释放空间。</span>
            </div>
            <button
              type="button"
              className="danger-button"
              style={{ padding: "4px 10px", fontSize: "12px", height: "28px" }}
              onClick={() => setConfirmPrune("FAILED_AND_BLOCKED")}
              disabled={isReadOnly || pruningBusy}
            >
              <Trash2 size={13} />
              一键清理异常 Runs
            </button>
          </div>
        )}
        {tab === "completed" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: "var(--teal-soft)",
              borderBottom: "1px solid var(--teal-border)",
              color: "var(--teal)",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <History size={15} />
              <span style={{ color: "var(--ink)" }}>可清理 30 天前无策略变动的历史分析与拉取记录（NO_CHANGE / ANALYZED），减少历史数据堆积。</span>
            </div>
            <button
              type="button"
              className="secondary-button"
              style={{ padding: "4px 10px", fontSize: "12px", height: "28px" }}
              onClick={() => setConfirmPrune("OLD_NO_CHANGE")}
              disabled={isReadOnly || pruningBusy}
            >
              <History size={13} />
              清理 30 天前无变动 Runs
            </button>
          </div>
        )}
        <div className="runs-tabs-container">
          <div className="runs-tabs-list" role="tablist">
            <button
              type="button"
              className={`runs-tab-btn ${tab === "all" ? "active" : ""}`}
              onClick={() => { setTab("all"); setPage(1); }}
            >
              <span>全部</span>
              <span className="runs-tab-count">{metrics.recentAllCount}</span>
            </button>
            <button
              type="button"
              className={`runs-tab-btn ${tab === "approval" ? "active" : ""}`}
              onClick={() => { setTab("approval"); setPage(1); }}
            >
              <span>🔥 待审批</span>
              <span className={`runs-tab-count ${metrics.pendingApprovalCount > 0 ? "alert" : ""}`}>
                {metrics.pendingApprovalCount}
              </span>
            </button>
            <button
              type="button"
              className={`runs-tab-btn ${tab === "running" ? "active" : ""}`}
              onClick={() => { setTab("running"); setPage(1); }}
            >
              <span>⏳ 进行中</span>
              <span className="runs-tab-count">{metrics.runningCount}</span>
            </button>
            <button
              type="button"
              className={`runs-tab-btn ${tab === "completed" ? "active" : ""}`}
              onClick={() => { setTab("completed"); setPage(1); }}
            >
              <span>✅ 已完成</span>
              <span className="runs-tab-count">{effectiveRuns.filter((r) => ["EXECUTED", "ANALYZED", "NO_CHANGE"].includes(r.state)).length}</span>
            </button>
            <button
              type="button"
              className={`runs-tab-btn ${tab === "issues" ? "active" : ""}`}
              onClick={() => { setTab("issues"); setPage(1); }}
            >
              <span>⚠️ 异常/阻断</span>
              <span className="runs-tab-count">{metrics.issuesCount}</span>
            </button>
          </div>
        </div>

        <div className="runs-filter-toolbar">
          <div className="search">
            <Search />
            <input
              placeholder="搜索 Run ID、ASIN、SKU、状态..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            />
          </div>

          <CustomSelect
            value={selectedMarketplace}
            onChange={(val) => { setSelectedMarketplace(val); setPage(1); }}
            options={[
              { value: "all", label: `全部站点 (${marketplaceOptions.length})` },
              ...marketplaceOptions.map((m) => ({ value: m, label: m })),
            ]}
            title="按站点筛选"
            aria-label="按站点筛选"
          />

          <CustomSelect
            value={selectedAsin}
            onChange={(val) => { setSelectedAsin(val); setPage(1); }}
            options={[
              { value: "all", label: `全部 ASIN (${asinOptions.length})` },
              ...asinOptions.map((asin) => ({ value: asin, label: asin })),
            ]}
            title="按 ASIN 筛选"
            aria-label="按 ASIN 筛选"
          />

          <CustomSelect
            value={selectedTaskType}
            onChange={(val) => { setSelectedTaskType(val); setPage(1); }}
            options={[
              { value: "all", label: "全部任务类型" },
              { value: "WEEKLY_OPTIMIZATION", label: "每周优化" },
              { value: "MONTHLY_OPTIMIZATION", label: "月度优化" },
              { value: "EXPERIMENT_REVIEW", label: "实验复盘" },
            ]}
            title="按任务类型筛选"
            aria-label="按任务类型筛选"
          />

          <CustomSelect
            value={selectedTrigger}
            onChange={(val) => { setSelectedTrigger(val); setPage(1); }}
            options={[
              { value: "all", label: "全部触发来源" },
              { value: "MANUAL", label: "手动触发" },
              { value: "SCHEDULE", label: "定时触发" },
            ]}
            title="按触发来源筛选"
            aria-label="按触发来源筛选"
          />

          <CustomSelect
            value={dateFilter}
            onChange={(val) => { setDateFilter(val); setPage(1); }}
            options={[
              { value: "all", label: "全部时间" },
              { value: "today", label: "今天" },
              { value: "7d", label: "最近 7 天" },
              { value: "30d", label: "最近 30 天" },
            ]}
            title="按时间范围筛选"
            aria-label="按时间范围筛选"
          />

          {hasActiveFilters && (
            <button type="button" className="runs-filter-reset" onClick={resetFilters}>
              <RotateCcw size={12} />
              重置筛选
            </button>
          )}
        </div>

        <DataTable
          empty={hasActiveFilters ? "没有匹配的 Run 记录，请尝试调整筛选条件" : "暂无 Run 记录"}
          columns={[
            "Run ID",
            "Scope",
            "SKU",
            "任务类型",
            "运行模式",
            "触发来源",
            "质量状态",
            "运行状态",
            "创建时间",
            "操作",
          ]}
          rows={pageItems.map((run) => {
            const sku = run.sku || skuMap.get(run.scope_id) || skuMap.get(run.child_asin) || "-";
            return [
              <span key="id" className="mono">
                {shortId(run.run_id)}
              </span>,
              <strong key="asin">{run.child_asin}</strong>,
              <span key="sku" className="mono" title={sku}>
                {sku}
              </span>,
              taskLabel(run.task_type),
              <TaskModeBadge key="mode" mode={run.task_mode} />,
              run.trigger_source,
              <QualityGateBadge key="quality" status={run.quality_status ?? "-"} />,
              <div key="status" className="run-status-cell" style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                <RunStateDotBadge state={run.state} />
                {(run.error_code || run.error_message) && (
                  <span className="run-error-tag" title={run.error_message ? `${run.error_code ?? "ERROR"}: ${run.error_message}` : run.error_code ?? "异常"}>
                    <AlertCircle size={11} />
                    {run.error_code ?? "ERR"}
                  </span>
                )}
              </div>,
              dateTime(run.created_at),
              <div key="nav" className="table-actions-cell" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
                {run.state === "FAILED" ? (
                  <button
                    className="icon-button"
                    onClick={(e) => void handleRetry(run, e)}
                    disabled={isReadOnly || retrying === run.run_id}
                    title={isReadOnly ? "只读保护状态" : "重试此 Run"}
                    aria-label={`重试 Run ${run.run_id}`}
                  >
                    <RotateCcw className={retrying === run.run_id ? "spin" : ""} size={14} />
                  </button>
                ) : (
                  <span style={{ width: "28px", height: "28px", display: "inline-block", flexShrink: 0 }} />
                )}
                <button
                  className="icon-button"
                  onClick={() => onSelect(run.run_id)}
                  title="查看详情"
                  aria-label={`查看 Run ${run.run_id} 详情`}
                >
                  <ChevronRight size={16} />
                </button>
              </div>,
            ];
          })}
        />

        {totalFiltered > 0 && (
          <Pagination
            current={currentPage}
            total={totalFiltered}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </section>

      {confirmPrune && (
        <PruneConfirmDialog
          target={confirmPrune}
          busy={pruningBusy}
          close={() => setConfirmPrune(null)}
          confirm={() => void handlePrune(confirmPrune)}
        />
      )}
    </div>
  );
}

export function RunsPage({
  runs,
  scopes = [],
  selected,
  onSelect,
  system,
  isRefreshing,
  onRefresh,
  onToggleMenu,
  setNotice,
}: {
  runs: Run[];
  scopes?: Scope[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onRefresh?: () => Promise<void>;
  onToggleMenu?: () => void;
  setNotice?: NoticeSetter;
}) {
  if (selected)
    return (
      <RunDetail
        runId={selected}
        back={() => onSelect(null)}
        system={system}
        onToggleMenu={onToggleMenu}
      />
    );

  return (
    <RunsView
      runs={runs}
      scopes={scopes}
      onSelect={(id) => onSelect(id)}
      system={system}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      onToggleMenu={onToggleMenu}
      setNotice={setNotice}
    />
  );
}

export function RunDetail({
  runId,
  back,
  system,
  onToggleMenu,
}: {
  runId: string;
  back: () => void;
  system: SystemInfo | null;
  onToggleMenu?: () => void;
}) {
  const [run, setRun] = useState<any>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [performance, setPerformance] = useState<any[]>([]);
  const [analysis, setAnalysis] = useState<AiAnalysisDetails | null>(null);
  const [recommendations, setRecommendations] = useState<OptimizationRecommendations | null>(null);
  const [execution, setExecution] = useState<RunExecutionDetails | null>(null);
  const [tab, setTab] = useState("timeline");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [previewArtifact, setPreviewArtifact] = useState<Artifact | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);

  const isReadOnly = system?.write_kill_switch ?? true;

  const closeArtifactPreview = useCallback(() => setPreviewArtifact(null), []);

  const inLoadingRef = useRef(false);

  const load = useCallback(async () => {
    if (inLoadingRef.current) return;
    inLoadingRef.current = true;
    try {
      const [detail, artifactData, performanceData, analysisData, recommendationData, executionData] =
        await Promise.all([
          api.get<any>(`/api/v1/runs/${runId}`),
          api.get<Artifact[]>(`/api/v1/runs/${runId}/artifacts`),
          api.get<any[]>(`/api/v1/runs/${runId}/performance`),
          api.get<AiAnalysisDetails>(`/api/v1/runs/${runId}/analysis`),
          api.get<OptimizationRecommendations>(`/api/v1/runs/${runId}/recommendations`),
          api.get<RunExecutionDetails>(`/api/v1/runs/${runId}/execution`),
        ]);
      setRun((prev: any) => (JSON.stringify(prev) === JSON.stringify(detail) ? prev : detail));
      setArtifacts((prev) => (JSON.stringify(prev) === JSON.stringify(artifactData) ? prev : artifactData));
      setPerformance((prev) => (JSON.stringify(prev) === JSON.stringify(performanceData) ? prev : performanceData));
      setAnalysis((prev) => (JSON.stringify(prev) === JSON.stringify(analysisData) ? prev : analysisData));
      setRecommendations((prev) => (JSON.stringify(prev) === JSON.stringify(recommendationData) ? prev : recommendationData));
      setExecution((prev) => (JSON.stringify(prev) === JSON.stringify(executionData) ? prev : executionData));
      setLoadError(null);
    } catch (err: any) {
      setLoadError(err?.message || "加载运行详情失败");
    } finally {
      inLoadingRef.current = false;
    }
  }, [runId]);

  useEffect(() => {
    void load();
    const isActive = run && ["QUEUED", "CLAIMED", "RUNNING", "OPTIMIZING", "ANALYZING", "EXECUTING", "ROLLING_BACK"].includes(run.state);
    if ((!run && !loadError) || isActive) {
      const timer = setInterval(() => void load(), 2000);
      return () => clearInterval(timer);
    }
  }, [load, run?.state, !run && !loadError]);

  useEffect(() => {
    if (!tabInitialized && run) {
      if (run.state === "APPROVAL_REQUIRED" && run.task_mode !== "ANALYZE_ONLY") {
        setTab("recommendations");
      }
      setTabInitialized(true);
    }
  }, [run, tabInitialized]);

  if (!run) {
    if (loadError) {
      return (
        <div className="run-detail-page-container">
          <PageHeading
            title={
              <div className="detail-title-group">
                <button className="back-link-btn" onClick={back} title="返回 Runs 列表">
                  <ChevronRight className="rotate-180" size={16} /> 返回列表
                </button>
                <h1 className="mono">Run #{shortId(runId)}</h1>
              </div>
            }
            badge={<span className="badge badge-failed">加载失败</span>}
            isReadOnly={isReadOnly}
            onRefresh={load}
            isRefreshing={false}
            onToggleMenu={onToggleMenu}
          />
          <section className="empty-state" style={{ padding: "48px 24px", textAlign: "center" }}>
            <p style={{ color: "var(--color-danger, #ef4444)", marginBottom: "16px", fontSize: "14px" }}>
              {loadError}
            </p>
            <button className="primary-btn" onClick={() => void load()}>
              重新加载
            </button>
          </section>
        </div>
      );
    }
    return <Loading />;
  }

  return (
    <div className="run-detail-page-container">
      <PageHeading
        title={
          <div className="detail-title-group">
            <button className="back-link-btn" onClick={back} title="返回 Runs 列表">
              <ChevronRight className="rotate-180" size={16} /> 返回列表
            </button>
            <h1 className="mono">Run #{shortId(run.run_id)}</h1>
          </div>
        }
        badge={
          <>
            <RunStateDotBadge state={run.state} />
            <QualityGateBadge status={run.quality_status ?? "PENDING"} />
          </>
        }
        isReadOnly={isReadOnly}
        onRefresh={load}
        isRefreshing={false}
        onToggleMenu={onToggleMenu}
      />

      <RunSummaryBand run={run} artifactCount={artifacts.length} />

      <div className="tabs">
        <Tab id="timeline" current={tab} set={setTab}>
          流水线阶段
        </Tab>
        {run.task_mode === "ANALYZE_ONLY" ? (
          <Tab id="analysis" current={tab} set={setTab}>
            AI 诊断分析
          </Tab>
        ) : (
          <Tab id="recommendations" current={tab} set={setTab}>
            {run.task_type === "EXPERIMENT_REVIEW"
              ? "实验复盘分析"
              : run.task_type === "DAILY_SENTINEL"
              ? "巡检与熔断方案"
              : run.task_type === "ADAPTIVE_EVALUATION"
              ? "自适应调优方案"
              : run.task_type === "MILESTONE_TRANSITION"
              ? "里程碑终审方案"
              : run.task_type === "AUTONOMOUS_LAUNCH"
              ? "点火启动方案"
              : "优化建议方案"}
          </Tab>
        )}
        <Tab id="execution" current={tab} set={setTab}>
          执行与读回
        </Tab>
        <Tab id="performance" current={tab} set={setTab}>
          标准化指标
        </Tab>
        <Tab id="artifacts" current={tab} set={setTab}>
          Artifacts 凭证
        </Tab>
        <Tab id="events" current={tab} set={setTab}>
          事件流
        </Tab>
      </div>

      {tab === "timeline" && (
        <PipelineTimeline
          run={run}
          artifacts={artifacts}
          onPreviewArtifact={setPreviewArtifact}
        />
      )}

      {tab === "analysis" && <AiAnalysisView data={analysis} />}
      {tab === "recommendations" && (
        <ErrorBoundary fallbackTitle="加载优化方案视图时遇到异常">
          <OptimizationAdviceView
            data={recommendations}
            currency={run?.currency || (run?.marketplace === "UK" ? "GBP" : "EUR")}
            taskType={run?.task_type}
          />
        </ErrorBoundary>
      )}
      {tab === "execution" && <ExecutionResultsView data={execution} />}
      {tab === "performance" && (
        <DataTable
          className="performance-metrics-table"
          empty="暂无标准化指标"
          columns={["类型", "搜索词 / 关键词 / ASIN / 自动投放", "Entity ID", "时间窗口", "Clicks", "Spend", "Orders", "Sales", "Freshness"]}
          rows={performance.map((row) => {
            const label = entityDisplayLabel(row.entity, "-");
            return [
              row.entity?.entity_type === "search_term" ? "搜索词" : row.entity?.entity_type === "keyword" ? "关键词" : row.entity?.entity_type === "target" ? "投放目标" : row.grain,
              <span className="performance-entity-label" title={label}>{label}</span>,
              row.entity_id,
              row.window_name,
              row.clicks,
              formatMetricNumber(row.spend),
              row.orders,
              formatMetricNumber(row.sales),
              row.freshness_date,
            ];
          })}
        />
      )}
      {tab === "artifacts" && (
        <DataTable
          empty="暂无 Artifact 凭证"
          columns={["类型", "所属阶段", "Schema", "SHA-256", "大小", "生成时间", "操作"]}
          rows={artifacts.map((artifact) => [
            <button
              key="kind"
              className="artifact-link"
              onClick={() => setPreviewArtifact(artifact)}
            >
              {artifact.kind}
            </button>,
            artifact.stage,
            artifact.schema_version,
            <span key="hash" className="mono">
              {shortHash(artifact.sha256)}
            </span>,
            bytes(artifact.byte_size),
            dateTime(artifact.generated_at),
            <div key="act" className="artifact-row-actions">
              <button
                className="icon-button"
                onClick={() => setPreviewArtifact(artifact)}
                title="在线预览"
                aria-label={`预览 ${artifact.kind}`}
              >
                <Eye />
              </button>
              <a
                className="icon-button"
                href={`/api/v1/artifacts/${artifact.artifact_id}/download`}
                title="下载文件"
                aria-label={`下载 ${artifact.kind}`}
              >
                <Download />
              </a>
            </div>,
          ])}
        />
      )}
      {tab === "events" && <EventStreamConsole events={run.events} />}

      {previewArtifact && (
        <ArtifactPreviewDialog artifact={previewArtifact} onClose={closeArtifactPreview} />
      )}
    </div>
  );
}

function MasterCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  title,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  title?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="master-checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      title={title}
    />
  );
}

export type ApprovalQuickTab =
  | "ALL"
  | "LOW_RISK"
  | "HIGH_MEDIUM_RISK"
  | "BID_DOWN"
  | "BID_UP"
  | "NEGATIVE";

export type InBatchSortOrder =
  | "DEFAULT"
  | "DELTA_DOWN_MAX"
  | "DELTA_UP_MAX"
  | "RISK_HIGH_FIRST";

export function parseNumericBid(valueJson: any): number | null {
  if (valueJson === null || valueJson === undefined) return null;
  if (typeof valueJson === "number") return Number.isFinite(valueJson) ? valueJson : null;

  let raw: any = valueJson;
  if (typeof valueJson === "string") {
    const trimmed = valueJson.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
    try {
      raw = JSON.parse(trimmed);
    } catch {
      raw = trimmed;
    }
  }

  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === "string") {
    const parsed = parseFloat(raw.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof raw === "object" && raw !== null) {
    if ("bid" in raw) {
      const b = typeof raw.bid === "number" ? raw.bid : parseFloat(String(raw.bid).replace(/[^0-9.-]/g, ""));
      return Number.isFinite(b) ? b : null;
    }
    if ("amount" in raw) {
      const b = typeof raw.amount === "number" ? raw.amount : parseFloat(String(raw.amount).replace(/[^0-9.-]/g, ""));
      return Number.isFinite(b) ? b : null;
    }
    for (const val of Object.values(raw)) {
      const b = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.-]/g, ""));
      if (Number.isFinite(b)) return b;
    }
  }
  return null;
}

export function ApprovalsPage({
  runs,
  scopes = [],
  initialPlans,
  onNavigateToRun,
  onChanged,
  setNotice,
  system,
  isRefreshing,
  onToggleMenu,
}: {
  runs: Run[];
  scopes?: Scope[];
  initialPlans?: Plan[];
  onNavigateToRun?: (runId: string) => void;
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
}) {
  const pendingRuns = runs.filter(
    (run) => run.state === "APPROVAL_REQUIRED" && !AUTONOMOUS_TASK_TYPES.has(run.task_type || "")
  );

  const effectiveInitialPlans = useMemo(() => {
    if (initialPlans) {
      return initialPlans.filter((p) => {
        const run = runs.find((r) => r.run_id === p.run_id);
        return !run || !AUTONOMOUS_TASK_TYPES.has(run.task_type || "");
      });
    }
    return null;
  }, [initialPlans, runs]);

  const [plans, setPlans] = useState<Plan[]>(effectiveInitialPlans ?? []);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [approving, setApproving] = useState<string | null>(null);
  const [batchApproving, setBatchApproving] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});

  // Filters & Sorting state
  const [quickTab, setQuickTab] = useState<ApprovalQuickTab>("ALL");
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>("ALL");
  const [selectedRisk, setSelectedRisk] = useState<string>("ALL");
  const [selectedActionType, setSelectedActionType] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [inBatchSort, setInBatchSort] = useState<InBatchSortOrder>("DEFAULT");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isReadOnly = system?.write_kill_switch ?? true;

  useEffect(() => {
    if (!initialPlans) {
      void Promise.all(pendingRuns.map((run) => api.get<Plan>(`/api/v1/runs/${run.run_id}/plan`))).then(setPlans);
    } else if (effectiveInitialPlans) {
      setPlans(effectiveInitialPlans);
    }
  }, [runs.map((run) => `${run.run_id}:${run.state}:${run.task_type}`).join("|"), initialPlans, effectiveInitialPlans]);

  // Global KPI Calculations across all plans
  const kpiStats = useMemo(() => {
    let totalBatches = 0;
    let totalProposedActions = 0;
    let lowRiskCount = 0;
    let mediumRiskCount = 0;
    let highRiskCount = 0;
    let bidDownCount = 0;
    let bidUpCount = 0;
    let negativeCount = 0;

    for (const plan of plans) {
      const run = runs.find((r) => r.run_id === plan.run_id);
      if (run && AUTONOMOUS_TASK_TYPES.has(run.task_type || "")) continue;
      totalBatches++;
      for (const action of plan.actions) {
        if (action.status !== "PROPOSED") continue;
        totalProposedActions++;

        const risk = (action.risk || "LOW").toUpperCase();
        if (risk === "LOW") lowRiskCount++;
        else if (risk === "MEDIUM") mediumRiskCount++;
        else if (risk === "HIGH") highRiskCount++;

        const op = (action.operation || "").toLowerCase();
        const res = (action.resource_type || "").toLowerCase();

        if (res.includes("negative") || op.includes("negative")) {
          negativeCount++;
        } else {
          const numFrom = parseNumericBid(action.current_value_json);
          const numTo = parseNumericBid(action.constrained_value_json);
          if (numFrom !== null && numTo !== null) {
            if (numTo < numFrom) bidDownCount++;
            else if (numTo > numFrom) bidUpCount++;
          }
        }
      }
    }

    return {
      totalBatches,
      totalProposedActions,
      lowRiskCount,
      mediumRiskCount,
      highRiskCount,
      bidDownCount,
      bidUpCount,
      negativeCount,
    };
  }, [plans, runs]);

  // Marketplace dropdown options
  const marketplaceOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of plans) {
      const run = runs.find((r) => r.run_id === p.run_id);
      if (run && AUTONOMOUS_TASK_TYPES.has(run.task_type || "")) continue;
      const scope = scopes.find((s) => s.scope_id === run?.scope_id);
      const mp = (run?.marketplace || scope?.marketplace || "OTHER").toUpperCase();
      counts.set(mp, (counts.get(mp) ?? 0) + 1);
    }

    const list: CustomSelectOption[] = [
      { value: "ALL", label: "全部站点", sublabel: `${plans.length} 批次` },
    ];

    const order = ["UK", "DE", "FR", "IT", "ES", "US", "CA", "JP"];
    for (const mp of order) {
      if (counts.has(mp)) {
        list.push({
          value: mp,
          label: `${mp}${MARKETPLACE_LABELS[mp] ? ` (${MARKETPLACE_LABELS[mp]})` : ""}`,
          sublabel: `${counts.get(mp)} 批次`,
          flag: <MarketplaceFlag marketplace={mp} />,
        });
      }
    }
    for (const [mp, count] of counts.entries()) {
      if (!order.includes(mp) && mp !== "ALL") {
        list.push({
          value: mp,
          label: mp,
          sublabel: `${count} 批次`,
          flag: <MarketplaceFlag marketplace={mp} />,
        });
      }
    }
    return list;
  }, [plans, runs, scopes]);

  // Quick tabs definition
  const quickTabs: Array<{ id: ApprovalQuickTab; label: string; count: number; icon: ReactNode }> = [
    { id: "ALL", label: "全部批次", count: kpiStats.totalProposedActions, icon: <Layers size={13} /> },
    { id: "LOW_RISK", label: "仅看低风险", count: kpiStats.lowRiskCount, icon: <ShieldCheck size={13} /> },
    { id: "HIGH_MEDIUM_RISK", label: "含中/高风险", count: kpiStats.mediumRiskCount + kpiStats.highRiskCount, icon: <ShieldAlert size={13} /> },
    { id: "BID_DOWN", label: "出价调降 (控本)", count: kpiStats.bidDownCount, icon: <TrendingDown size={13} /> },
    { id: "BID_UP", label: "出价调升 (放量)", count: kpiStats.bidUpCount, icon: <TrendingUp size={13} /> },
    { id: "NEGATIVE", label: "新增否定", count: kpiStats.negativeCount, icon: <XCircle size={13} /> },
  ];

  // Filtering & In-batch sorting
  const filteredPlanItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return plans
      .map((plan) => {
        const run = runs.find((r) => r.run_id === plan.run_id);
        if (run && AUTONOMOUS_TASK_TYPES.has(run.task_type || "")) {
          return null;
        }
        const scope = scopes.find((s) => s.scope_id === run?.scope_id);
        const marketplace = (run?.marketplace || scope?.marketplace || "").toUpperCase();
        const productName = (scope?.display_name || run?.display_name || "").toLowerCase();
        const childAsin = (scope?.child_asin || run?.child_asin || "").toLowerCase();
        const sku = (scope?.sku || "").toLowerCase();
        const runId = (plan.run_id || run?.run_id || "").toLowerCase();

        // Marketplace filter
        if (selectedMarketplace !== "ALL" && marketplace !== selectedMarketplace) {
          return null;
        }

        // Filter individual actions inside the plan
        let actions = plan.actions.filter((action) => {
          const risk = (action.risk || "LOW").toUpperCase();
          const op = (action.operation || "").toLowerCase();
          const res = (action.resource_type || "").toLowerCase();
          const numFrom = parseNumericBid(action.current_value_json);
          const numTo = parseNumericBid(action.constrained_value_json);
          const isNumeric = numFrom !== null && numTo !== null;

          // Quick tab filter
          if (quickTab === "LOW_RISK" && risk !== "LOW") return false;
          if (quickTab === "HIGH_MEDIUM_RISK" && risk === "LOW") return false;
          if (quickTab === "BID_DOWN" && (!isNumeric || numTo >= numFrom)) return false;
          if (quickTab === "BID_UP" && (!isNumeric || numTo <= numFrom)) return false;
          if (quickTab === "NEGATIVE" && !res.includes("negative") && !op.includes("negative")) return false;

          // Selected risk filter
          if (selectedRisk !== "ALL" && risk !== selectedRisk) return false;

          // Selected action type filter
          const opTag = actionOperationTag(action.operation, action.resource_type);
          if (selectedActionType === "UPDATE_BID" && opTag.kind !== "update") return false;
          if (selectedActionType === "NEGATIVE" && opTag.kind !== "negative") return false;
          if (selectedActionType === "CREATE" && opTag.kind !== "create") return false;
          if (selectedActionType === "PAUSE" && opTag.kind !== "pause") return false;

          // Search query matching
          if (q) {
            const label = (action.entity?.display_label || action.resource_type || "").toLowerCase();
            const camp = (action.campaign_name || "").toLowerCase();
            const reason = (action.decision_reason || "").toLowerCase();
            const matchesAction = label.includes(q) || camp.includes(q) || reason.includes(q);
            const matchesPlan =
              productName.includes(q) ||
              childAsin.includes(q) ||
              sku.includes(q) ||
              runId.includes(q) ||
              marketplace.toLowerCase().includes(q);
            if (!matchesAction && !matchesPlan) return false;
          }

          return true;
        });

        if (actions.length === 0) return null;

        // In-batch sorting
        if (inBatchSort === "DELTA_DOWN_MAX") {
          actions = [...actions].sort((a, b) => {
            const aFrom = parseNumericBid(a.current_value_json);
            const aTo = parseNumericBid(a.constrained_value_json);
            const bFrom = parseNumericBid(b.current_value_json);
            const bTo = parseNumericBid(b.constrained_value_json);
            const aPct = aFrom !== null && aTo !== null && aFrom > 0 ? (aTo - aFrom) / aFrom : 0;
            const bPct = bFrom !== null && bTo !== null && bFrom > 0 ? (bTo - bFrom) / bFrom : 0;
            if (aPct !== bPct) return aPct - bPct; // Smallest (most negative) first
            return (bFrom ?? 0) - (aFrom ?? 0); // Tie-breaker: higher current bid first
          });
        } else if (inBatchSort === "DELTA_UP_MAX") {
          actions = [...actions].sort((a, b) => {
            const aFrom = parseNumericBid(a.current_value_json);
            const aTo = parseNumericBid(a.constrained_value_json);
            const bFrom = parseNumericBid(b.current_value_json);
            const bTo = parseNumericBid(b.constrained_value_json);
            const aPct = aFrom !== null && aTo !== null && aFrom > 0 ? (aTo - aFrom) / aFrom : 0;
            const bPct = bFrom !== null && bTo !== null && bFrom > 0 ? (bTo - bFrom) / bFrom : 0;
            if (aPct !== bPct) return bPct - aPct; // Largest positive first
            return (bTo ?? 0) - (aTo ?? 0); // Tie-breaker: higher target bid first
          });
        } else if (inBatchSort === "RISK_HIGH_FIRST") {
          const riskWeight: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          actions = [...actions].sort((a, b) => {
            const aW = riskWeight[(a.risk || "LOW").toUpperCase()] ?? 1;
            const bW = riskWeight[(b.risk || "LOW").toUpperCase()] ?? 1;
            if (aW !== bW) return bW - aW;
            const aFrom = parseNumericBid(a.current_value_json) ?? 0;
            const bFrom = parseNumericBid(b.current_value_json) ?? 0;
            return bFrom - aFrom;
          });
        }

        return {
          plan,
          run,
          scope,
          actions,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [
    plans,
    runs,
    scopes,
    selectedMarketplace,
    quickTab,
    selectedRisk,
    selectedActionType,
    searchQuery,
    inBatchSort,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [quickTab, selectedMarketplace, selectedRisk, selectedActionType, searchQuery, inBatchSort]);

  // Pagination for batches
  const totalBatchesCount = filteredPlanItems.length;
  const totalPages = Math.max(1, Math.ceil(totalBatchesCount / pageSize));
  const paginatedPlanItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPlanItems.slice(start, start + pageSize);
  }, [filteredPlanItems, page, pageSize]);

  // Total selected actions count across all plans
  const totalSelectedCount = useMemo(() => {
    return Object.values(selected).reduce((sum, ids) => sum + ids.length, 0);
  }, [selected]);

  const toggleExpand = (planId: string) => {
    setExpandedPlans((prev) => ({ ...prev, [planId]: !prev[planId] }));
  };

  const anyExpanded = plans.some((p) => expandedPlans[p.plan_id] === true);
  const toggleExpandAll = () => {
    if (anyExpanded) {
      setExpandedPlans({});
    } else {
      const all: Record<string, boolean> = {};
      for (const p of plans) all[p.plan_id] = true;
      setExpandedPlans(all);
    }
  };

  // Global batch selection handlers
  const selectAllLowRisk = () => {
    const nextSelected: Record<string, string[]> = {};
    let count = 0;
    for (const plan of plans) {
      const lowRiskIds = plan.actions
        .filter((a) => a.status === "PROPOSED" && (a.risk || "LOW").toUpperCase() === "LOW")
        .map((a) => a.action_id);
      if (lowRiskIds.length > 0) {
        nextSelected[plan.plan_id] = lowRiskIds;
        count += lowRiskIds.length;
      }
    }
    setSelected(nextSelected);
    setNotice({
      type: "success",
      text: `已一键勾选所有低风险调优项（共 ${count} 项）`,
    });
  };

  const selectAllFiltered = () => {
    const nextSelected: Record<string, string[]> = { ...selected };
    let count = 0;
    for (const item of filteredPlanItems) {
      const pActions = item.actions.filter((a) => a.status === "PROPOSED").map((a) => a.action_id);
      nextSelected[item.plan.plan_id] = pActions;
      count += pActions.length;
    }
    setSelected(nextSelected);
    setNotice({
      type: "success",
      text: `已勾选当前筛选结果中的可执行动作（共 ${count} 项）`,
    });
  };

  const clearAllSelected = () => {
    setSelected({});
    setNotice({
      type: "success",
      text: "已清空所有勾选动作",
    });
  };

  const approve = async (plan: Plan) => {
    try {
      setApproving(plan.plan_id);
      const result = await api.post<{
        status: string;
        experiments_created: number;
        failure_code: string | null;
        verified_action_count?: number;
        readback_count?: number;
      }>(`/api/v1/plans/${plan.plan_id}/approve`, {
        batch_id: plan.batch_id,
        plan_hash: plan.plan_hash,
        approved_action_ids: selected[plan.plan_id] ?? [],
        approval_comment: "批准所选低风险调整",
      });
      if (result.status === "EXECUTED") {
        setNotice({
          type: "success",
          text: `审批通过并执行完成，已回读验证 ${result.verified_action_count ?? 0} 项动作（${result.readback_count ?? 0} 条读回），已自动创建 ${result.experiments_created} 个追踪实验`,
        });
      } else {
        setNotice({
          type: "error",
          text: `审批已通过，但执行状态为 ${result.status}${result.failure_code ? ` · ${result.failure_code}` : ""}`,
        });
      }
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setApproving(null);
    }
  };

  const approveSingle = async (plan: Plan, actionId: string) => {
    try {
      setApproving(plan.plan_id);
      const result = await api.post<{
        status: string;
        experiments_created: number;
        failure_code: string | null;
        verified_action_count?: number;
        readback_count?: number;
      }>(`/api/v1/plans/${plan.plan_id}/approve`, {
        batch_id: plan.batch_id,
        plan_hash: plan.plan_hash,
        approved_action_ids: [actionId],
        approval_comment: "单项批准执行",
      });
      if (result.status === "EXECUTED") {
        setNotice({
          type: "success",
          text: `已单独批准并执行该项，已回读验证 ${result.verified_action_count ?? 0} 项动作（${result.readback_count ?? 0} 条读回），已自动创建 ${result.experiments_created} 个追踪实验`,
        });
      } else {
        setNotice({
          type: "error",
          text: `审批已通过，但执行状态为 ${result.status}${result.failure_code ? ` · ${result.failure_code}` : ""}`,
        });
      }
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setApproving(null);
    }
  };

  const approveBatchSelected = async () => {
    const targetPlans = plans.filter((p) => (selected[p.plan_id]?.length ?? 0) > 0);
    if (targetPlans.length === 0) return;

    setBatchApproving(true);
    let totalSuccess = 0;
    let totalExperiments = 0;
    let totalVerified = 0;
    let totalReadbacks = 0;
    const failureNotes: string[] = [];

    try {
      for (const plan of targetPlans) {
        const actionIds = selected[plan.plan_id]!;
        try {
          const result = await api.post<{
            status: string;
            experiments_created: number;
            failure_code: string | null;
            verified_action_count?: number;
            readback_count?: number;
          }>(`/api/v1/plans/${plan.plan_id}/approve`, {
            batch_id: plan.batch_id,
            plan_hash: plan.plan_hash,
            approved_action_ids: actionIds,
            approval_comment: "批量跨批次批准优化建议",
          });

          if (result.status === "EXECUTED") {
            totalSuccess += actionIds.length;
            totalExperiments += result.experiments_created ?? 0;
            totalVerified += result.verified_action_count ?? 0;
            totalReadbacks += result.readback_count ?? 0;
          } else {
            failureNotes.push(`批次 #${shortId(plan.run_id)}: ${result.status}`);
          }
        } catch (err: any) {
          failureNotes.push(`批次 #${shortId(plan.run_id)}: ${err.message || "请求失败"}`);
        }
      }

      if (failureNotes.length === 0) {
        setNotice({
          type: "success",
          text: `已成功批量批准 ${targetPlans.length} 个批次共 ${totalSuccess} 个优化动作，已回读验证 ${totalVerified} 项动作（${totalReadbacks} 条读回），已自动创建 ${totalExperiments} 个跟踪实验`,
        });
        setSelected({});
      } else {
        setNotice({
          type: "error",
          text: `批量审批执行完成（成功 ${totalSuccess} 项）：${failureNotes.join("；")}`,
        });
      }
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setBatchApproving(false);
    }
  };

  const reject = async (plan: Plan) => {
    try {
      await api.post(`/api/v1/plans/${plan.plan_id}/reject`, { comment: "本轮不执行" });
      setNotice({ type: "success", text: "已拒绝此批次调整" });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    }
  };

  const hasActiveFilters =
    quickTab !== "ALL" ||
    selectedMarketplace !== "ALL" ||
    selectedRisk !== "ALL" ||
    selectedActionType !== "ALL" ||
    searchQuery.trim() !== "" ||
    inBatchSort !== "DEFAULT";

  const resetFilters = () => {
    setQuickTab("ALL");
    setSelectedMarketplace("ALL");
    setSelectedRisk("ALL");
    setSelectedActionType("ALL");
    setSearchQuery("");
    setInBatchSort("DEFAULT");
  };

  return (
    <>
      <PageHeading
        title="审批中心"
        detail={`当前有 ${plans.length} 个待审批任务 · 共 ${kpiStats.totalProposedActions} 项建议动作`}
        isReadOnly={isReadOnly}
        onRefresh={onChanged}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
      />
      {plans.length === 0 ? (
        <section className="empty-state">
          <ListChecks />
          <h2>没有待审批的优化任务</h2>
          <p>当任务在“需要审批”模式下生成优化建议后，待审批计划将出现在这里。</p>
        </section>
      ) : (
        <>
          {/* Stage 2: Global Impact & Risk KPI Dashboard */}
          <div className="approval-kpi-grid">
            <div
              className={`approval-kpi-card ${quickTab === "ALL" ? "is-active" : ""}`}
              onClick={() => setQuickTab("ALL")}
            >
              <div className="approval-kpi-header">
                <span className="approval-kpi-icon total"><Layers size={15} /></span>
                <span className="approval-kpi-title">待审建议总览</span>
              </div>
              <div className="approval-kpi-body">
                <strong className="approval-kpi-value">{kpiStats.totalProposedActions}</strong>
                <span className="approval-kpi-unit">项动作</span>
              </div>
              <div className="approval-kpi-sub">
                <span>包含 <strong>{kpiStats.totalBatches}</strong> 个批次</span>
              </div>
            </div>

            <div
              className={`approval-kpi-card ${quickTab === "LOW_RISK" ? "is-active" : ""}`}
              onClick={() => setQuickTab(quickTab === "LOW_RISK" ? "ALL" : "LOW_RISK")}
            >
              <div className="approval-kpi-header">
                <span className="approval-kpi-icon low"><ShieldCheck size={15} /></span>
                <span className="approval-kpi-title">低风险微调 (安全)</span>
              </div>
              <div className="approval-kpi-body">
                <strong className="approval-kpi-value text-emerald">{kpiStats.lowRiskCount}</strong>
                <span className="approval-kpi-unit">项</span>
              </div>
              <div className="approval-kpi-sub">
                <span>中高风险: <strong>{kpiStats.mediumRiskCount + kpiStats.highRiskCount}</strong> 项</span>
              </div>
            </div>

            <div
              className={`approval-kpi-card ${quickTab === "BID_DOWN" ? "is-active" : ""}`}
              onClick={() => setQuickTab(quickTab === "BID_DOWN" ? "ALL" : "BID_DOWN")}
            >
              <div className="approval-kpi-header">
                <span className="approval-kpi-icon down"><TrendingDown size={15} /></span>
                <span className="approval-kpi-title">出价调降 (控本)</span>
              </div>
              <div className="approval-kpi-body">
                <strong className="approval-kpi-value text-teal">{kpiStats.bidDownCount}</strong>
                <span className="approval-kpi-unit">项</span>
              </div>
              <div className="approval-kpi-sub">
                <span>出价调升: <strong>{kpiStats.bidUpCount}</strong> 项</span>
              </div>
            </div>

            <div
              className={`approval-kpi-card ${quickTab === "NEGATIVE" ? "is-active" : ""}`}
              onClick={() => setQuickTab(quickTab === "NEGATIVE" ? "ALL" : "NEGATIVE")}
            >
              <div className="approval-kpi-header">
                <span className="approval-kpi-icon neg"><XCircle size={15} /></span>
                <span className="approval-kpi-title">新增否定 (止损)</span>
              </div>
              <div className="approval-kpi-body">
                <strong className="approval-kpi-value text-rose">{kpiStats.negativeCount}</strong>
                <span className="approval-kpi-unit">词</span>
              </div>
              <div className="approval-kpi-sub">
                <span>排除无效花费流量</span>
              </div>
            </div>
          </div>

          {/* Stage 1: Multi-dimension Filter Bar & Quick Tabs */}
          <div className="approval-filter-bar">
            <div className="approval-quick-tabs">
              {quickTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`approval-tab-btn ${quickTab === t.id ? "is-active" : ""}`}
                  onClick={() => setQuickTab(t.id)}
                >
                  <span className="approval-tab-icon">{t.icon}</span>
                  <span>{t.label}</span>
                  <span className="approval-tab-count">{t.count}</span>
                </button>
              ))}
            </div>

            <div className="approval-filter-row">
              <div className="approval-filter-select-group">
                <CustomSelect
                  value={selectedMarketplace}
                  onChange={(val) => setSelectedMarketplace(String(val))}
                  options={marketplaceOptions}
                  className="approval-mp-select"
                />

                <CustomSelect
                  value={selectedRisk}
                  onChange={(val) => setSelectedRisk(String(val))}
                  options={[
                    { value: "ALL", label: "全部风险等级" },
                    { value: "LOW", label: "🟢 仅看低风险", sublabel: `${kpiStats.lowRiskCount} 项` },
                    { value: "MEDIUM", label: "🟡 仅看中风险", sublabel: `${kpiStats.mediumRiskCount} 项` },
                    { value: "HIGH", label: "🔴 仅看高风险", sublabel: `${kpiStats.highRiskCount} 项` },
                  ]}
                />

                <CustomSelect
                  value={selectedActionType}
                  onChange={(val) => setSelectedActionType(String(val))}
                  options={[
                    { value: "ALL", label: "全部动作类型" },
                    { value: "UPDATE_BID", label: "出价调整 (Update Bid)" },
                    { value: "NEGATIVE", label: "新增否定 (Negative)" },
                    { value: "CREATE", label: "新增投放 (Create)" },
                    { value: "PAUSE", label: "暂停投放 (Pause)" },
                  ]}
                />

                <CustomSelect
                  value={inBatchSort}
                  onChange={(val) => setInBatchSort(val as InBatchSortOrder)}
                  options={[
                    { value: "DEFAULT", label: "排序: AI 默认推荐" },
                    { value: "DELTA_DOWN_MAX", label: "排序: 调价降幅最大优先" },
                    { value: "DELTA_UP_MAX", label: "排序: 调价涨幅最大优先" },
                    { value: "RISK_HIGH_FIRST", label: "排序: 风险最高优先" },
                  ]}
                />
              </div>

              <div className="approval-search-wrap">
                <Search size={14} className="approval-search-icon" />
                <input
                  type="text"
                  placeholder="搜索 ASIN、SKU、产品名、Campaign 或关键词..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="approval-search-input"
                />
                {searchQuery && (
                  <button type="button" className="approval-search-clear" onClick={() => setSearchQuery("")}>
                    <X size={12} />
                  </button>
                )}
              </div>

              {hasActiveFilters && (
                <button type="button" className="approval-reset-filters-btn" onClick={resetFilters}>
                  <RotateCcw size={12} />
                  <span>重置筛选</span>
                </button>
              )}
            </div>
          </div>

          {/* Stage 1 & 3: Global Batch Operations & Toolbar */}
          <div className="approval-global-toolbar">
            <div className="approval-toolbar-left">
              <span className="approval-toolbar-count">
                匹配 <strong>{filteredPlanItems.length}</strong> / {plans.length} 个批次 · 已勾选 <strong className="text-teal">{totalSelectedCount}</strong> 项动作
              </span>
            </div>

            <div className="approval-toolbar-right">
              {kpiStats.lowRiskCount > 0 && (
                <button
                  type="button"
                  className="approval-quick-action-btn select-low"
                  onClick={selectAllLowRisk}
                  title="一键选中所有批次中的低风险安全动作"
                >
                  <Zap size={13} />
                  <span>勾选所有低风险 ({kpiStats.lowRiskCount} 项)</span>
                </button>
              )}

              <button
                type="button"
                className="approval-quick-action-btn"
                onClick={selectAllFiltered}
                title="勾选当前筛选出的所有批次动作"
              >
                <CheckCircle2 size={13} />
                <span>全选当前结果</span>
              </button>

              {totalSelectedCount > 0 && (
                <button
                  type="button"
                  className="approval-quick-action-btn clear"
                  onClick={clearAllSelected}
                  title="清空所有已勾选的动作"
                >
                  <X size={13} />
                  <span>清空勾选</span>
                </button>
              )}

              <button
                type="button"
                className="approval-expand-all-btn"
                onClick={toggleExpandAll}
              >
                {anyExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <span>{anyExpanded ? "全部收起" : "全部展开"}</span>
              </button>

              <button
                type="button"
                className={`primary-button approval-global-submit-btn ${totalSelectedCount > 0 ? "is-active" : "is-disabled"}`}
                onClick={() => void approveBatchSelected()}
                disabled={isReadOnly || batchApproving || totalSelectedCount === 0}
                title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : undefined}
              >
                {batchApproving ? <RefreshCw className="spin" size={14} /> : <ShieldCheck size={14} />}
                <span>{batchApproving ? "正在批量执行..." : `批量批准已选 (${totalSelectedCount} 项)`}</span>
              </button>
            </div>
          </div>

          {/* Filtered Empty State */}
          {filteredPlanItems.length === 0 ? (
            <section className="empty-state approval-filter-empty">
              <Filter size={32} />
              <h3>未找到匹配的待审批动作</h3>
              <p>当前筛选条件（Tab / 站点 / 风险 / 关键词）下没有符合要求的建议动作。</p>
              <button type="button" className="secondary-button" onClick={resetFilters}>
                <RotateCcw size={13} />
                <span>重置筛选条件</span>
              </button>
            </section>
          ) : (
            <>
              {/* Batch List */}
              {paginatedPlanItems.map(({ plan, run, scope, actions }) => {
                const planSelected = selected[plan.plan_id] ?? [];
                const proposedActions = actions.filter((a) => a.status === "PROPOSED");
                const allSelected = proposedActions.length > 0 && planSelected.length === proposedActions.length;
                const isIndeterminate = planSelected.length > 0 && !allSelected;
                const isNoneSelected = planSelected.length === 0;
                const isExpanded = expandedPlans[plan.plan_id] === true;

                const marketplace = (run?.marketplace || scope?.marketplace || "").toUpperCase();
                const marketplaceName = marketplace && MARKETPLACE_LABELS[marketplace] ? MARKETPLACE_LABELS[marketplace] : null;
                const childAsin = scope?.child_asin || run?.child_asin || "";
                const sku = scope?.sku || "";
                const rawProductName = scope?.display_name || run?.display_name || "";
                const runId = plan.run_id || run?.run_id || "";

                let productDisplay = "";
                if (childAsin && sku) {
                  productDisplay = `${childAsin} (${sku})`;
                } else if (rawProductName) {
                  productDisplay = rawProductName;
                } else {
                  productDisplay = childAsin || sku || "-";
                }

                const toggleAll = () => {
                  if (allSelected) {
                    setSelected((current) => ({ ...current, [plan.plan_id]: [] }));
                  } else {
                    setSelected((current) => ({
                      ...current,
                      [plan.plan_id]: proposedActions.map((a) => a.action_id),
                    }));
                  }
                };

                return (
                  <section key={plan.plan_id} className={`approval-panel ${!isExpanded ? "is-collapsed" : "is-expanded"}`}>
                    <div
                      className={`approval-header ${!isExpanded ? "is-collapsed" : ""}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest("button, input, a, label, .approval-run-link-inline")) return;
                        toggleExpand(plan.plan_id);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="approval-header-left">
                        <label className="master-checkbox-label" onClick={(e) => e.stopPropagation()}>
                          <MasterCheckbox
                            checked={allSelected}
                            indeterminate={isIndeterminate}
                            onChange={toggleAll}
                            disabled={isReadOnly || approving !== null || proposedActions.length === 0}
                            title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : undefined}
                          />
                          <span className="master-checkbox-text">
                            {planSelected.length > 0
                              ? `全选 (${planSelected.length}/${proposedActions.length})`
                              : "全选可执行项"}
                          </span>
                        </label>
                        <span className="approval-context-divider">|</span>
                        
                        <div className="approval-header-context">
                          {/* 站点 */}
                          <div className="approval-context-item">
                            {marketplace && <MarketplaceFlag marketplace={marketplace} />}
                            <strong className="approval-context-val">{marketplace || "-"}</strong>
                            {marketplaceName && <span className="approval-context-sub">({marketplaceName})</span>}
                          </div>

                          <span className="approval-context-divider">|</span>

                          {/* 产品 */}
                          <div className="approval-context-item" title={`产品: ${productDisplay}`}>
                            <span className="approval-context-label">产品</span>
                            <strong className="approval-context-val">{productDisplay}</strong>
                          </div>

                          <span className="approval-context-divider">|</span>

                          {/* 本次任务 RUN ID */}
                          <div className="approval-context-item">
                            <span className="approval-context-label">任务</span>
                            {runId ? (
                              <button
                                type="button"
                                className="approval-run-link-inline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNavigateToRun?.(runId);
                                }}
                                title={`点击查看该 Run (${runId}) 完整的优化建议`}
                              >
                                <strong className="approval-run-tag mono">#{shortId(runId)}</strong>
                                <span className="approval-run-action-text">
                                  查看详情 <ExternalLink size={11} />
                                </span>
                              </button>
                            ) : (
                              <span className="mono">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="approval-header-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`approval-toggle-btn ${isExpanded ? "is-expanded" : ""}`}
                          onClick={() => toggleExpand(plan.plan_id)}
                          aria-expanded={isExpanded}
                          title={isExpanded ? "收起动作明细" : "展开动作明细"}
                        >
                          <span>{isExpanded ? "收起明细" : `展开明细 (${proposedActions.length} 项)`}</span>
                          <ChevronDown size={14} className={`toggle-arrow ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        <button
                          type="button"
                          className={`secondary-button danger ${isNoneSelected ? "is-disabled" : ""}`}
                          onClick={() => void reject(plan)}
                          disabled={isReadOnly || approving !== null || isNoneSelected}
                          title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : "全部拒绝"}
                        >
                          全部拒绝
                        </button>
                        <button
                          type="button"
                          className={`primary-button approval-submit-btn ${planSelected.length > 0 ? "is-active" : "is-disabled"}`}
                          onClick={() => void approve(plan)}
                          disabled={isReadOnly || approving !== null || isNoneSelected}
                          title={isReadOnly ? "当前处于只读保护，解除写入锁后可操作" : undefined}
                        >
                          {approving === plan.plan_id ? <RefreshCw className="spin" size={14} /> : <ShieldCheck size={14} />}
                          <span>{approving === plan.plan_id ? "正在执行..." : `批准执行 ${planSelected.length} 项`}</span>
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="action-list">
                        {actions.map((action) => {
                          const checked = planSelected.includes(action.action_id);
                          const executable = action.status === "PROPOSED";
                          const isNegative = String(action.resource_type || "").toLowerCase().includes("negative") || String(action.operation || "").toLowerCase().includes("negative");
                          const opInfo = actionOperationTag(action.operation, action.resource_type, action);
                          const entityLabel = resolveActionEntityLabel(action);
                          const breadcrumbPath = formatCampaignBreadcrumb(
                            action.campaign_name,
                            marketplace,
                            sku,
                            childAsin,
                            action.entity,
                            action.resource_type
                          );

                          return (
                            <div
                              key={action.action_id}
                              tabIndex={0}
                              role="row"
                              aria-selected={checked}
                              className={`action-row ${!executable ? "held" : ""} ${checked ? "is-selected" : ""}`}
                              onClick={() => {
                                if (isReadOnly || !executable || approving !== null) return;
                                setSelected((current) => ({
                                  ...current,
                                  [plan.plan_id]: checked
                                    ? (current[plan.plan_id] ?? []).filter((id) => id !== action.action_id)
                                    : [...(current[plan.plan_id] ?? []), action.action_id],
                                }));
                              }}
                              onKeyDown={(e) => {
                                if (isReadOnly || !executable || approving !== null) return;
                                if (e.key === "Enter" || e.key === "a" || e.key === "A") {
                                  e.preventDefault();
                                  void approveSingle(plan, action.action_id);
                                } else if (e.key === "Backspace" || e.key === "Delete" || e.key === "x" || e.key === "X") {
                                  e.preventDefault();
                                  setSelected((current) => ({
                                    ...current,
                                    [plan.plan_id]: (current[plan.plan_id] ?? []).filter((id) => id !== action.action_id),
                                  }));
                                } else if (e.key === " ") {
                                  e.preventDefault();
                                  setSelected((current) => ({
                                    ...current,
                                    [plan.plan_id]: checked
                                      ? (current[plan.plan_id] ?? []).filter((id) => id !== action.action_id)
                                      : [...(current[plan.plan_id] ?? []), action.action_id],
                                  }));
                                }
                              }}
                              title="键盘快捷键：[Enter] 批准该项 · [Backspace] 排除该项 · [空格] 勾选"
                            >
                              <div className="action-row-select" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  disabled={isReadOnly || !executable || approving !== null}
                                  checked={checked}
                                  title={isReadOnly ? "当前处于只读保护，解除写入锁后可勾选" : undefined}
                                  onChange={(e) =>
                                    setSelected((current) => ({
                                      ...current,
                                      [plan.plan_id]: e.target.checked
                                        ? [...(current[plan.plan_id] ?? []), action.action_id]
                                        : (current[plan.plan_id] ?? []).filter((id) => id !== action.action_id),
                                    }))
                                  }
                                />
                              </div>
                              <div className="action-kind">
                                <div className="action-title-row">
                                  <RiskBadge risk={action.risk} />
                                  <span className={`action-op-badge kind-${opInfo.kind}`}>{opInfo.text}</span>
                                  <strong className="action-entity-name" title={entityLabel}>
                                    {entityLabel}
                                  </strong>
                                </div>
                                <div className="action-breadcrumb-row">
                                  <span className="action-breadcrumb" title={`路径: ${breadcrumbPath}`}>
                                    {breadcrumbPath}
                                  </span>
                                </div>
                              </div>
                              <DiffValue
                                from={action.current_value_json}
                                to={action.constrained_value_json}
                                currency={scope?.currency || resolveMarketplaceCurrency(marketplace)}
                                isNegative={isNegative}
                                isPlacement={isPlacementAction(action)}
                                operation={action.operation}
                                entity={action.entity}
                                resourceType={action.resource_type}
                              />
                              <div className="action-evidence">
                                <div className="action-evidence-header">
                                  <span className="approval-reason-label"><Bot size={12} />AI 决策理由</span>
                                  <span className="evidence-status">{action.status === "PROPOSED" ? "待审批" : action.status === "APPROVED" ? "已审批" : action.status}</span>
                                </div>
                                <small className="decision-reason-text">
                                  {formatAcosText(withoutEntityIds(action.decision_reason || "该历史动作未保存 AI 决策理由，请结合运行详情中的证据与最终建议核验。", [action.entity?.entity_id]))}
                                </small>
                              </div>
                              <div className="action-row-actions" onClick={(e) => e.stopPropagation()}>
                                {executable && (
                                  <>
                                    <button
                                      type="button"
                                      className="row-action-btn approve"
                                      disabled={isReadOnly || approving !== null}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void approveSingle(plan, action.action_id);
                                      }}
                                      title="单独批准执行该项 (快捷键: Enter)"
                                    >
                                      <Check size={13} />
                                      <span>批准</span>
                                      <kbd className="kbd-hint">⏎</kbd>
                                    </button>
                                    <button
                                      type="button"
                                      className="row-action-btn reject"
                                      disabled={isReadOnly || approving !== null}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelected((current) => ({
                                          ...current,
                                          [plan.plan_id]: (current[plan.plan_id] ?? []).filter((id) => id !== action.action_id),
                                        }));
                                      }}
                                      title="取消勾选/排除该项 (快捷键: Backspace)"
                                    >
                                      <X size={13} />
                                      <span>排除</span>
                                      <kbd className="kbd-hint">⌫</kbd>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}

              {/* Stage 3: Pagination Controls */}
              {totalPages > 1 && (
                <div className="approval-pagination">
                  <div className="approval-pagination-info">
                    第 <strong>{page}</strong> / {totalPages} 页 · 共 {totalBatchesCount} 个批次
                  </div>
                  <div className="approval-pagination-buttons">
                    <button
                      type="button"
                      className="pagination-nav-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft size={14} />
                      <span>上一页</span>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pNum) => (
                      <button
                        key={pNum}
                        type="button"
                        className={`pagination-num-btn ${page === pNum ? "is-active" : ""}`}
                        onClick={() => setPage(pNum)}
                      >
                        {pNum}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="pagination-nav-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <span>下一页</span>
                      <ChevronRight size={14} />
                    </button>
                  </div>
                  <div className="approval-pagination-size">
                    <CustomSelect
                      value={pageSize}
                      onChange={(val) => {
                        setPageSize(Number(val));
                        setPage(1);
                      }}
                      options={[
                        { value: 5, label: "每页 5 批次" },
                        { value: 10, label: "每页 10 批次" },
                        { value: 20, label: "每页 20 批次" },
                        { value: 50, label: "每页 50 批次" },
                      ]}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export function ExperimentsPage({
  system,
  isRefreshing,
  onRefresh,
  onToggleMenu,
}: {
  system: SystemInfo | null;
  isRefreshing?: boolean;
  onRefresh?: () => Promise<void>;
  onToggleMenu?: () => void;
}) {
  const [items, setItems] = useState<Experiment[] | null>(null);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarketplace, setSelectedMarketplace] = useState("all");
  const [selectedSku, setSelectedSku] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSort, setSelectedSort] = useState("created_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const isReadOnly = system?.write_kill_switch ?? true;

  const load = useCallback(async () => {
    const data = await api.get<Experiment[]>("/api/v1/experiments");
    setItems(data);
    if (data) {
      setExpandedMap((prev) => {
        const next = { ...prev };
        data.forEach((item) => {
          if (next[item.experiment_id] === undefined) {
            next[item.experiment_id] = false;
          }
        });
        return next;
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const observing = items ? items.filter((item) => ["OBSERVING", "EXTENDED"].includes(item.state)).length : 0;
  const effective = items ? items.filter((item) => item.latest_evaluation?.decision.effective === true).length : 0;
  const rollback = items ? items.filter((item) => item.state !== "ROLLED_BACK" && item.latest_evaluation?.decision.needs_rollback).length : 0;

  const marketplaceOptions = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => i.marketplace).filter(Boolean))).sort();
  }, [items]);

  const skuOptions = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => i.sku).filter(Boolean))).sort();
  }, [items]);

  const roleOptions = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((i) => i.definition?.campaign_role).filter((v): v is string => Boolean(v)))).sort();
  }, [items]);

  const statusOptions: CustomSelectOption[] = useMemo(() => [
    { value: "all", label: "全部状态" },
    { value: "OBSERVING", label: `观察期中 (${observing})` },
    { value: "EFFECTIVE", label: `已证实有效 (${effective})` },
    { value: "ROLLBACK", label: `建议回滚 (${rollback})` },
    { value: "TOO_EARLY", label: "尚未到成熟期" },
    { value: "INSUFFICIENT_DATA", label: "证据样本不足" },
    { value: "INEFFECTIVE", label: "未见实质改善" },
    { value: "DEGRADED", label: "指标恶化" },
    { value: "ROLLED_BACK", label: "已回滚并验证" },
  ], [observing, effective, rollback]);

  const sortOptions: CustomSelectOption[] = useMemo(() => [
    { value: "created_desc", label: "最新创建" },
    { value: "mature_asc", label: "复盘到期时间 (由近到远)" },
    { value: "spend_desc", label: "基线花费 (高到低)" },
    { value: "orders_desc", label: "基线订单 (高到低)" },
  ], []);

  const hasActiveFilters = searchQuery !== "" || selectedMarketplace !== "all" || selectedSku !== "all" || selectedRole !== "all" || selectedStatus !== "all" || selectedSort !== "created_desc";

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedMarketplace("all");
    setSelectedSku("all");
    setSelectedRole("all");
    setSelectedStatus("all");
    setSelectedSort("created_desc");
    setPage(1);
  };

  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const entityLabel = entityDisplayLabel(item.entity, item.definition.entity_label || item.campaign_name || item.resource_type || "").toLowerCase();
        const hypothesis = (item.definition.hypothesis ?? "").toLowerCase();
        const sku = (item.sku ?? "").toLowerCase();
        const childAsin = (item.child_asin ?? "").toLowerCase();
        const campaignName = (item.campaign_name ?? "").toLowerCase();
        const entityId = (item.definition.entity_id ?? "").toLowerCase();
        const expId = (item.experiment_id ?? "").toLowerCase();
        if (!entityLabel.includes(q) && !hypothesis.includes(q) && !sku.includes(q) && !childAsin.includes(q) && !campaignName.includes(q) && !entityId.includes(q) && !expId.includes(q)) {
          return false;
        }
      }
      if (selectedMarketplace !== "all" && item.marketplace !== selectedMarketplace) return false;
      if (selectedSku !== "all" && item.sku !== selectedSku) return false;
      if (selectedRole !== "all" && item.definition.campaign_role !== selectedRole) return false;
      if (selectedStatus !== "all") {
        const evalState = item.state === "ROLLED_BACK" ? "ROLLED_BACK" : item.latest_evaluation?.classification ?? item.state;
        const isEffective = item.latest_evaluation?.decision.effective === true;
        const needsRollback = item.state !== "ROLLED_BACK" && (item.latest_evaluation?.decision.needs_rollback ?? false);
        if (selectedStatus === "OBSERVING") {
          if (!["OBSERVING", "EXTENDED"].includes(item.state)) return false;
        } else if (selectedStatus === "EFFECTIVE") {
          if (!isEffective) return false;
        } else if (selectedStatus === "ROLLBACK") {
          if (!needsRollback) return false;
        } else {
          if (evalState !== selectedStatus && item.state !== selectedStatus) return false;
        }
      }
      return true;
    }).sort((a, b) => {
      if (selectedSort === "mature_asc") {
        return Date.parse(a.mature_review_at || a.created_at) - Date.parse(b.mature_review_at || b.created_at);
      }
      if (selectedSort === "spend_desc") {
        const spendA = Number(a.definition.metrics?.find((m) => m.key === "spend")?.baseline ?? 0);
        const spendB = Number(b.definition.metrics?.find((m) => m.key === "spend")?.baseline ?? 0);
        return spendB - spendA;
      }
      if (selectedSort === "orders_desc") {
        const ordersA = Number(a.definition.metrics?.find((m) => m.key === "orders")?.baseline ?? 0);
        const ordersB = Number(b.definition.metrics?.find((m) => m.key === "orders")?.baseline ?? 0);
        return ordersB - ordersA;
      }
      return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
  }, [items, searchQuery, selectedMarketplace, selectedSku, selectedRole, selectedStatus, selectedSort]);

  if (!items) return <Loading />;

  const totalFiltered = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * pageSize, safePage * pageSize);

  const anyExpanded = pagedItems.some((item) => expandedMap[item.experiment_id] === true);

  const toggleAll = () => {
    const targetState = !anyExpanded;
    setExpandedMap((prev) => {
      const next = { ...prev };
      pagedItems.forEach((item) => {
        next[item.experiment_id] = targetState;
      });
      return next;
    });
  };

  const toggleItem = (id: string) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <>
      <PageHeading
        title="实验复盘与追踪"
        isReadOnly={isReadOnly}
        onRefresh={onRefresh ?? load}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
        detail="跟踪已执行 Action 的长期效果，排除延迟后客观复盘，并将结论自动反馈给下一次 AI 决策"
      />
      <section className="experiment-summary" aria-label="实验摘要">
        <span
          className={`is-clickable ${selectedStatus === "all" ? "is-active" : ""}`}
          onClick={() => { setSelectedStatus("all"); setPage(1); }}
          title="点击查看全部实验"
          role="button"
          tabIndex={0}
        >
          <small>实验总数</small>
          <strong>{items.length}</strong>
        </span>
        <span
          className={`is-clickable ${selectedStatus === "OBSERVING" ? "is-active" : ""}`}
          onClick={() => { setSelectedStatus(selectedStatus === "OBSERVING" ? "all" : "OBSERVING"); setPage(1); }}
          title="点击快速筛选处于观察期中的实验"
          role="button"
          tabIndex={0}
        >
          <small>观察期中</small>
          <strong>{observing}</strong>
        </span>
        <span
          className={`is-clickable ${selectedStatus === "EFFECTIVE" ? "is-active" : ""}`}
          onClick={() => { setSelectedStatus(selectedStatus === "EFFECTIVE" ? "all" : "EFFECTIVE"); setPage(1); }}
          title="点击快速筛选已证实有效的实验"
          role="button"
          tabIndex={0}
        >
          <small>已证实有效</small>
          <strong>{effective}</strong>
        </span>
        <span
          className={`is-clickable ${rollback ? "danger" : ""} ${selectedStatus === "ROLLBACK" ? "is-active" : ""}`}
          onClick={() => { setSelectedStatus(selectedStatus === "ROLLBACK" ? "all" : "ROLLBACK"); setPage(1); }}
          title="点击快速筛选建议回滚的实验"
          role="button"
          tabIndex={0}
        >
          <small>建议回滚</small>
          <strong>{rollback}</strong>
        </span>
      </section>

      {items.length === 0 ? (
        <section className="empty-state">
          <FlaskConical />
          <h2>暂无生效中的实验</h2>
          <p>成功执行并实时读回验证的优化动作会自动进入观察期并生成追踪实验。</p>
        </section>
      ) : (
        <div className="experiment-container">
          <div className="experiment-filter-toolbar">
            <div className="search">
              <Search size={14} />
              <input
                placeholder="搜索关键词、ASIN、SKU、广告活动、实验 ID..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => { setSearchQuery(""); setPage(1); }}
                  title="清空搜索"
                  aria-label="清空搜索"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <CustomSelect
              value={selectedMarketplace}
              onChange={(val) => { setSelectedMarketplace(val); setPage(1); }}
              options={[
                { value: "all", label: `全部站点 (${marketplaceOptions.length})` },
                ...marketplaceOptions.map((m) => ({ value: m, label: m, icon: getMarketplaceFlag(m) })),
              ]}
              title="按站点筛选"
              aria-label="按站点筛选"
            />

            {skuOptions.length > 1 && (
              <CustomSelect
                value={selectedSku}
                onChange={(val) => { setSelectedSku(val); setPage(1); }}
                options={[
                  { value: "all", label: `全部 SKU (${skuOptions.length})` },
                  ...skuOptions.map((sku) => ({ value: sku, label: sku })),
                ]}
                title="按 SKU 筛选"
                aria-label="按 SKU 筛选"
              />
            )}

            <CustomSelect
              value={selectedRole}
              onChange={(val) => { setSelectedRole(val); setPage(1); }}
              options={[
                { value: "all", label: `全部投放类型 (${roleOptions.length})` },
                ...roleOptions.map((role) => ({ value: role, label: role })),
              ]}
              title="按投放类型筛选"
              aria-label="按投放类型筛选"
            />

            <CustomSelect
              value={selectedStatus}
              onChange={(val) => { setSelectedStatus(val); setPage(1); }}
              options={statusOptions}
              title="按复盘状态筛选"
              aria-label="按复盘状态筛选"
            />

            <CustomSelect
              value={selectedSort}
              onChange={(val) => { setSelectedSort(val); setPage(1); }}
              options={sortOptions}
              title="排序方式"
              aria-label="排序方式"
            />

            {hasActiveFilters && (
              <button type="button" className="runs-filter-reset" onClick={resetFilters}>
                <RotateCcw size={12} />
                重置筛选
              </button>
            )}
          </div>

          <div className="experiment-list-toolbar">
            <span className="list-count-label">
              {hasActiveFilters ? `筛选出 ${totalFiltered} 项（共 ${items.length} 个实验项目）` : `共 ${items.length} 个实验追踪项目`}
            </span>
            {pagedItems.length > 0 && (
              <button
                type="button"
                className="experiment-expand-all-btn"
                onClick={toggleAll}
              >
                {anyExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <span>{anyExpanded ? "折叠本页" : "展开本页"}</span>
              </button>
            )}
          </div>

          {pagedItems.length === 0 ? (
            <div className="empty-state" style={{ padding: "40px 20px" }}>
              <Search size={32} style={{ color: "var(--muted)", marginBottom: "8px" }} />
              <h3>未找到匹配的实验追踪项目</h3>
              <p>尝试调整搜索词或筛选条件</p>
              <button type="button" className="ghost-button" onClick={resetFilters} style={{ marginTop: "12px" }}>
                重置所有筛选
              </button>
            </div>
          ) : (
            <div className="experiment-list">
              {pagedItems.map((item) => (
                <ExperimentCard
                  key={item.experiment_id}
                  item={item}
                  isExpanded={expandedMap[item.experiment_id] === true}
                  onToggle={() => toggleItem(item.experiment_id)}
                />
              ))}
            </div>
          )}

          {totalFiltered > 0 && (
            <Pagination
              current={safePage}
              total={totalFiltered}
              pageSize={pageSize}
              onPageChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          )}
        </div>
      )}
    </>
  );
}

export {
  ExperimentCard,
  ExperimentMilestone,
  ExperimentMetricRow,
  formatExperimentHypothesis,
  formatExperimentMetric,
  formatCriteriaText,
  metricRoleLabel,
  operatorLabel,
  experimentDecisionTitle,
  renderRollbackValue,
} from "./ExperimentCard.tsx";

type ProviderFormConfig = {
  api_key: string;
  base_url: string;
  model_id: string;
  reasoning_effort: ReasoningEffort;
  context_window: number;
};

type RuntimeConfigForm = {
  model: {
    provider: ModelProvider;
    enabled: boolean;
    api_key: string;
    base_url: string;
    model_id: string;
    reasoning_effort: ReasoningEffort;
    context_window: number;
    providers: {
      openai: ProviderFormConfig;
      google: ProviderFormConfig;
    };
  };
  map_mcp: { enabled: boolean; endpoint: string; token: string; rest_api_key: string };
  sorftime_mcp: { enabled: boolean; endpoint: string; token: string };
  task_queue: { max_concurrent_tasks: number };
};
type ConnectionTarget = "model" | "map" | "sorftime";
type ConnectionState = { state: "idle" | "testing" | "success" | "error"; message?: string };
type ConnectionTestResult = { ok: true; latency_ms: number; models?: string[]; selected_model?: string; tools?: string[] };

function emptyRuntimeForm(): RuntimeConfigForm {
  return {
    model: {
      provider: "openai",
      enabled: false,
      api_key: "",
      base_url: "https://api.openai.com/v1",
      model_id: "gpt-5",
      reasoning_effort: "medium",
      context_window: 262144,
      providers: {
        openai: { api_key: "", base_url: "https://api.openai.com/v1", model_id: "gpt-5", reasoning_effort: "medium", context_window: 262144 },
        google: { api_key: "", base_url: "https://generativelanguage.googleapis.com/v1beta", model_id: "gemini-2.5-flash", reasoning_effort: "medium", context_window: 1048576 },
      },
    },
    map_mcp: { enabled: false, endpoint: "https://app.marketplaceadpros.com", token: "", rest_api_key: "" },
    sorftime_mcp: { enabled: false, endpoint: "https://mcp.sorftime.com", token: "" },
    task_queue: { max_concurrent_tasks: 2 },
  };
}

function runtimeForm(system: SystemInfo | null): RuntimeConfigForm {
  if (!system?.runtime_config) return emptyRuntimeForm();
  const config = system.runtime_config;
  const activeProvider = config.model.provider ?? "openai";
  const openaiStored = config.model.providers?.openai;
  const googleStored = config.model.providers?.google;

  const openaiProfile: ProviderFormConfig = {
    api_key: "",
    base_url: openaiStored?.base_url ?? (activeProvider === "openai" ? config.model.base_url : "https://api.openai.com/v1"),
    model_id: openaiStored?.model_id ?? (activeProvider === "openai" ? config.model.model_id : "gpt-5"),
    reasoning_effort: openaiStored?.reasoning_effort ?? (activeProvider === "openai" ? config.model.reasoning_effort : "medium"),
    context_window: openaiStored?.context_window ?? (activeProvider === "openai" ? (config.model.context_window ?? 262144) : 262144),
  };

  const googleProfile: ProviderFormConfig = {
    api_key: "",
    base_url: googleStored?.base_url ?? (activeProvider === "google" ? config.model.base_url : "https://generativelanguage.googleapis.com/v1beta"),
    model_id: googleStored?.model_id ?? (activeProvider === "google" ? config.model.model_id : "gemini-2.5-flash"),
    reasoning_effort: googleStored?.reasoning_effort ?? (activeProvider === "google" ? config.model.reasoning_effort : "medium"),
    context_window: googleStored?.context_window ?? (activeProvider === "google" ? (config.model.context_window ?? 1048576) : 1048576),
  };

  const activeProfile = activeProvider === "google" ? googleProfile : openaiProfile;

  return {
    model: {
      provider: activeProvider,
      enabled: config.model.enabled,
      api_key: "",
      base_url: activeProfile.base_url,
      model_id: activeProfile.model_id,
      reasoning_effort: activeProfile.reasoning_effort,
      context_window: activeProfile.context_window,
      providers: {
        openai: openaiProfile,
        google: googleProfile,
      },
    },
    map_mcp: {
      enabled: config.map_mcp.enabled,
      endpoint: config.map_mcp.endpoint || "https://app.marketplaceadpros.com",
      token: "",
      rest_api_key: "",
    },
    sorftime_mcp: {
      enabled: config.sorftime_mcp.enabled,
      endpoint: config.sorftime_mcp.endpoint || "https://mcp.sorftime.com",
      token: "",
    },
    task_queue: { max_concurrent_tasks: config.task_queue.max_concurrent_tasks },
  };
}



export function SystemPage({
  system,
  marketplaceSettings,
  onChanged,
  setNotice,
  isRefreshing,
  onToggleMenu,
}: {
  system: SystemInfo | null;
  marketplaceSettings: MarketplaceSetting[];
  onChanged: () => Promise<void>;
  setNotice: NoticeSetter;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
}) {
  const [form, setForm] = useState<RuntimeConfigForm>(() => runtimeForm(system));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState<Record<"api" | "map" | "map_rest" | "sorftime", boolean>>({
    api: false,
    map: false,
    map_rest: false,
    sorftime: false,
  });
  const [tests, setTests] = useState<Record<ConnectionTarget, ConnectionState>>({
    model: { state: "idle" },
    map: { state: "idle" },
    sorftime: { state: "idle" },
  });

  // Unified marketplace state
  const [marketplacesState, setMarketplacesState] = useState<Record<string, { integration_id: string; account_id: string; profile_id: string; sp_marketplace_id: string }>>(() => {
    const initial: Record<string, { integration_id: string; account_id: string; profile_id: string; sp_marketplace_id: string }> = {};
    for (const m of marketplaceSettings) {
      initial[m.marketplace] = {
        integration_id: m.integration_id,
        account_id: m.account_id,
        profile_id: m.profile_id,
        sp_marketplace_id: m.sp_marketplace_id || AMAZON_SP_MARKETPLACE_IDS[m.marketplace] || "",
      };
    }
    return initial;
  });

  const isReadOnly = system?.write_kill_switch ?? true;

  useEffect(() => {
    if (!dirty) {
      setForm(runtimeForm(system));
      const initial: Record<string, { integration_id: string; account_id: string; profile_id: string; sp_marketplace_id: string }> = {};
      for (const m of marketplaceSettings) {
        initial[m.marketplace] = {
          integration_id: m.integration_id,
          account_id: m.account_id,
          profile_id: m.profile_id,
          sp_marketplace_id: m.sp_marketplace_id || AMAZON_SP_MARKETPLACE_IDS[m.marketplace] || "",
        };
      }
      setMarketplacesState(initial);
    }
  }, [system?.runtime_config?.updated_at, marketplaceSettings, dirty]);

  const update = (section: keyof RuntimeConfigForm, field: string, value: string | boolean | number) => {
    if (section === "model") {
      setForm((current) => {
        const provider = current.model.provider;
        return {
          ...current,
          model: {
            ...current.model,
            [field]: value,
            providers: {
              ...current.model.providers,
              [provider]: {
                ...current.model.providers[provider],
                [field]: value,
              },
            },
          },
        };
      });
    } else {
      setForm((current) => ({ ...current, [section]: { ...current[section], [field]: value } }));
    }
    setDirty(true);
    if (section !== "task_queue") {
      const target = section === "map_mcp" ? "map" : section === "sorftime_mcp" ? "sorftime" : "model";
      setTests((current) => ({ ...current, [target]: { state: "idle" } }));
    }
  };

  const changeModelProvider = (nextProvider: ModelProvider) => {
    setForm((current) => {
      const targetProfile = current.model.providers[nextProvider];
      return {
        ...current,
        model: {
          ...current.model,
          provider: nextProvider,
          api_key: targetProfile.api_key,
          base_url: targetProfile.base_url,
          model_id: targetProfile.model_id,
          reasoning_effort: targetProfile.reasoning_effort,
          context_window: targetProfile.context_window,
        },
      };
    });
    setDirty(true);
    setTests((current) => ({ ...current, model: { state: "idle" } }));
  };

  const updateMarketplace = (marketplace: string, field: "integration_id" | "account_id" | "profile_id" | "sp_marketplace_id", value: string) => {
    setMarketplacesState((current) => ({
      ...current,
      [marketplace]: { ...current[marketplace], [field]: value },
    }));
    setDirty(true);
  };

  const isMarketplacesDirty = marketplaceSettings.some((m) => {
    const current = marketplacesState[m.marketplace];
    return current && (
      current.integration_id !== m.integration_id ||
      current.account_id !== m.account_id ||
      current.profile_id !== m.profile_id ||
      current.sp_marketplace_id !== (m.sp_marketplace_id || AMAZON_SP_MARKETPLACE_IDS[m.marketplace] || "")
    );
  });
  const isFormDirty = dirty || isMarketplacesDirty;

  const requestConfig = () => ({
    model: {
      provider: form.model.provider,
      enabled: form.model.enabled,
      base_url: form.model.base_url,
      model_id: form.model.model_id,
      reasoning_effort: form.model.reasoning_effort,
      context_window: form.model.context_window,
      ...(form.model.api_key ? { api_key: form.model.api_key } : {}),
      providers: {
        openai: {
          base_url: form.model.providers.openai.base_url,
          model_id: form.model.providers.openai.model_id,
          reasoning_effort: form.model.providers.openai.reasoning_effort,
          context_window: form.model.providers.openai.context_window,
          ...(form.model.providers.openai.api_key ? { api_key: form.model.providers.openai.api_key } : {}),
        },
        google: {
          base_url: form.model.providers.google.base_url,
          model_id: form.model.providers.google.model_id,
          reasoning_effort: form.model.providers.google.reasoning_effort,
          context_window: form.model.providers.google.context_window,
          ...(form.model.providers.google.api_key ? { api_key: form.model.providers.google.api_key } : {}),
        },
      },
    },
    map_mcp: {
      ...form.map_mcp,
      endpoint: form.map_mcp.endpoint || "https://app.marketplaceadpros.com",
      ...(form.map_mcp.token ? { token: form.map_mcp.token } : {}),
      ...(form.map_mcp.rest_api_key ? { rest_api_key: form.map_mcp.rest_api_key } : {}),
    },
    sorftime_mcp: {
      ...form.sorftime_mcp,
      endpoint: form.sorftime_mcp.endpoint || "https://mcp.sorftime.com",
      ...(form.sorftime_mcp.token ? { token: form.sorftime_mcp.token } : {}),
    },
    task_queue: { ...form.task_queue },
  });

  const test = async (target: ConnectionTarget) => {
    setTests((current) => ({ ...current, [target]: { state: "testing", message: "正在发起探测连接..." } }));
    try {
      const result = await api.post<ConnectionTestResult>("/api/v1/system/runtime-config/test", {
        target,
        config: requestConfig(),
      });
      if (target === "model" && result.models) setModels(result.models);
      const count =
        target === "model" ? `${result.models?.length ?? 0} 个可用模型` : `${result.tools?.length ?? 0} 个工具`;
      setTests((current) => ({
        ...current,
        [target]: { state: "success", message: `连接正常 · 延迟 ${result.latency_ms} ms · ${count}` },
      }));
    } catch (error) {
      setTests((current) => ({
        ...current,
        [target]: { state: "error", message: error instanceof Error ? error.message : "连接失败" },
      }));
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      await api.put("/api/v1/system/runtime-config", requestConfig());
      const changedMarketplaces = marketplaceSettings.filter((m) => {
        const current = marketplacesState[m.marketplace];
        return current && (
          current.integration_id !== m.integration_id ||
          current.account_id !== m.account_id ||
          current.profile_id !== m.profile_id ||
          current.sp_marketplace_id !== (m.sp_marketplace_id || AMAZON_SP_MARKETPLACE_IDS[m.marketplace] || "")
        );
      });
      if (changedMarketplaces.length > 0) {
        await Promise.all(
          changedMarketplaces.map((m) =>
            api.put(`/api/v1/marketplace-settings/${m.marketplace}`, marketplacesState[m.marketplace])
          )
        );
      }
      setForm((current) => ({
        ...current,
        model: {
          ...current.model,
          api_key: "",
          providers: {
            openai: { ...current.model.providers.openai, api_key: "" },
            google: { ...current.model.providers.google, api_key: "" },
          },
        },
        map_mcp: { ...current.map_mcp, token: "", rest_api_key: "" },
        sorftime_mcp: { ...current.sorftime_mcp, token: "" },
      }));
      setDirty(false);
      setNotice({ type: "success", text: "系统配置与站点身份已保存并应用生效" });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSaving(false);
    }
  };

  const toggleWriteLock = async () => {
    if (!system) return;
    try {
      await api.put("/api/v1/system/write-kill-switch", { enabled: !system.write_kill_switch });
      setNotice({
        type: "success",
        text: `全局写入锁已${system.write_kill_switch ? "关闭 (允许受控写入)" : "开启 (只读保护)"}`,
      });
      await onChanged();
    } catch (error) {
      showError(error, setNotice);
    }
  };

  return (
    <>
      <PageHeading
        title="系统与连接配置"
        isReadOnly={isReadOnly}
        onRefresh={onChanged}
        isRefreshing={isRefreshing}
        onToggleMenu={onToggleMenu}
      />
      <div className="system-grid system-summary">
        <section>
          <SectionTitle title="全局写入安全开关" />
          <div className={`kill-switch ${system?.write_kill_switch ? "locked" : "unlocked"}`}>
            {system?.write_kill_switch ? <ShieldCheck size={28} /> : <Unlock size={28} />}
            <div>
              <strong>{system?.write_kill_switch ? "全局写入锁已开启 (只读安全保护中)" : "Executor 写入已解锁 (允许实盘修改)"}</strong>
              <span>{system?.write_kill_switch ? "WRITE_KILL_SWITCH = ON · 拦截所有真实 API 写请求" : "WRITE_KILL_SWITCH = OFF · 允许写入 MAP 与修改广告参数"}</span>
            </div>
            <button
              type="button"
              className={system?.write_kill_switch ? "secondary-button" : "danger-button"}
              onClick={() => void toggleWriteLock()}
            >
              {system?.write_kill_switch ? "🔓 解除写入锁" : "🔒 立即开启写入锁"}
            </button>
          </div>
        </section>
        <section>
          <SectionTitle title="运行环境与凭证" />
          <DefinitionList
            rows={[
              ["应用版本", system?.app_version ?? "-"],
              ["MCP Schema Hash", <span key="h" className="mono">{shortHash(system?.mcp_schema_hash)}</span>],
              ["数据库", <span key="p" className="mono">{system?.database_driver ?? "-"}{system?.db_path ? ` · ${system.db_path}` : ""}</span>],
            ]}
          />
        </section>
      </div>

      <section className="config-section">
        <div className="config-section-header">
          <span className="config-icon">
            <Gauge />
          </span>
          <div>
            <h2>任务执行并发</h2>
            <p>VPS 任务队列</p>
          </div>
          <strong className="task-concurrency-value">{form.task_queue.max_concurrent_tasks} 个</strong>
        </div>
        <div className="config-body task-concurrency-body">
          <Field label="最大同时执行任务数">
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={form.task_queue.max_concurrent_tasks}
              onChange={(event) => update("task_queue", "max_concurrent_tasks", Number(event.target.value))}
            />
          </Field>
        </div>
      </section>

      <MarketplaceSettingsSection
        settings={marketplaceSettings}
        values={marketplacesState}
        onChange={updateMarketplace}
      />

      <section className="config-section">
        <div className="config-section-header">
          <span className="config-icon">
            <Bot />
          </span>
          <div>
            <h2>AI 推理模型服务</h2>
            <p>{form.model.provider === "google" ? "Google Gemini Generative AI API" : "OpenAI-compatible Responses / Chat Completions API"}</p>
          </div>
          <Toggle
            checked={form.model.enabled}
            onChange={(value) => update("model", "enabled", value)}
            label="实时 AI 策略"
          />
        </div>
        <div className={`config-body ${!form.model.enabled ? "is-disabled-panel" : ""}`}>
          <fieldset disabled={!form.model.enabled} className="config-fieldset">
            <div className="config-grid model-config-grid">
              <Field label="模型服务商">
                <CustomSelect
                  value={form.model.provider}
                  onChange={(value) => changeModelProvider(value as ModelProvider)}
                  disabled={!form.model.enabled}
                  options={[
                    { value: "openai", label: "OpenAI-compatible", sublabel: "Responses / Chat Completions" },
                    { value: "google", label: "Google Gemini", sublabel: "Generative AI API" },
                  ]}
                />
              </Field>
              <Field label="API Base URL">
                <input
                  placeholder={form.model.provider === "google" ? "https://generativelanguage.googleapis.com/v1beta" : "https://api.openai.com/v1"}
                  value={form.model.base_url}
                  onChange={(e) => update("model", "base_url", e.target.value)}
                  disabled={!form.model.enabled}
                />
              </Field>
              <SecretField
                label="API Key"
                value={form.model.api_key}
                visible={showSecret.api}
                hasSecret={Boolean(
                  system?.runtime_config?.model.providers?.[form.model.provider]?.has_api_key ??
                    (system?.runtime_config?.model.provider === form.model.provider && system?.runtime_config?.model.has_api_key)
                )}
                suffix={
                  system?.runtime_config?.model.providers?.[form.model.provider]?.api_key_suffix ??
                  (system?.runtime_config?.model.provider === form.model.provider ? system?.runtime_config?.model.api_key_suffix : null)
                }
                onToggle={() => setShowSecret((current) => ({ ...current, api: !current.api }))}
                onChange={(value) => update("model", "api_key", value)}
                disabled={!form.model.enabled}
              />
              <Field label="模型 ID">
                <input
                  list="detected-models"
                  placeholder="输入或选择模型 ID"
                  value={form.model.model_id}
                  onChange={(e) => update("model", "model_id", e.target.value)}
                  disabled={!form.model.enabled}
                />
                <datalist id="detected-models">
                  {models.map((model) => (
                    <option key={model} value={model} />
                  ))}
                </datalist>
              </Field>
              <Field label="上下文窗口 (Context Window)">
                <CustomSelect
                  value={String(form.model.context_window)}
                  onChange={(val) => update("model", "context_window", Number(val))}
                  disabled={!form.model.enabled}
                  options={[
                    { value: "65536", label: "64K", sublabel: "65,536 tokens · 极简轻量" },
                    { value: "131072", label: "128K", sublabel: "131,072 tokens · 常用基准" },
                    { value: "262144", label: "256K", sublabel: "262,144 tokens · 推荐 OpenAI" },
                    { value: "524288", label: "512K", sublabel: "524,288 tokens · 超长分析" },
                    { value: "1048576", label: "1M", sublabel: "1,048,576 tokens · 推荐 Gemini" },
                    { value: "2097152", label: "2M", sublabel: "2,097,152 tokens · 极限容量" },
                  ]}
                />
              </Field>
              <Field label="推理强度 (Reasoning Effort)">
                <CustomSelect
                  value={form.model.reasoning_effort}
                  onChange={(val) => update("model", "reasoning_effort", val)}
                  disabled={!form.model.enabled}
                  options={[
                    { value: "off", label: "关闭", sublabel: "off" },
                    { value: "minimal", label: "极低", sublabel: "minimal" },
                    { value: "low", label: "低", sublabel: "low" },
                    { value: "medium", label: "中等", sublabel: "medium" },
                    { value: "high", label: "高", sublabel: "high" },
                    { value: "xhigh", label: "超高", sublabel: "xhigh" },
                    { value: "max", label: "最大", sublabel: "max" },
                  ]}
                />
              </Field>
            </div>
            <ConnectionBar state={tests.model} onTest={() => void test("model")} disabled={!form.model.enabled} />
          </fieldset>
        </div>
      </section>

      <section className="config-section">
        <div className="config-section-header">
          <span className="config-icon">
            <PlugZap />
          </span>
          <div>
            <h2>MCP 外部数据连接</h2>
            <p>MAP 广告数据与 Sorftime 市场商品情报桥接</p>
          </div>
        </div>
        <MapConfigSection
          config={form.map_mcp}
          hasMcpToken={system?.runtime_config?.map_mcp.has_token ?? false}
          mcpSuffix={system?.runtime_config?.map_mcp.token_suffix}
          hasRestKey={system?.runtime_config?.map_mcp.has_rest_api_key ?? false}
          restSuffix={system?.runtime_config?.map_mcp.rest_api_key_suffix}
          showMcpSecret={showSecret.map}
          showRestSecret={showSecret.map_rest}
          state={tests.map}
          onToggleMcpSecret={() => setShowSecret((current) => ({ ...current, map: !current.map }))}
          onToggleRestSecret={() => setShowSecret((current) => ({ ...current, map_rest: !current.map_rest }))}
          onUpdate={(field, value) => update("map_mcp", field, value)}
          onTest={() => void test("map")}
        />
        <SorftimeConfigSection
          config={form.sorftime_mcp}
          hasToken={system?.runtime_config?.sorftime_mcp.has_token ?? false}
          suffix={system?.runtime_config?.sorftime_mcp.token_suffix}
          visible={showSecret.sorftime}
          state={tests.sorftime}
          onToggleSecret={() => setShowSecret((current) => ({ ...current, sorftime: !current.sorftime }))}
          onUpdate={(field, value) => update("sorftime_mcp", field, value)}
          onTest={() => void test("sorftime")}
        />
      </section>

      <div className="settings-save-bar">
        {isFormDirty ? (
          <span className="save-bar-dirty">● 存在未保存的配置更改</span>
        ) : (
          <span>上次保存于 {dateTime(system?.runtime_config?.updated_at)}</span>
        )}
        <button
          type="button"
          className={`primary-button ${!isFormDirty ? "is-disabled" : ""}`}
          onClick={() => void save()}
          disabled={saving || !isFormDirty}
        >
          {saving ? <RefreshCw className="spin" size={15} /> : <Save size={15} />}
          <span>保存并应用配置</span>
        </button>
      </div>
    </>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  activeLabel = "已启用",
  inactiveLabel = "未启用",
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  activeLabel?: string;
  inactiveLabel?: string;
}) {
  const displayText = label ?? (checked ? activeLabel : inactiveLabel);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`toggle toggle-switch ${checked ? "checked" : ""}`}
      onClick={() => onChange(!checked)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        background: "transparent",
        border: "none",
        padding: "0",
        margin: "0",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <span
        className="toggle-track"
        style={{
          position: "relative",
          display: "inline-block",
          width: "44px",
          minWidth: "44px",
          maxWidth: "44px",
          height: "24px",
          minHeight: "24px",
          maxHeight: "24px",
          boxSizing: "border-box",
          backgroundColor: checked ? "var(--teal, #10b981)" : "var(--surface-raised, #243530)",
          borderRadius: "9999px",
          transition: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          flexShrink: 0,
          verticalAlign: "middle",
        }}
      >
        <span
          className="toggle-thumb"
          style={{
            position: "absolute",
            top: "3px",
            left: "3px",
            width: "18px",
            minWidth: "18px",
            maxWidth: "18px",
            height: "18px",
            minHeight: "18px",
            maxHeight: "18px",
            display: "block",
            backgroundColor: "#ffffff",
            borderRadius: "50%",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
            transform: checked ? "translateX(20px)" : "translateX(0px)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            margin: "0",
          }}
        />
      </span>
      <span className="toggle-label" style={{ fontSize: "13px", fontWeight: 550, color: "var(--ink-body, #334155)" }}>
        {displayText}
      </span>
    </button>
  );
}

function SecretField({
  label,
  value,
  visible,
  hasSecret,
  suffix,
  onToggle,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  visible: boolean;
  hasSecret: boolean;
  suffix?: string | null;
  onToggle: () => void;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const placeholder = hasSecret ? `已配置 · 末四位 ${suffix ?? "****"} (留空保持原值)` : "输入密钥";
  return (
    <Field label={label}>
      <div className={`secret-input ${disabled ? "is-disabled" : ""}`}>
        <input
          type={visible ? "text" : "password"}
          autoComplete="new-password"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <button
          type="button"
          className="icon-button"
          onClick={onToggle}
          title={visible ? "隐藏密钥" : "显示密钥"}
          aria-label={visible ? "隐藏密钥" : "显示密钥"}
          disabled={disabled}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </Field>
  );
}

function ConnectionBar({
  state,
  onTest,
  disabled = false,
}: {
  state: ConnectionState;
  onTest: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={`connection-bar ${disabled ? "is-disabled" : ""}`}>
      <ConnectionFeedback state={state} disabled={disabled} />
      <button
        type="button"
        className={`secondary-button probe-btn ${state.state === "success" ? "probe-success" : state.state === "error" ? "probe-error" : state.state === "testing" ? "probe-testing" : ""}`}
        onClick={onTest}
        disabled={disabled || state.state === "testing"}
      >
        {state.state === "testing" ? (
          <>
            <RefreshCw className="spin" size={14} />
            <span>探测中...</span>
          </>
        ) : state.state === "success" ? (
          <>
            <Check size={14} />
            <span>连接成功</span>
          </>
        ) : state.state === "error" ? (
          <>
            <AlertTriangle size={14} />
            <span>探测失败</span>
          </>
        ) : (
          <>
            <PlugZap size={14} />
            <span>探测连接</span>
          </>
        )}
      </button>
    </div>
  );
}

function ConnectionFeedback({ state, disabled = false }: { state: ConnectionState; disabled?: boolean }) {
  if (disabled) return <span className="connection-feedback idle">服务未启用 · 连接不可用</span>;
  if (state.state === "idle") return <span className="connection-feedback idle">尚未发起探测</span>;
  return (
    <span className={`connection-feedback ${state.state}`}>
      {state.state === "success" ? <Check size={14} /> : state.state === "error" ? <AlertTriangle size={14} /> : <RefreshCw size={14} className="spin" />}
      {state.message}
    </span>
  );
}

function MapConfigSection({
  config,
  hasMcpToken,
  mcpSuffix,
  hasRestKey,
  restSuffix,
  showMcpSecret,
  showRestSecret,
  state,
  onToggleMcpSecret,
  onToggleRestSecret,
  onUpdate,
  onTest,
}: {
  config: RuntimeConfigForm["map_mcp"];
  hasMcpToken: boolean;
  mcpSuffix?: string | null;
  hasRestKey: boolean;
  restSuffix?: string | null;
  showMcpSecret: boolean;
  showRestSecret: boolean;
  state: ConnectionState;
  onToggleMcpSecret: () => void;
  onToggleRestSecret: () => void;
  onUpdate: (field: string, value: string | boolean) => void;
  onTest: () => void;
}) {
  return (
    <div className="mcp-config">
      <div className="mcp-title">
        <div>
          <strong>MAP 广告系统连接</strong>
          <span style={{ fontSize: "12px", color: "var(--ink-muted, #64748b)", marginLeft: "8px" }}>
            内置服务地址 · https://app.marketplaceadpros.com
          </span>
        </div>
        <Toggle checked={config.enabled} onChange={(value) => onUpdate("enabled", value)} label="启用 MAP 广告连接" />
      </div>
      <div className={`mcp-body ${!config.enabled ? "is-disabled-panel" : ""}`}>
        <fieldset disabled={!config.enabled} className="config-fieldset">
          <div className="config-grid mcp-config-grid">
            <SecretField
              label="REST API Key (报表与实体极速拉取)"
              value={config.rest_api_key}
              visible={showRestSecret}
              hasSecret={hasRestKey}
              suffix={restSuffix}
              onToggle={onToggleRestSecret}
              onChange={(value) => onUpdate("rest_api_key", value)}
              disabled={!config.enabled}
            />
            <SecretField
              label="MCP Access Token (AI 工具调用)"
              value={config.token}
              visible={showMcpSecret}
              hasSecret={hasMcpToken}
              suffix={mcpSuffix}
              onToggle={onToggleMcpSecret}
              onChange={(value) => onUpdate("token", value)}
              disabled={!config.enabled}
            />
          </div>
          <ConnectionBar state={state} onTest={onTest} disabled={!config.enabled} />
        </fieldset>
      </div>
    </div>
  );
}

function SorftimeConfigSection({
  config,
  hasToken,
  suffix,
  visible,
  state,
  onToggleSecret,
  onUpdate,
  onTest,
}: {
  config: RuntimeConfigForm["sorftime_mcp"];
  hasToken: boolean;
  suffix?: string | null;
  visible: boolean;
  state: ConnectionState;
  onToggleSecret: () => void;
  onUpdate: (field: string, value: string | boolean) => void;
  onTest: () => void;
}) {
  return (
    <div className="mcp-config">
      <div className="mcp-title">
        <div>
          <strong>Sorftime 市场情报连接</strong>
          <span style={{ fontSize: "12px", color: "var(--ink-muted, #64748b)", marginLeft: "8px" }}>
            内置服务地址 · https://mcp.sorftime.com
          </span>
        </div>
        <Toggle checked={config.enabled} onChange={(value) => onUpdate("enabled", value)} label="启用 Sorftime 市场情报连接" />
      </div>
      <div className={`mcp-body ${!config.enabled ? "is-disabled-panel" : ""}`}>
        <fieldset disabled={!config.enabled} className="config-fieldset">
          <div className="config-grid mcp-config-grid">
            <SecretField
              label="Access Token"
              value={config.token}
              visible={visible}
              hasSecret={hasToken}
              suffix={suffix}
              onToggle={onToggleSecret}
              onChange={(value) => onUpdate("token", value)}
              disabled={!config.enabled}
            />
          </div>
          <ConnectionBar state={state} onTest={onTest} disabled={!config.enabled} />
        </fieldset>
      </div>
    </div>
  );
}

const AMAZON_SP_MARKETPLACE_IDS: Record<string, string> = {
  UK: "A1F83G8C2ARO7P",
  FR: "A13V1IB3VIYZZH",
  DE: "A1PA6795UKMFR9",
  IT: "APJ6JRA9NG5V4",
  ES: "A1RKKUPIHCS9HS",
};

function MarketplaceSettingsSection({
  settings,
  values,
  onChange,
}: {
  settings: MarketplaceSetting[];
  values: Record<string, { integration_id: string; account_id: string; profile_id: string; sp_marketplace_id: string }>;
  onChange: (marketplace: string, field: "integration_id" | "account_id" | "profile_id" | "sp_marketplace_id", value: string) => void;
}) {
  return (
    <section className="config-section">
      <div className="config-section-header">
        <span className="config-icon">
          <MapPin />
        </span>
        <div>
          <h2>欧洲站点身份配置</h2>
          <p>Marketplace 级 SP-API 站点 ID、Integration ID、account_id 与 profile_id 绑定</p>
        </div>
      </div>
      <div className="site-settings-list">
        {settings.map((setting) => (
          <MarketplaceSettingRow
            key={setting.marketplace}
            setting={setting}
            value={values[setting.marketplace] ?? {
              integration_id: setting.integration_id,
              account_id: setting.account_id,
              profile_id: setting.profile_id,
              sp_marketplace_id: setting.sp_marketplace_id || AMAZON_SP_MARKETPLACE_IDS[setting.marketplace] || "",
            }}
            onChange={(field, val) => onChange(setting.marketplace, field, val)}
          />
        ))}
      </div>
    </section>
  );
}

function MarketplaceSettingRow({
  setting,
  value,
  onChange,
}: {
  setting: MarketplaceSetting;
  value: { integration_id: string; account_id: string; profile_id: string; sp_marketplace_id: string };
  onChange: (field: "integration_id" | "account_id" | "profile_id" | "sp_marketplace_id", value: string) => void;
}) {
  return (
    <div className="site-setting-row">
      <div className="site-code">
        <strong>{setting.marketplace} 站</strong>
        <small>
          {setting.currency} · {setting.scope_count} 个 Scope
        </small>
      </div>
      <Field label="SP-API 站点 ID">
        <input
          placeholder="输入 SP-API 站点 ID"
          value={value.sp_marketplace_id ?? ""}
          onChange={(e) => onChange("sp_marketplace_id", e.target.value)}
          title="亚马逊官方全球标准站点 ID (SP-API Marketplace ID)，用于拉取后台业务报告、库存及 BuyBox 数据"
        />
      </Field>
      <Field label="Integration ID">
        <input
          placeholder="输入 Integration ID"
          value={value.integration_id}
          onChange={(e) => onChange("integration_id", e.target.value)}
        />
      </Field>
      <Field label="account_id">
        <input
          placeholder="输入 account_id"
          value={value.account_id}
          onChange={(e) => onChange("account_id", e.target.value)}
        />
      </Field>
      <Field label="profile_id">
        <input
          placeholder="输入 profile_id"
          value={value.profile_id}
          onChange={(e) => onChange("profile_id", e.target.value)}
        />
      </Field>
    </div>
  );
}

function CreateScopeDialog({
  marketplaceSettings,
  close,
  onCreated,
  setNotice,
  initialValues,
}: {
  marketplaceSettings: MarketplaceSetting[];
  close: () => void;
  onCreated: (id: string) => Promise<void>;
  setNotice: NoticeSetter;
  initialValues?: { marketplace?: string; child_asin?: string; sku?: string } | null;
}) {
  const initialMarketplace =
    initialValues?.marketplace ??
    marketplaceSettings.find((setting) => setting.marketplace === "DE" && setting.configured)?.marketplace ??
    marketplaceSettings.find((setting) => setting.configured)?.marketplace ??
    "DE";
  const [form, setForm] = useState({
    marketplace: initialMarketplace,
    child_asin: initialValues?.child_asin ?? "",
    sku: initialValues?.sku ?? "",
  });
  const [submitting, setSubmitting] = useState(false);
  const site = marketplaceSettings.find((setting) => setting.marketplace === form.marketplace);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const created = await api.post<Scope>("/api/v1/scopes", form);
      try {
        await api.post(`/api/v1/scopes/${created.scope_id}/verify`);
      } catch (error) {
        setNotice({ type: "error", text: `Scope 已保存，但身份校验未通过：${error instanceof Error ? error.message : "校验失败"}` });
        await onCreated(created.scope_id);
        return;
      }
      setNotice({ type: "success", text: "Scope 已创建并完成站点身份校验" });
      await onCreated(created.scope_id);
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog title="新建 ASIN Scope" close={close}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="form-grid">
          <Field label="Marketplace 站点">
            <CustomSelect
              value={form.marketplace}
              onChange={(val) => setForm({ ...form, marketplace: val })}
              options={marketplaceSettings.map((setting) => ({
                value: setting.marketplace,
                label: setting.marketplace,
                sublabel: setting.configured ? setting.currency : "未配置",
                flag: getMarketplaceFlag(setting.marketplace),
              }))}
            />
          </Field>
          <Field label="Child ASIN">
            <input
              autoFocus
              required
              value={form.child_asin}
              onChange={(e) => setForm({ ...form, child_asin: e.target.value.toUpperCase() })}
              placeholder="例：B09XYZ1234"
            />
          </Field>
          <Field label="SKU">
            <input
              required
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="例：AURORION-BLACK"
            />
          </Field>
          <div className={`scope-site-preview ${site?.configured ? "configured" : "missing"}`}>
            <span>
              <small>Integration ID</small>
              <strong>{site?.integration_id || "未配置"}</strong>
            </span>
            <span>
              <small>account_id</small>
              <strong>{site?.account_id || "未配置"}</strong>
            </span>
            <span>
              <small>profile_id</small>
              <strong>{site?.profile_id || "未配置"}</strong>
            </span>
          </div>
        </div>
        <div className="dialog-actions">
          <button type="button" className="secondary-button" onClick={close}>
            取消
          </button>
          <button className="primary-button" disabled={!site?.configured || submitting}>
            {submitting && <RefreshCw className="spin" />}
            {submitting ? "正在验证" : "创建并验证"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ScheduleDialog({
  scope,
  schedule,
  close,
  onSaved,
  setNotice,
}: {
  scope: Scope;
  schedule?: Schedule | null;
  close: () => void;
  onSaved: () => Promise<void>;
  setNotice: NoticeSetter;
}) {
  const isEditing = Boolean(schedule);
  const defaultLocalTime = schedule?.local_time
    ?? (schedule?.task_type === "MONTHLY_OPTIMIZATION" ? "15:00" : "09:00");
  const [form, setForm] = useState({
    scope_id: scope.scope_id,
    task_type: schedule?.task_type ?? "WEEKLY_OPTIMIZATION",
    task_mode: (schedule?.task_mode ?? "APPROVAL_REQUIRED") as TaskMode,
    frequency: schedule?.frequency ?? "WEEKLY",
    day_of_week: schedule?.day_of_week ?? 1,
    day_of_month: schedule?.day_of_month ?? 5,
    local_time: defaultLocalTime,
    scheduled_date: scheduleDateInputValue(schedule?.next_run_at),
    strategy_binding: (schedule as any)?.strategy_binding ?? "LATEST_PUBLISHED",
    misfire_policy: (schedule as any)?.misfire_policy ?? "SKIP",
  });
  const [saving, setSaving] = useState(false);
  const isOneTimeReview = form.task_type === "EXPERIMENT_REVIEW" && form.frequency === "MANUAL";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (isEditing && schedule) {
        await api.put(`/api/v1/schedules/${schedule.schedule_id}`, form);
        setNotice({ type: "success", text: "定时任务已修改成功" });
      } else {
        await api.post("/api/v1/schedules", form);
        setNotice({ type: "success", text: "定时任务已创建成功" });
      }
      await onSaved();
    } catch (error) {
      showError(error, setNotice);
    } finally {
      setSaving(false);
    }
  };

  const modes: Array<{ value: TaskMode; label: string; detail: string; icon: ReactNode }> = [
    { value: "ANALYZE_ONLY", label: "仅运行", detail: "仅拉取数据并输出 Markdown AI 分析诊断", icon: <Search /> },
    { value: "APPROVAL_REQUIRED", label: "需要审批", detail: "建议生成后进入审批中心，人工批准后安全执行", icon: <ShieldCheck /> },
    { value: "AUTO_EXECUTE", label: "自动执行", detail: "建议生成后直通执行器，受单批次上限与安全锁保护", icon: <Zap /> },
  ];
  const availableModes = form.task_type === "EXPERIMENT_REVIEW"
    ? [{ value: "APPROVAL_REQUIRED" as TaskMode, label: "自动复盘 · 回滚需审批", detail: "复盘按时自动运行；任何回滚动作都必须人工批准", icon: <ShieldCheck /> }]
    : modes;

  return (
    <Dialog title={isEditing ? "修改定时任务" : "新建定时任务"} close={saving ? () => undefined : close}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="form-grid">
          <Field label="任务类型">
            <CustomSelect
              value={form.task_type}
              onChange={(val) => {
                let nextFreq = form.frequency;
                let nextTime = form.local_time;
                if (val === "MONTHLY_OPTIMIZATION") {
                  nextFreq = "MONTHLY";
                  if (!isEditing || form.local_time === "09:00") {
                    nextTime = "15:00";
                  }
                } else if (val === "WEEKLY_OPTIMIZATION") {
                  if (form.frequency === "MANUAL" || form.frequency === "MONTHLY") {
                    nextFreq = "WEEKLY";
                  }
                  if (!isEditing && form.local_time === "15:00") {
                    nextTime = "09:00";
                  }
                } else if (val === "EXPERIMENT_REVIEW" && form.task_type !== val) {
                  nextFreq = "MANUAL";
                }
                setForm({
                  ...form,
                  task_type: val,
                  task_mode: val === "EXPERIMENT_REVIEW" ? "APPROVAL_REQUIRED" : form.task_mode,
                  frequency: nextFreq,
                  local_time: nextTime,
                });
              }}
              options={[
                { value: "WEEKLY_OPTIMIZATION", label: "每周优化" },
                { value: "MONTHLY_OPTIMIZATION", label: "月度优化" },
                { value: "EXPERIMENT_REVIEW", label: "实验复盘" },
                ...(AUTONOMOUS_TASK_TYPES.has(form.task_type) ? [
                  { value: "DAILY_SENTINEL", label: "每日安全巡检轨 (Daily Sentinel)" },
                  { value: "ADAPTIVE_EVALUATION", label: "多维自适应调优轨 (Adaptive Evaluation)" },
                  { value: "MILESTONE_TRANSITION", label: "阶段展期与跃迁终审轨 (Milestone Transition)" },
                  { value: "SCHEDULED_EVALUATION", label: "自动驾驶巡检 (Scheduled Evaluation)" },
                ] : []),
              ]}
            />
          </Field>
          <div className="task-mode-field">
            <Field label="运行安全模式">
              <div className="task-mode-segments" role="radiogroup" aria-label="运行安全模式">
                {availableModes.map((mode) => (
                  <label key={mode.value} className={form.task_mode === mode.value ? "selected" : ""}>
                    <input
                      type="radio"
                      name="task-mode"
                      value={mode.value}
                      checked={form.task_mode === mode.value}
                      onChange={() => setForm({ ...form, task_mode: mode.value })}
                    />
                    <span>
                      {mode.icon}
                      <strong>{mode.label}</strong>
                      <small>{mode.detail}</small>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <Field label="执行频率">
            <CustomSelect
              value={form.frequency}
              onChange={(val) => {
                let nextTime = form.local_time;
                if (val === "MONTHLY" && form.local_time === "09:00" && !isEditing) {
                  nextTime = "15:00";
                } else if (val === "WEEKLY" && form.local_time === "15:00" && !isEditing) {
                  nextTime = "09:00";
                }
                setForm({ ...form, frequency: val, local_time: nextTime });
              }}
              options={[
                { value: "WEEKLY", label: "每周" },
                { value: "MONTHLY", label: "每月" },
                { value: "DAILY", label: "每日" },
                { value: "MANUAL", label: form.task_type === "EXPERIMENT_REVIEW" ? "单次指定日期" : "仅手动触发" },
              ]}
            />
          </Field>
          {form.frequency === "WEEKLY" && (
            <Field label="执行星期">
              <CustomSelect
                value={form.day_of_week}
                onChange={(val) => setForm({ ...form, day_of_week: Number(val) })}
                options={["一", "二", "三", "四", "五", "六", "日"].map((day, index) => ({
                  value: index + 1,
                  label: `星期${day}`,
                }))}
              />
            </Field>
          )}
          {form.frequency === "MONTHLY" && (
            <Field label="执行日期 (几号)">
              <CustomSelect
                value={form.day_of_month}
                onChange={(val) => setForm({ ...form, day_of_month: Number(val) })}
                options={Array.from({ length: 31 }, (_, i) => ({
                  value: i + 1,
                  label: `${i + 1} 日`,
                }))}
              />
            </Field>
          )}
          {isOneTimeReview && (
            <Field label="执行日期 · 上海时间 (Asia/Shanghai)">
              <CustomDatePicker
                required
                min={scheduleDateInputValue(new Date().toISOString())}
                value={form.scheduled_date}
                onChange={(val) => setForm({ ...form, scheduled_date: val })}
              />
            </Field>
          )}
          <Field label="执行时间 · 上海时间 (Asia/Shanghai)">
            <CustomTimePicker
              value={form.local_time}
              onChange={(val) => setForm({ ...form, local_time: val })}
            />
          </Field>
        </div>
        <div className="dialog-actions">
          <button type="button" className="secondary-button" onClick={close} disabled={saving}>
            取消
          </button>
          <button className="primary-button" disabled={saving}>
            {isEditing ? <Save /> : <CalendarClock />}
            {isEditing ? (saving ? "保存中..." : "保存修改") : (saving ? "创建中..." : "创建定时任务")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

type NoticeSetter = (notice: { type: "error" | "success"; text: string } | null) => void;

function NavButton({
  icon,
  label,
  href,
  active,
  count,
  collapsed,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  href: string;
  active: boolean;
  count?: number;
  collapsed?: boolean;
  onClick: () => void;
}) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onClick();
  };

  return (
    <a
      href={href}
      className={`nav-button ${active ? "active" : ""} ${collapsed ? "collapsed" : ""}`}
      onClick={handleClick}
      title={collapsed ? (count ? `${label} (${count})` : label) : undefined}
      aria-label={count ? `${label} (${count})` : label}
      aria-current={active ? "page" : undefined}
    >
      <span className="nav-icon-wrap">
        {icon}
        {collapsed && count ? <b className="nav-badge-dot">{count}</b> : null}
      </span>
      {!collapsed && <span className="nav-label">{label}</span>}
      {!collapsed && count ? <b>{count}</b> : null}
    </a>
  );
}

export function TitleSafetyBadge({ isReadOnly }: { isReadOnly: boolean }) {
  return (
    <span
      className={`title-safety-badge ${isReadOnly ? "safety-on" : "safety-off"}`}
      title={
        isReadOnly
          ? "全局写入锁已开启：所有真实修改将被安全拦截 (只读保护模式)"
          : "全局写入锁已解除：执行器可正常向亚马逊广告 API 写入调整"
      }
    >
      {isReadOnly ? <Lock size={12} /> : <Unlock size={12} />}
      <span>{isReadOnly ? "只读保护 (写入锁开启)" : "写入已就绪 (锁已解除)"}</span>
    </span>
  );
}

export function PageHeading({
  title,
  isReadOnly,
  badge,
  action,
  onRefresh,
  isRefreshing,
  onToggleMenu,
}: {
  title: ReactNode;
  isReadOnly?: boolean;
  badge?: ReactNode;
  detail?: string;
  action?: ReactNode;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
  onToggleMenu?: () => void;
}) {
  const { navigate } = useContext(NavigationContext);
  return (
    <div className="unified-page-header">
      <div className="header-title-group">
        {onToggleMenu && (
          <button
            type="button"
            className="icon-button mobile-menu overview-mobile-menu"
            onClick={onToggleMenu}
            title="切换导航菜单"
            aria-label="切换导航菜单"
          >
            <Menu />
          </button>
        )}
        <div className="title-with-badge">
          {typeof title === "string" ? <h1>{title}</h1> : title}
          {badge}
          {isReadOnly !== undefined && <TitleSafetyBadge isReadOnly={isReadOnly} />}
        </div>
      </div>
      <div className="header-action-group">
        {action}
        {onRefresh && (
          <button
            type="button"
            className="header-refresh-btn"
            onClick={() => void onRefresh()}
            title="刷新当前数据"
            aria-label="刷新当前数据"
            disabled={isRefreshing}
          >
            <RefreshCw size={14} className={isRefreshing ? "spin" : ""} />
          </button>
        )}
        <button
          type="button"
          className="header-cockpit-btn"
          onClick={() => navigate?.("scale_dashboard")}
          title="进入自动驾驶空间 (放量驾驶舱)"
          aria-label="进入自动驾驶空间 (放量驾驶舱)"
        >
          <Rocket size={15} />
        </button>
        <ThemeToggleBtn />
      </div>
    </div>
  );
}

function SectionTitle({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="section-title">
      <h2>{title}</h2>
      {typeof action === "string" ? <button className="link-button">{action}</button> : action}
    </div>
  );
}

function MetricValueDisplay({ value }: { value: ReactNode }) {
  const elRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!elRef.current || typeof value !== "string") return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const match = value.match(/^([^0-9.-]*)([0-9,.]+)([^0-9.-]*)$/);
    if (!match) return;
    const prefix = match[1];
    const rawNum = parseFloat(match[2].replace(/,/g, ""));
    const suffix = match[3];
    if (isNaN(rawNum) || rawNum === 0) return;
    const decimals = match[2].includes(".") ? match[2].split(".")[1].length : 0;

    const counter = { val: 0 };
    const tween = gsap.to(counter, {
      val: rawNum,
      duration: 0.75,
      ease: "power2.out",
      onUpdate: () => {
        if (!elRef.current) return;
        const formattedNum = decimals > 0
          ? counter.val.toLocaleString("zh-CN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
          : Math.round(counter.val).toLocaleString("zh-CN");
        elRef.current.textContent = `${prefix}${formattedNum}${suffix}`;
      },
    });

    return () => {
      tween.kill();
    };
  }, [value]);

  return <strong ref={elRef} className="metric-value">{value}</strong>;
}

function Metric({
  label,
  value,
  note,
  tone,
  delta,
  deltaType = "good-up",
  tooltip,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: string;
  delta?: number | null;
  deltaType?: "good-up" | "good-down" | "neutral";
  tooltip?: string;
}) {
  let deltaClass = "neutral";
  if (delta != null) {
    if (deltaType === "good-up") {
      deltaClass = delta > 0 ? "up-good" : delta < 0 ? "down-warn" : "neutral";
    } else if (deltaType === "good-down") {
      deltaClass = delta < 0 ? "down-good" : delta > 0 ? "up-warn" : "neutral";
    } else {
      deltaClass = delta > 0 ? "up-neutral" : delta < 0 ? "down-neutral" : "neutral";
    }
  }

  return (
    <div className={`metric-card ${tone || ""}`}>
      <div className="metric-header">
        <span className="metric-label">{label}</span>
        {tooltip && (
          <span className="metric-tooltip-trigger" title={tooltip} aria-label={tooltip}>
            <Info size={13} />
          </span>
        )}
      </div>
      <MetricValueDisplay value={value} />
      <div className="metric-footer">
        <small className="metric-note">{note}</small>
        {delta != null && (
          <span className={`metric-delta-badge ${deltaClass}`} title={`较上一周期环比变化 ${(delta * 100).toFixed(2)}%`}>
            {delta > 0 ? <TrendingUp size={11} /> : delta < 0 ? <TrendingDown size={11} /> : null}
            <span>{delta === 0 ? "持平" : `${delta > 0 ? "+" : ""}${(delta * 100).toFixed(1)}%`}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function Status({ value }: { value: string }) {
  return <span className={`status status-${value.toLowerCase().replaceAll("_", "-")}`}>{value}</span>;
}

export function RunStateDotBadge({
  state,
  failureCode,
  failureReason,
}: {
  state: string;
  failureCode?: string | null;
  failureReason?: string | null;
}) {
  const normalized = state.toLowerCase().replaceAll("_", "-");
  const isRunning = [
    "running",
    "pulling",
    "analyzing",
    "optimizing",
    "queued",
    "claimed",
  ].includes(normalized);

  const isFailed = ["failed", "error", "critical"].includes(normalized);
  const failureText = failureReason || failureCode;
  const tooltip = isFailed
    ? `运行失败${failureText ? ` · 原因: ${failureText}` : " (点击进入排查原因)"}`
    : `运行状态: ${state}`;

  return (
    <span className={`run-state-dot-badge state-${normalized}`} title={tooltip}>
      <span className={`run-state-dot ${isRunning ? "dot-pulsing" : ""}`} />
      <span>{state}</span>
      {isFailed && failureText && (
        <span className="run-failure-hint" title={failureText}>
          <Info size={11} />
        </span>
      )}
    </span>
  );
}

export function QualityGateBadge({ status }: { status?: string | null }) {
  const value = (status ?? "PENDING").toUpperCase();
  const normalized = value.toLowerCase().replaceAll("_", "-");
  const isPass = ["PASS", "COMPLETE", "HEALTHY", "VALIDATED", "APPROVED"].includes(value);
  const isWarn = ["WARN", "WARNING", "DEGRADED", "PARTIAL", "ATTENTION"].includes(value);
  const isFail = ["FAIL", "FAILED", "ERROR", "CRITICAL", "BLOCKED"].includes(value);

  const icon = isPass ? (
    <CheckCircle2 size={12} />
  ) : isWarn ? (
    <AlertTriangle size={12} />
  ) : isFail ? (
    <XCircle size={12} />
  ) : (
    <ShieldCheck size={12} />
  );

  return (
    <span className={`quality-gate-badge quality-${normalized}`} title={`质量门禁状态: ${value}`}>
      {icon}
      <span>{value}</span>
    </span>
  );
}

export function RunSummaryBand({
  run,
  artifactCount,
}: {
  run: {
    trigger_source: string;
    task_type: string;
    task_mode: TaskMode | string;
    started_at: string | null;
    finished_at: string | null;
  };
  artifactCount: number;
}) {
  const durationText = formatDuration(run.started_at, run.finished_at);

  return (
    <div className="summary-band run-summary-band">
      <span>
        <small>触发来源</small>
        <strong>{run.trigger_source}</strong>
      </span>
      <span>
        <small>任务类型</small>
        <strong>{taskLabel(run.task_type)}</strong>
      </span>
      <span>
        <small>运行模式</small>
        <strong>{taskModeLabel(run.task_mode)}</strong>
      </span>
      <span>
        <small>开始时间</small>
        <time>{dateTime(run.started_at)}</time>
      </span>
      <span>
        <small>完成时间</small>
        <time>{dateTime(run.finished_at)}</time>
      </span>
      <span className="summary-duration-cell">
        <small>总耗时 (Duration)</small>
        <strong className="summary-duration-value" title={`总耗时: ${durationText}`}>
          <Clock3 size={13} className="duration-icon" />
          <span>{durationText}</span>
        </strong>
      </span>
      <span>
        <small>产出 Artifacts</small>
        <strong>{artifactCount}</strong>
      </span>
    </div>
  );
}

export function PipelineTimeline({
  run,
  artifacts,
  onPreviewArtifact,
}: {
  run: {
    steps: Array<{
      step_id: string;
      stage: string;
      status: string;
      attempt: number;
      started_at: string | null;
      completed_at: string | null;
      error_code?: string | null;
      error_message?: string | null;
      output_artifact_id?: string | null;
      input_artifact_id?: string | null;
    }>;
    events: Array<{
      event_id?: number | string;
      step_id?: string | null;
      event_type: string;
      payload_json: string;
      created_at: string;
    }>;
    error_code?: string | null;
    error_message?: string | null;
  };
  artifacts: Artifact[];
  onPreviewArtifact: (artifact: Artifact) => void;
}) {
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };

  const stepsList = run.steps || [];
  const allExpanded = useMemo(() => {
    if (!stepsList.length) return false;
    return stepsList.every((step) => expandedSteps[step.step_id]);
  }, [stepsList, expandedSteps]);

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedSteps({});
    } else {
      const next: Record<string, boolean> = {};
      stepsList.forEach((s) => {
        next[s.step_id] = true;
      });
      setExpandedSteps(next);
    }
  };

  const completedCount = stepsList.filter((s) => s.status === "COMPLETED").length;
  const failedCount = stepsList.filter((s) => s.status === "FAILED").length;
  const runningCount = stepsList.filter((s) =>
    ["RUNNING", "PULLING", "ANALYZING", "OPTIMIZING"].includes(s.status)
  ).length;

  return (
    <section className="pipeline-timeline-panel">
      <div className="pipeline-timeline-header">
        <div className="pipeline-header-title-group">
          <h3>流水线执行阶段</h3>
          <span className="pipeline-header-meta">
            共 {stepsList.length} 个阶段 ·{" "}
            {completedCount === stepsList.length && stepsList.length > 0
              ? "已全部完成"
              : failedCount > 0
              ? `${failedCount} 个阶段执行失败`
              : runningCount > 0
              ? `${runningCount} 个阶段正在执行`
              : `${completedCount} 个已完成`}
          </span>
        </div>
        <div className="pipeline-header-actions">
          <button
            type="button"
            className="secondary-button compact-btn"
            onClick={toggleAll}
            title={allExpanded ? "收起所有阶段的日志抽屉" : "展开所有阶段的日志抽屉"}
          >
            <FileText size={13} />
            <span>{allExpanded ? "收起全部日志" : "展开全部日志"}</span>
          </button>
        </div>
      </div>

      <div className="pipeline-stepper-list">
        {stepsList.map((step, index) => {
          const isExpanded = !!expandedSteps[step.step_id];
          const stepEvents = (run.events || []).filter(
            (e) =>
              e.step_id === step.step_id ||
              (!e.step_id &&
                typeof e.payload_json === "string" &&
                e.payload_json.includes(step.stage))
          );
          const stepArtifact = artifacts.find(
            (a) =>
              (step.output_artifact_id && a.artifact_id === step.output_artifact_id) ||
              a.stage === step.stage
          );
          const isCompleted = step.status === "COMPLETED";
          const isRunning = ["RUNNING", "PULLING", "ANALYZING", "OPTIMIZING"].includes(step.status);
          const isFailed = ["FAILED", "ERROR", "BLOCKED"].includes(step.status);
          const stepDuration = formatDuration(step.started_at, step.completed_at);

          return (
            <div
              key={step.step_id || `${step.stage}-${index}`}
              className={`pipeline-step-node ${step.status.toLowerCase()} ${isExpanded ? "expanded" : ""}`}
            >
              <div className="step-indicator-col">
                <span className={`step-dot-icon ${step.status.toLowerCase()}`}>
                  {isCompleted ? (
                    <CheckCircle2 size={16} />
                  ) : isRunning ? (
                    <RefreshCw size={15} className="spin" />
                  ) : isFailed ? (
                    <AlertTriangle size={15} />
                  ) : (
                    <Clock3 size={15} />
                  )}
                </span>
              </div>

              <div className="step-content-card">
                <div
                  className="step-card-header"
                  onClick={() => toggleStep(step.step_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStep(step.step_id);
                    }
                  }}
                  title="点击展开/收起阶段详细执行日志"
                >
                  <div className="step-info-cluster">
                    <strong className="step-stage-name">{step.stage}</strong>
                    <span className={`step-status-chip status-${step.status.toLowerCase()}`}>
                      {step.status}
                    </span>
                    <span className="step-meta-chip">
                      尝试次数: {step.attempt}
                    </span>
                    <span className="step-meta-chip duration-chip" title={`阶段耗时: ${stepDuration}`}>
                      <Clock3 size={12} className="meta-icon" />
                      耗时: {stepDuration}
                    </span>
                    <span className="step-meta-chip time-chip">
                      {step.completed_at
                        ? `完成于: ${dateTime(step.completed_at)}`
                        : step.started_at
                        ? `开始于: ${dateTime(step.started_at)}`
                        : "-"}
                    </span>
                  </div>

                  <div className="step-actions-cluster" onClick={(e) => e.stopPropagation()}>
                    {stepArtifact && (
                      <button
                        type="button"
                        className="step-artifact-badge-btn"
                        onClick={() => onPreviewArtifact(stepArtifact)}
                        title={`查看产出产物: ${stepArtifact.kind} (${bytes(stepArtifact.byte_size)})`}
                        aria-label={`预览产物 ${stepArtifact.kind}`}
                      >
                        <Package size={12} />
                        <span>产物: {stepArtifact.kind}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className={`step-logs-toggle-btn ${isExpanded ? "active" : ""}`}
                      onClick={() => toggleStep(step.step_id)}
                      title={isExpanded ? "收起日志" : "查看此阶段的详细日志与事件"}
                    >
                      <FileText size={12} />
                      <span>{isExpanded ? "收起日志" : "查看日志"}</span>
                      {stepEvents.length > 0 && (
                        <span className="step-event-count">({stepEvents.length})</span>
                      )}
                      {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="step-accordion-content">
                    {step.error_message && (
                      <div className="step-error-callout">
                        <AlertTriangle size={16} />
                        <div>
                          <strong>{step.error_code || "STEP_EXECUTION_ERROR"}</strong>
                          <p>{step.error_message}</p>
                        </div>
                      </div>
                    )}

                    {stepEvents.length > 0 ? (
                      <div className="step-terminal-log">
                        <div className="terminal-header">
                          <Terminal size={12} />
                          <span>
                            {step.stage} 阶段执行日志与事件流 ({stepEvents.length} 条记录)
                          </span>
                        </div>
                        <div className="terminal-body">
                          {stepEvents.map((evt, evtIdx) => (
                            <div
                              key={evt.event_id || `${evt.event_type}-${evtIdx}`}
                              className="terminal-line"
                            >
                              <time className="terminal-time">{dateTime(evt.created_at)}</time>
                              <span className="terminal-event-type">{evt.event_type}</span>
                              <code className="terminal-payload">
                                {typeof evt.payload_json === "string"
                                  ? evt.payload_json
                                  : JSON.stringify(evt.payload_json)}
                              </code>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="step-terminal-empty">
                        <span>该阶段无独立事件日志</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {run.error_message && (
        <div className="pipeline-error-block">
          <AlertTriangle size={18} />
          <div>
            <strong>{run.error_code || "RUN_FAILED"}</strong>
            <p>{run.error_message}</p>
          </div>
        </div>
      )}
    </section>
  );
}

function TaskModeBadge({ mode }: { mode: TaskMode }) {
  return (
    <span className={`task-mode-badge mode-${mode.toLowerCase().replaceAll("_", "-")}`}>
      {taskModeLabel(mode)}
    </span>
  );
}

export function DataTable({
  columns,
  rows,
  empty,
  emptyAction,
  rowClassName,
  className,
}: {
  columns: Array<string | ReactNode>;
  rows: ReactNode[][];
  empty: string;
  emptyAction?: ReactNode;
  rowClassName?: (index: number) => string | undefined;
  className?: string;
}) {
  return (
    <div className={`table-scroll${className ? ` ${className}` : ""}`}>
      <table>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={typeof column === "string" ? column : index}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr key={index} className={rowClassName ? rowClassName(index) : undefined}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td className="empty-cell-wrapper" colSpan={columns.length}>
                <div className="table-empty-state">
                  <div className="empty-icon-box">
                    <Search size={20} />
                  </div>
                  <strong className="empty-title">{empty}</strong>
                  <p className="empty-desc">当前时间范围或筛选条件下暂未查询到数据</p>
                  {emptyAction}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Tab({
  id,
  current,
  set,
  children,
}: {
  id: string;
  current: string;
  set: (id: string) => void;
  children: ReactNode;
}) {
  return (
    <button className={current === id ? "tab active" : "tab"} onClick={() => set(id)}>
      {children}
    </button>
  );
}

export function DefinitionList({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="definition-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="form-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <span>{label}</span>
      {children}
    </div>
  );
}

export function MoneyField({
  label,
  path,
  strategy,
  set,
  currency,
  disabled,
}: {
  label: string;
  path: string;
  strategy: any;
  set: (path: string, val: any) => void;
  currency: string;
  disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <div className={`adornment-input ${disabled ? "disabled" : ""}`}>
        <input
          className="number-input-right"
          inputMode="decimal"
          value={getPath(strategy, path) ?? ""}
          onChange={(e) => set(path, e.target.value)}
          disabled={disabled}
          placeholder="0.00"
          title={disabled ? "当前处于只读保护或非草稿模式，无法编辑" : undefined}
        />
        <span className="adornment-suffix">{currency}</span>
      </div>
    </Field>
  );
}

export function formatPercentInput(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const num = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(num)) return "";
  return parseFloat((num * 100).toPrecision(10)).toString();
}

export function formatMetricNumber(value: unknown): string {
  if (value == null || value === "") return "-";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (num === 0) return "0";
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  return rounded.toString();
}

export function PercentField({
  label,
  path,
  strategy,
  set,
  disabled,
}: {
  label: string;
  path: string;
  strategy: any;
  set: (path: string, val: any) => void;
  disabled?: boolean;
}) {
  const raw = getPath(strategy, path);
  const formatted = formatPercentInput(raw);
  const [localVal, setLocalVal] = useState(formatted);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalVal(formatPercentInput(raw));
    }
  }, [raw, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalVal(val);
    if (val === "" || val == null) {
      set(path, null);
    } else {
      const num = Number(val);
      if (!isNaN(num)) {
        const decimalVal = parseFloat((num / 100).toPrecision(10));
        set(path, decimalVal.toString());
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setLocalVal(formatPercentInput(getPath(strategy, path)));
  };

  return (
    <Field label={label}>
      <div className={`adornment-input ${disabled ? "disabled" : ""}`}>
        <input
          className="number-input-right"
          inputMode="decimal"
          value={isFocused ? localVal : formatted}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="0"
          title={disabled ? "当前处于只读保护或非草稿模式，无法编辑" : undefined}
        />
        <span className="adornment-suffix">%</span>
      </div>
    </Field>
  );
}

function BridgeRow({
  label,
  value,
  negative,
  strong,
}: {
  label: string;
  value: string;
  negative?: boolean;
  strong?: boolean;
}) {
  return (
    <div className={`bridge-row ${strong ? "strong" : ""}`}>
      <span>{label}</span>
      <b className={negative ? "negative" : ""}>
        {negative ? "− " : ""}
        {value}
      </b>
    </div>
  );
}

function ValidationPanel({ validation }: { validation: any }) {
  return (
    <div className={validation.valid ? "validation valid" : "validation invalid"}>
      <strong>{validation.valid ? "✓ 校验通过" : `发现 ${validation.errors?.length ?? 0} 项错误`}</strong>
      {(validation.errors ?? []).slice(0, 5).map((error: any, index: number) => (
        <span key={index}>{error.message}</span>
      ))}
      {(validation.warnings ?? []).slice(0, 3).map((warning: any, index: number) => (
        <span key={index}>{warning.message}</span>
      ))}
    </div>
  );
}

function Dialog({
  title,
  close,
  children,
  className,
  style,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
  className?: string;
  style?: import("react").CSSProperties;
}) {
  const content = (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className={`dialog ${className || ""}`} style={style} role="dialog" aria-modal="true">
        <div className="dialog-header">
          <h2>{title}</h2>
          <button className="icon-button" onClick={close} title="关闭" aria-label="关闭对话框">
            <X />
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
}

export function resolveMarketplaceCurrency(marketplace?: string | null, scopeCurrency?: string | null): string {
  if (scopeCurrency) return scopeCurrency;
  const mp = (marketplace || "").toUpperCase();
  if (mp === "UK") return "GBP";
  if (mp === "US") return "USD";
  if (mp === "JP") return "JPY";
  if (mp === "CA") return "CAD";
  if (mp === "AU") return "AUD";
  return "EUR";
}

export function getCurrencySymbol(currency?: string): string {
  if (!currency) return "";
  const code = currency.toUpperCase();
  switch (code) {
    case "GBP":
      return "£";
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "JPY":
      return "¥";
    case "CAD":
      return "CA$";
    case "AUD":
      return "AU$";
    default:
      return code.length <= 3 ? code : "";
  }
}

function formatBidWithCurrency(val: string | number, symbol: string): string {
  const num = typeof val === "number" ? val : Number(val);
  if (Number.isFinite(num)) {
    return `${symbol}${num.toFixed(2)}`;
  }
  return String(val);
}

export function formatCampaignBreadcrumb(
  campaignName?: string | null,
  _marketplace?: string | null,
  _sku?: string | null,
  _asin?: string | null,
  _entity?: ActionEntity | null,
  _resourceType?: string | null
): string {
  return (campaignName && campaignName.trim()) || "-";
}

function parseNegativeTargetDisplay(val: unknown): string {
  if (val == null) return "-";
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (typeof parsed === "object" && parsed !== null) {
        return parseNegativeTargetDisplay(parsed);
      }
    } catch {
      return val;
    }
  }
  if (typeof val === "object" && val !== null) {
    const record = val as Record<string, unknown>;
    const keyword = record.keywordText ?? record.keyword_text ?? record.negative_keyword ?? record.keyword;
    if (keyword != null && String(keyword).trim()) {
      return String(keyword).trim();
    }
    const asin = record.asin ?? record.negative_asin;
    if (asin != null && String(asin).trim()) {
      return String(asin).trim();
    }
    const expression = record.expression ?? record.target_expression;
    const expItem = Array.isArray(expression) ? expression[0] : expression;
    if (expItem && typeof expItem === "object") {
      const expVal = (expItem as Record<string, unknown>).value;
      if (expVal != null && String(expVal).trim()) return String(expVal).trim();
    }
    const entries = Object.entries(record).filter(([k]) => !k.toLowerCase().includes("match"));
    if (entries.length > 0) {
      return entries.map(([, v]) => String(v)).join(", ");
    }
  }
  return String(val);
}

export function DiffValue({
  from,
  to,
  currency,
  isNegative,
  isPlacement,
  operation,
  entity,
  resourceType,
}: {
  from: unknown;
  to: unknown;
  currency?: string;
  isNegative?: boolean;
  isPlacement?: boolean;
  operation?: string;
  entity?: ActionEntity | null;
  resourceType?: string | null;
}) {
  if (isNegative) {
    const targetText = parseNegativeTypeDisplay(to, entity, resourceType);
    return (
      <div className="diff-value diff-value-negative">
        <div className="diff-value-row">
          <span className="diff-val-to">{targetText}</span>
        </div>
      </div>
    );
  }

  const stateChange = extractStateValues(from, to);
  if (stateChange) {
    return (
      <div className="diff-value">
        <div className="diff-value-row">
          <span className="diff-val-from">{stateChange.fromState}</span>
          <span className="diff-arrow-icon">→</span>
          <span className="diff-val-to">{stateChange.toState}</span>
        </div>
      </div>
    );
  }

  const isPlc =
    isPlacement ||
    isPlacementAction({
      proposed_value: to,
      constrained_value: to,
      current_value: from,
      resource_type: resourceType,
    });

  if (isPlc) {
    const fromInfo = extractPlacementPercentages(from);
    const toInfo = extractPlacementPercentages(to);

    let deltaNode: ReactNode = null;
    if (fromInfo.primaryPct != null && toInfo.primaryPct != null) {
      const diff = toInfo.primaryPct - fromInfo.primaryPct;
      if (diff > 0) {
        deltaNode = <span className="diff-delta up">+{diff}%</span>;
      } else if (diff < 0) {
        deltaNode = <span className="diff-delta down">{diff}%</span>;
      } else {
        deltaNode = <span className="diff-delta neutral">0%</span>;
      }
    }

    return (
      <div className="diff-value">
        <div className="diff-value-row">
          <span className="diff-val-from">{fromInfo.display}</span>
          <span className="diff-arrow-icon">→</span>
          <span className="diff-val-to">{toInfo.display}</span>
        </div>
        {deltaNode && <div className="diff-value-delta-row">{deltaNode}</div>}
      </div>
    );
  }

  const numFrom = parseNumericBid(from);
  const numTo = parseNumericBid(to);
  const symbol = getCurrencySymbol(currency);

  const isFromEmpty =
    from == null ||
    from === "" ||
    from === "null" ||
    from === "undefined" ||
    (typeof from === "object" && Object.keys(from as any).length === 0);

  const isCreate =
    String(operation || "").toLowerCase() === "create" || isFromEmpty;

  if (isCreate || isFromEmpty) {
    if (resourceType) {
      const rt = resourceType.toLowerCase();
      if (rt.includes("ad_group") || rt.includes("adgroup")) {
        let rec: any = {};
        try { rec = typeof to === "string" ? JSON.parse(to) : (to || {}); } catch { rec = to || {}; }
        const bid = Number(rec?.default_bid ?? rec?.defaultBid ?? 1.00);
        const state = String(rec?.status ?? rec?.state ?? "ENABLED");
        return (
          <div className="diff-value">
            <div className="diff-value-row">
              <span className="diff-val-from">默认出价: {symbol ? formatBidWithCurrency(bid, symbol) : Number(bid).toFixed(2)}</span>
              <span className="diff-arrow-icon">·</span>
              <span className="diff-val-to">状态: {state}</span>
            </div>
          </div>
        );
      }
      if (rt.includes("product_ad") || rt.includes("productad")) {
        let rec: any = {};
        try { rec = typeof to === "string" ? JSON.parse(to) : (to || {}); } catch { rec = to || {}; }
        const asin = rec?.asin ?? entity?.asin ?? "ASIN";
        const sku = rec?.sku ?? (entity as any)?.sku ?? "-";
        return (
          <div className="diff-value">
            <div className="diff-value-row">
              <span className="diff-val-from">ASIN: {String(asin)}</span>
              {sku !== "-" && (
                <>
                  <span className="diff-arrow-icon">·</span>
                  <span className="diff-val-to">SKU: {String(sku)}</span>
                </>
              )}
            </div>
          </div>
        );
      }
      if (rt.includes("campaign")) {
        let rec: any = {};
        try { rec = typeof to === "string" ? JSON.parse(to) : (to || {}); } catch { rec = to || {}; }
        const budget = Number(rec?.daily_budget ?? rec?.dailyBudget ?? 10);
        const targeting = String(rec?.targeting ?? rec?.targetingType ?? "MANUAL");
        return (
          <div className="diff-value">
            <div className="diff-value-row">
              <span className="diff-val-from">日预算: {symbol ? formatBidWithCurrency(budget, symbol) : Number(budget).toFixed(2)}/日</span>
              <span className="diff-arrow-icon">·</span>
              <span className="diff-val-to">{targeting === "MANUAL" ? "手动投放" : targeting === "AUTO" ? "自动投放" : targeting}</span>
            </div>
          </div>
        );
      }
    }

    let displayTo = "";
    if (numTo !== null) {
      displayTo = symbol ? formatBidWithCurrency(numTo, symbol) : Number(numTo).toFixed(2);
    } else {
      try {
        const b = typeof to === "string" ? JSON.parse(to) : to;
        displayTo = typeof b === "object" && b !== null ? Object.values(b).join(", ") : String(b);
      } catch {
        displayTo = String(to);
      }
    }

    const typeLabel = parseTargetingTypeDisplay(to, entity, resourceType);

    if (typeLabel) {
      return (
        <div className="diff-value">
          <div className="diff-value-row">
            <span className="diff-val-from">{typeLabel}</span>
            <span className="diff-arrow-icon">→</span>
            <span className="diff-val-to">{displayTo}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="diff-value">
        <div className="diff-value-row">
          <span className="diff-val-to">{displayTo}</span>
        </div>
      </div>
    );
  }

  const isNumeric = numFrom !== null && numTo !== null;
  let fromVal = String(from);
  let toVal = String(to);
  if (!isNumeric) {
    try {
      const a = typeof from === "string" ? JSON.parse(from) : from;
      const b = typeof to === "string" ? JSON.parse(to) : to;
      fromVal = typeof a === "object" && a !== null ? Object.values(a).join(", ") : String(a);
      toVal = typeof b === "object" && b !== null ? Object.values(b).join(", ") : String(b);
    } catch {
      fromVal = String(from);
      toVal = String(to);
    }
  }

  const displayFrom = isNumeric && symbol ? formatBidWithCurrency(numFrom, symbol) : isNumeric ? String(numFrom) : fromVal;
  const displayTo = isNumeric && symbol ? formatBidWithCurrency(numTo, symbol) : isNumeric ? String(numTo) : toVal;

  let deltaNode: ReactNode = null;
  if (isNumeric && numFrom > 0) {
    const pct = Math.round(((numTo - numFrom) / numFrom) * 100);
    if (pct < 0) {
      deltaNode = <span className="diff-delta down">{pct}%</span>;
    } else if (pct > 0) {
      deltaNode = <span className="diff-delta up">+{pct}%</span>;
    } else {
      deltaNode = <span className="diff-delta neutral">0%</span>;
    }
  }

  return (
    <div className="diff-value">
      <div className="diff-value-row">
        <span className="diff-val-from">{displayFrom}</span>
        <span className="diff-arrow-icon">→</span>
        <span className="diff-val-to">{displayTo}</span>
      </div>
      {deltaNode && <div className="diff-value-delta-row">{deltaNode}</div>}
    </div>
  );
}

function RiskBadge({ risk }: { risk: string }) {
  const normalized = (risk || "LOW").toUpperCase();
  const label = normalized === "LOW" ? "低风险" : normalized === "MEDIUM" ? "中风险" : "高风险";
  return (
    <span className={`risk-badge risk-${normalized.toLowerCase()}`} title={`风险评估: ${label} (${normalized})`}>
      <span className="risk-dot" />
      <span>{label}</span>
    </span>
  );
}

function actionOperationTag(operation: string, resourceType?: string, action?: any): { text: string; kind: string } {
  const op = (operation || "").toLowerCase();
  const res = (resourceType || "").toLowerCase();
  if (action && isStatePauseAction(action)) {
    return { text: "暂停投放", kind: "pause" };
  }
  if (action && isBudgetAction(action)) {
    return { text: "预算调整", kind: "update" };
  }
  if (action && isPlacementAction(action)) {
    return { text: "位置调整", kind: "update" };
  }
  if (res.includes("ad_group") || res.includes("adgroup")) {
    return { text: "新建广告组", kind: "create" };
  }
  if (res.includes("product_ad") || res.includes("productad")) {
    return { text: "添加商品", kind: "create" };
  }
  if (res.includes("campaign") && op === "create") {
    return { text: "新建活动", kind: "create" };
  }
  if (op === "update") {
    if (res.includes("bid") || res.includes("keyword") || res.includes("target")) {
      return { text: "出价调整", kind: "update" };
    }
    if (res.includes("campaign")) {
      return { text: "预算调整", kind: "update" };
    }
    return { text: "属性更新", kind: "update" };
  }
  if (op === "create") {
    if (res.includes("negative")) {
      return { text: "新增否定", kind: "negative" };
    }
    if (res.includes("keyword")) {
      return { text: "新增关键词", kind: "create" };
    }
    return { text: "新增投放", kind: "create" };
  }
  if (op === "pause" || op === "archive") {
    return { text: "暂停投放", kind: "pause" };
  }
  return { text: operation.toUpperCase(), kind: "default" };
}

function approvalEntityKind(entity: ActionEntity | null | undefined): string {
  return entity?.entity_type === "keyword" ? "关键词" : entity?.entity_type === "target" ? "投放目标" : "广告实体";
}

function approvalEntitySubtitleMeta(entity: ActionEntity | null | undefined, mainLabel: string): string | null {
  if (!entity) return null;
  if (entity.entity_type === "keyword") {
    return entity.match_type ? `${entity.match_type} 匹配` : null;
  }
  if (entity.asin) {
    return mainLabel.includes(entity.asin) ? null : `ASIN: ${entity.asin}`;
  }
  const autoLabel = entity.targeting_type ? automaticTargetLabel(entity.targeting_type) : null;
  if (autoLabel && mainLabel.includes(autoLabel)) {
    return null;
  }
  return autoLabel;
}

function approvalEntityMeta(entity: ActionEntity, resourceType?: string | null): string {
  const isNegative = String(resourceType ?? "").toLowerCase().includes("negative");
  if (entity.entity_type === "keyword") return entity.match_type ? `${entity.match_type} ${isNegative ? "否定" : "匹配"}` : "关键词投放";
  if (entity.asin) return isNegative ? "ASIN 否定" : "商品投放";
  return entity.targeting_type ? automaticTargetLabel(entity.targeting_type) ?? `Target · ${entity.targeting_type}` : "Target";
}

function Loading() {
  return (
    <div className="loading" role="status">
      <RefreshCw className="spin" />
      <span>正在加载数据...</span>
    </div>
  );
}

function derivePreview(strategy: any) {
  const price = Number(strategy.economics.gross_order_revenue_vat_inclusive || 0);
  const refundRate = Number(strategy.economics.refund_rate || 0);
  const refundAmount = price * refundRate;
  const effectivePrice = price - refundAmount;
  const vatRate = Number(strategy.tax.vat_rate || 0);
  const vat = (effectivePrice * vatRate) / (1 + vatRate);
  const netRevenue = effectivePrice - vat;
  const fbaFee = Number(strategy.economics.fulfillment_cost || 0);
  const commission = Number(strategy.economics.amazon_fees || 0);
  const productCost = Number(strategy.economics.landed_product_cost || 0);
  const profit = effectivePrice - fbaFee - commission - vat - productCost;
  return {
    price,
    refundRate,
    refundAmount,
    effectivePrice,
    vat,
    fbaFee,
    commission,
    productCost,
    profit,
    acos: netRevenue > 0 ? profit / netRevenue : null,
  };
}

function simplifyUiStrategy(value: any) {
  const strategy = structuredClone(value);
  const economics = strategy.economics;
  if (economics.refund_rate == null) {
    const gross = Number(economics.gross_order_revenue_vat_inclusive || 0);
    const legacyRefund = Number(economics.expected_refunds_vat_inclusive || 0);
    economics.refund_rate = String(gross > 0 ? legacyRefund / gross : 0);
  }
  delete economics.expected_refunds_vat_inclusive;
  economics.other_variable_costs = "0";
  economics.net_revenue_ex_vat = null;
  economics.net_revenue = null;
  strategy.tax.revenue_input_mode = "GROSS_VAT_INCLUSIVE";
  strategy.tax.report_sales_vat_basis = "VAT_EXCLUSIVE";
  strategy.tax.cost_vat_basis = "GROSS_INCLUDES_NONRECOVERABLE_VAT";
  strategy.features.allow_acos_driven_actions = true;
  return strategy;
}

function getPath(value: any, path: string) {
  return path.split(".").reduce((current, key) => current?.[key], value);
}
export function shortId(value?: string | null) {
  return value ? value.split("-").slice(-1)[0].slice(0, 8) : "-";
}
export function shortHash(value?: string | null) {
  return value ? value.slice(0, 12) : "-";
}
export function compactJson(value: unknown) {
  return value == null ? "-" : JSON.stringify(value);
}
export function dateTime(value?: string | null) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

export function scheduleDateInputValue(value?: string | null) {
  if (!value || !Number.isFinite(new Date(value).getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}
export function formatDuration(startedAt?: string | null, finishedAt?: string | null): string {
  if (!startedAt) return "-";
  const start = new Date(startedAt).getTime();
  if (isNaN(start)) return "-";
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  if (isNaN(end)) return "-";
  const diffMs = Math.max(0, end - start);
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 1) return "< 1s";
  if (diffSec < 60) return `${diffSec}s`;
  const minutes = Math.floor(diffSec / 60);
  const remainingSec = diffSec % 60;
  if (diffSec < 3600) {
    return remainingSec > 0 ? `${minutes}m ${remainingSec}s` : `${minutes}m`;
  }
  const hours = Math.floor(diffSec / 3600);
  const remainingMin = Math.floor((diffSec % 3600) / 60);
  return remainingMin > 0 ? `${hours}h ${remainingMin}m` : `${hours}h`;
}
export function money(value: number, currency: string) {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}
export function percent(value: number) {
  return `${(value * 100).toFixed(2)}%`;
}
export function ratioText(value?: number | null) {
  return value == null ? "-" : percent(value);
}
export function totalSalesNote(
  tacos: number | null | undefined,
  status: DashboardPerformance["total_sales_status"] | undefined,
) {
  if (tacos != null) {
    return status && !["COMPLETED", "NOT_REQUESTED"].includes(status.state)
      ? "广告花费 / 店铺总销售额（沿用最近有效值）"
      : "广告花费 / 店铺总销售额";
  }
  switch (status?.state) {
    case "TOOL_UNAVAILABLE": return "Seller Central 未连接";
    case "ACCOUNT_DISCOVERY_UNAVAILABLE": return "请重新连接 MAP 以发现 SP 账户";
    case "ACCOUNT_NOT_FOUND": return "未找到当前站点的 SP 账户";
    case "ACCOUNT_AMBIGUOUS": return "当前站点匹配到多个 SP 账户";
    case "EMPTY": return "Seller Central 未返回总销售额";
    case "PARTIAL": return `总销售额缺少 ${status.missing_days} 个日期`;
    case "FAILED": return "总销售额拉取失败";
    default: return "等待 Seller Central 总销售额";
  }
}
export function bytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
export function taskLabel(value: string) {
  return (
    ({
      DATA_REFRESH: "数据刷新",
      WEEKLY_OPTIMIZATION: "每周优化",
      MONTHLY_OPTIMIZATION: "月度优化",
      EXPERIMENT_REVIEW: "实验复盘",
      DAILY_SENTINEL: "每日安全巡检轨 (Daily Sentinel)",
      ADAPTIVE_EVALUATION: "多维自适应调优轨 (Adaptive Evaluation)",
      MILESTONE_TRANSITION: "阶段展期与跃迁终审轨 (Milestone Transition)",
      AUTONOMOUS_LAUNCH: "战役点火启动 (Autonomous Launch)",
    } as Record<string, string>)[value] ?? value
  );
}
export function taskModeLabel(value: TaskMode | string) {
  return (
    ({
      ANALYZE_ONLY: "仅运行",
      APPROVAL_REQUIRED: "需要审批",
      AUTO_EXECUTE: "自动执行",
    } as Record<string, string>)[value] ?? value
  );
}
export function dayOfWeekLabel(day?: number | null) {
  if (!day) return "-";
  return ["周一", "周二", "周三", "周四", "周五", "周六", "周日"][day - 1] ?? `周${day}`;
}
export function titleFor(view: View) {
  return ({
    overview: "广告经营总览",
    scopes: "ASIN 运营",
    runs: "Runs 流水线",
    approvals: "审批中心",
    experiments: "实验复盘",
    system: "系统与连接配置",
  } as Record<View, string>)[view];
}
export function showError(error: unknown, setNotice: NoticeSetter) {
  setNotice({ type: "error", text: error instanceof Error ? error.message : "操作失败" });
}
